import {
  Component,
  Suspense,
  lazy,
  memo,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLocation, useParams, useSearchParams } from 'react-router-dom';
import { apiUrl, assetUrl } from '@/config/api';
import { useAuth } from '@/context/AuthContext.jsx';
import useLanguageNavigation from '@/hooks/useLanguageNavigation.js';
import RollingDiceWidget from '@/components/RollingDice/RollingDiceWidget.jsx';
import ToastNotification from '@/components/ToastNotification.jsx';
import InfoTooltip from '@/components/InfoTooltip.jsx';
import SupportRequestWidget from '@/components/SupportRequestWidget.jsx';
import { chips, getChipSrc } from '@/utils/chips.js';
import { getDeckCardTextStyle } from '@/utils/deckTextColors.js';
import CardPreviewModal from '@/components/CardPreviewModal.jsx';
import GameRecordingControls from '@/features/recording/GameRecordingControls.jsx';
import useGameRecording from '@/features/recording/useGameRecording.js';
import useAudioUploadSession from '@/features/recording/useAudioUploadSession.js';
import { toAbsoluteImageUrl } from '@/utils/imageUrls.js';
import { getLocalizedProfileField } from '@/utils/profileTranslations.js';
import { fetchWithIdempotencyRetry } from '@/utils/idempotency';
import { isTextWithinLimit, TEXT_LIMITS } from '@/utils/textLimits.js';
import videocallsIcon from '@/assets/iconsforgame/videocalls.png';
import chatIcon from '@/assets/iconsforgame/chat.png';
import logIcon from '@/assets/iconsforgame/logsrouts.png';
import notesIcon from '@/assets/iconsforgame/notes.png';
import settingsIcon from '@/assets/iconsforgame/settings.png';
import './GamePage.css';
import { useTranslation } from 'react-i18next';

const BOARD_ROWS = 8;
const BOARD_COLS = 9;
const TOTAL_CELLS = BOARD_ROWS * BOARD_COLS;
const FINISH_CELL = 68;
const ANIMATION_LOCK_NONE = 'NONE';
const ANIMATION_LOCK_ENTRY = 'ENTRY';
const ANIMATION_LOCK_MOVE = 'MOVE';
const REENTRY_CELL = 51;
const INTRO_DROP_DURATION_MS = 900;
const ANIMATION_WATCHDOG_MS = 12000;
const NOTES_MAX_LENGTH = 3000;
const NOTES_SAVE_DELAY_MS = 400;
const playerColors = ['#ef4444', '#0ea5e9', '#22c55e', '#f97316', '#a855f7', '#14b8a6'];
const DEFAULT_TIP_TARIFFS = [
  { id: 'tip-1', amountEur: 1 },
  { id: 'tip-2', amountEur: 2 },
  { id: 'tip-5', amountEur: 5 },
  { id: 'tip-10', amountEur: 10 },
];
const TIPS_CONTEXT_KEY = 'tips_context';
const WFP_TIP_FLOW_KEY = 'wfp_tip_flow';
const WFP_TIP_STARTED_KEY = 'wfp_tip_started';
const TIP_POLL_FAST_INTERVAL = 2500;
const TIP_POLL_SYNC_INTERVAL_MS = 12000;
const TIP_POLL_TIMEOUT_MS = 90000;
const LIVEKIT_COMPLETED_GAME_GRACE_MS = 24 * 60 * 60 * 1000;
const CHAT_NOTIFICATION_SOUND_URL = 'data:audio/wav;base64,UklGRlQAAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YTAAAAAAABEREQ==';
const CHAT_NOTIFICATION_BASE_VOLUME = 0.45;
const CHAT_NOTIFICATION_VOLUME_MULTIPLIER = 3;
const defaultSettings = {
  highlightVisitedCells: true,
  onSixBehavior: 'moveImmediately',
  movementSpeed: 3,
  chatNotificationSoundEnabled: true,
};

const VideoCallModal = lazy(() => import('@/features/video/VideoCallModal.jsx'));

class LivekitErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error) {
    this.props.onError?.(error);
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback;
    }
    return this.props.children;
  }
}

class GamePageErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error) {
    this.props.onError?.(error);
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null });
    this.props.onRetry?.();
  };

  render() {
    if (this.state.hasError) {
      return this.props.fallback?.(this.state.error, this.handleReset) || null;
    }
    return this.props.children;
  }
}

const clampCellNumber = (cell) => {
  if (!Number.isFinite(cell) || cell < 1) {
    return 1;
  }
  if (cell > TOTAL_CELLS) {
    return TOTAL_CELLS;
  }
  return cell;
};

const RAW_I18N_KEY_PATTERN = /^[a-z]+(\.[a-zA-Z0-9_]+)+$/;

const isRawSystemText = (value) => {
  if (typeof value !== 'string') {
    return false;
  }
  const normalized = value.trim();
  if (!normalized) {
    return false;
  }
  return RAW_I18N_KEY_PATTERN.test(normalized)
    || normalized.startsWith('Failed to fetch')
    || normalized.startsWith('room.')
    || normalized.startsWith('errors.');
};

const isFinishedPlayerForVideoLimit = (player) => {
  if (!player || player.status === 'beforeEnter') {
    return false;
  }
  return player.status === 'finishedOn68'
    || player.status === 'finished'
    || Number(player.currentCell) === FINISH_CELL;
};

const resolveCompletedGameAtMs = ({ game = null, players = [], session = null } = {}) => {
  const allPlayersFinished = Array.isArray(players)
    && players.length > 0
    && players.every((player) => isFinishedPlayerForVideoLimit(player));

  const latestPlayerFinishedAtMs = (allPlayersFinished ? players : []).reduce((latest, player) => {
    const finishedAtMs = Date.parse(player?.finishedAt || '');
    if (!Number.isFinite(finishedAtMs)) {
      return latest;
    }
    return Math.max(latest, finishedAtMs);
  }, Number.NEGATIVE_INFINITY);
  if (Number.isFinite(latestPlayerFinishedAtMs)) {
    return latestPlayerFinishedAtMs;
  }
  const sessionFinishedAtMs = Date.parse(session?.finishedAt || '');
  if (Number.isFinite(sessionFinishedAtMs)) {
    return sessionFinishedAtMs;
  }

  const definitelyFinished = session?.status === 'finished' || game?.status === 'finished';
  if (!definitelyFinished) {
    return null;
  }

  const sessionUpdatedAtMs = Date.parse(session?.updatedAt || '');
  if (Number.isFinite(sessionUpdatedAtMs)) {
    return sessionUpdatedAtMs;
  }

  const gameUpdatedAtMs = Date.parse(game?.updatedAt || '');
  if (Number.isFinite(gameUpdatedAtMs)) {
    return gameUpdatedAtMs;
  }

  const gameCreatedAtMs = Date.parse(game?.createdAt || '');
  return Number.isFinite(gameCreatedAtMs) ? gameCreatedAtMs : null;
};

const getRowIndex = (cellNumber) => {
  const normalized = Math.min(FINISH_CELL, clampCellNumber(cellNumber));
  const rowIndex = Math.floor((normalized - 1) / BOARD_COLS) + 1;
  return Math.max(1, Math.min(BOARD_ROWS, rowIndex));
};

const hashTipPlayerToken = (value) => {
  const normalized = String(value || '').trim();
  if (!normalized) {
    return '';
  }

  let hash = 0;
  for (let index = 0; index < normalized.length; index += 1) {
    hash = ((hash << 5) - hash + normalized.charCodeAt(index)) | 0;
  }

  return Math.abs(hash).toString(36);
};

const resolveStopCell = (cell) => {
  if (!Number.isFinite(cell) || cell <= 0) {
    return null;
  }
  return clampCellNumber(cell);
};

const getMoveKey = (move) => {
  if (!move) {
    return null;
  }
  if (move.id !== null && move.id !== undefined) {
    return String(move.id);
  }
  const playerId = move?.playerId;
  const moveIndex = move?.moveIndex;
  if (playerId === null || playerId === undefined) {
    return null;
  }
  return `${playerId}:${moveIndex}`;
};

const ensurePinnedFromCellForMove = (move, fallbackPositions, pinnedFromCellByMoveKeyRef) => {
  const moveKey = getMoveKey(move);
  if (!moveKey) {
    return { moveKey: null, pinnedFromCell: null };
  }
  const existing = pinnedFromCellByMoveKeyRef.current.get(moveKey);
  if (Number.isFinite(existing)) {
    return { moveKey, pinnedFromCell: existing };
  }

  const segmentedPath = resolveSegmentedStepsPath(move);
  if (segmentedPath.length) {
    pinnedFromCellByMoveKeyRef.current.set(moveKey, segmentedPath[0]);
    return { moveKey, pinnedFromCell: segmentedPath[0] };
  }

  const playerId = move?.playerId;
  const fallbackCellRaw = playerId !== null && playerId !== undefined
    ? fallbackPositions?.[playerId]
    : null;
  const pinnedFromCell = resolveMoveStartCell(move, fallbackCellRaw);
  pinnedFromCellByMoveKeyRef.current.set(moveKey, pinnedFromCell);
  return { moveKey, pinnedFromCell };
};

const shouldShowIntroDropStartForPlayer = (
  player,
  introDropPlayedByPlayer,
  introDropPlayedByPlayerRefMap = null,
) => {
  if (!player) {
    return false;
  }
  const playerId = player?.id;
  if (!Number.isInteger(playerId)) {
    return false;
  }
  if (Number.isInteger(player?.startCellNumber)) {
    return false;
  }
  if (introDropPlayedByPlayer[playerId] || introDropPlayedByPlayerRefMap?.get?.(playerId)) {
    return false;
  }
  if (player?.status && player.status !== 'beforeEnter') {
    return false;
  }
  return true;
};

const resolvePlayerDisplayCellForPlayer = (
  player,
  introDropPlayedByPlayer,
  introDropPlayedByPlayerRefMap = null,
) => {
  if (!player) {
    return null;
  }
  if (shouldShowIntroDropStartForPlayer(player, introDropPlayedByPlayer, introDropPlayedByPlayerRefMap)) {
    return FINISH_CELL;
  }
  const rawCell = player?.currentCell;
  return Number.isFinite(rawCell) ? clampCellNumber(rawCell) : 1;
};

const resolveIntroDropTargetCellForMove = (player, move) => {
  const segmentedTarget = resolveSegmentedDropTarget(move);
  if (Number.isFinite(segmentedTarget)) {
    return segmentedTarget;
  }
  return resolveMoveStartCell(move, player?.currentCell);
};

const resolveMoveStepValue = (move) => {
  const diceSeries = Array.isArray(move?.diceSeries) ? move.diceSeries : [];
  if (diceSeries.length) {
    return diceSeries[diceSeries.length - 1];
  }
  return move?.diceValue;
};

const resolveMoveStartCell = (move, fallbackCellRaw = 1) => {
  const fromCell = move?.fromCell;
  if (Number.isFinite(fromCell) && fromCell >= 1) {
    return clampCellNumber(fromCell);
  }
  const stepValue = resolveMoveStepValue(move);
  if (Number.isFinite(move?.toCell) && Number.isFinite(stepValue)) {
    return clampCellNumber(move.toCell - stepValue);
  }
  if (Number.isFinite(fallbackCellRaw)) {
    return clampCellNumber(fallbackCellRaw);
  }
  return 1;
};

const createTransitionMap = (boardConfig = {}) => {
  const snakes = Array.isArray(boardConfig?.snakes) ? boardConfig.snakes : [];
  const ladders = Array.isArray(boardConfig?.ladders) ? boardConfig.ladders : [];
  const transitions = [...snakes, ...ladders];
  const map = new Map();
  transitions.forEach((item) => {
    if (!Number.isFinite(item?.from) || !Number.isFinite(item?.to)) {
      return;
    }
    map.set(clampCellNumber(item.from), clampCellNumber(item.to));
  });
  return map;
};

const getCellPosition = (cellNumber) => {
  const normalized = clampCellNumber(cellNumber);
  const index = normalized - 1;
  const rowFromBottom = Math.floor(index / BOARD_COLS);
  const rowFromTop = BOARD_ROWS - rowFromBottom;
  const offset = index % BOARD_COLS;
  const isEvenRow = rowFromBottom % 2 === 0;
  const column = isEvenRow ? offset + 1 : BOARD_COLS - offset;
  return { row: rowFromTop, column };
};

const getCellCoordinates = (cellNumber) => {
  const { row, column } = getCellPosition(cellNumber);
  return {
    x: column - 0.5,
    y: row - 0.5,
  };
};

const getCellCenter = (cellNumber, centerMap) => {
  const normalized = clampCellNumber(cellNumber);
  const cached = centerMap?.get?.(normalized);
  return cached || getCellCoordinates(normalized);
};

const getCellRect = (cellNumber) => {
  const { row, column } = getCellPosition(cellNumber);
  return {
    width: 100 / BOARD_COLS,
    height: 100 / BOARD_ROWS,
    left: (column - 1) * (100 / BOARD_COLS),
    top: (row - 1) * (100 / BOARD_ROWS),
  };
};

const clampOffset = (value, limit = 32) => Math.max(-limit, Math.min(limit, value));

const getTokenOffset = (index, total) => {
  if (total <= 1) {
    return { x: 0, y: 0 };
  }

  const visibleCount = Math.min(total, 4);
  const layer = Math.floor(index / visibleCount);
  const positionIndex = index % visibleCount;

  const spread = 26;
  const step = spread / Math.max(1, visibleCount - 1);
  const start = -spread / 2;

  const compression = 1 + layer * 0.35 + (total > visibleCount ? (total - visibleCount) * 0.05 : 0);
  const xBase = start + step * positionIndex;
  const x = clampOffset(xBase / compression);

  const arcLift = visibleCount > 2
    ? Math.sin((positionIndex / Math.max(1, visibleCount - 1)) * Math.PI) * -5
    : 0;
  const y = clampOffset(arcLift - layer * 3.5, 20);

  return { x, y };
};

const BoardGrid = memo(({
  cellLayouts,
  cellTitles,
  playersByCell,
  onCellClick,
  highlightPlayerId,
  canShowVisitedCells,
  stopCellsByCell,
  isCompactBoardLabels,
}) => (
  <div className="board-cells-layer">
    {cellLayouts.map(({ cellNumber, style }) => {
      const playersInCell = playersByCell[cellNumber] || [];
      const hasStopMarker = canShowVisitedCells && stopCellsByCell.has(cellNumber);
      const title = cellTitles[cellNumber];
      const isSingleWord = title ? title.trim().split(/\s+/).length === 1 : false;
      return (
        <button
          type="button"
          key={`cell-${cellNumber}`}
          className="board-cell"
          style={style}
          onClick={() => onCellClick(cellNumber)}
        >
          <div className="cell-header">
            <span className="cell-number">{cellNumber}</span>
            {hasStopMarker && <span className="stop-marker" aria-hidden="true" />}
          </div>
          {!isCompactBoardLabels && title && (
            <p className={`cell-title${isSingleWord ? ' cell-title--single' : ''}`}>
              {title}
            </p>
          )}
          {playersInCell.length > 0 && (
            <div className="cell-tokens">
              {playersInCell
                .map((player) => ({
                  player,
                  chipSrc: player?.chipKey ? getChipSrc(player.chipKey) : null,
                }))
                .filter((entry) => entry.chipSrc)
                .map(({ player, chipSrc }, tokenIndex, list) => {
                  const offset = getTokenOffset(tokenIndex, list.length);
                  const isCurrentPlayer = player.id === highlightPlayerId;
                  return (
                    <span
                      key={`token-${player.id}`}
                      className={`cell-token${isCurrentPlayer ? ' current-player' : ''}`}
                      style={{
                        '--token-offset-x': `${offset.x}%`,
                        '--token-offset-y': `${offset.y}%`,
                        '--token-scale': isCurrentPlayer ? 1.12 : 1,
                        zIndex: isCurrentPlayer ? 2 : 1,
                      }}
                    >
                      <img className="cell-token__image" src={chipSrc} alt="" />
                    </span>
                  );
                })}
            </div>
          )}
        </button>
      );
    })}
  </div>
));

const clampSpeedLevel = (value) => {
  const parsed = Math.round(Number(value));
  if (!Number.isFinite(parsed)) {
    return 3;
  }
  return Math.max(1, Math.min(5, parsed));
};

const normalizeSettingsPayload = (rawSettings = {}) => ({
  highlightVisitedCells: Boolean(
    rawSettings.highlightVisitedCells ?? defaultSettings.highlightVisitedCells,
  ),
  onSixBehavior: rawSettings.onSixBehavior || defaultSettings.onSixBehavior,
  movementSpeed: clampSpeedLevel(rawSettings.movementSpeed ?? defaultSettings.movementSpeed),
});

const getStepDurationMs = (speedLevel) => {
  const normalized = clampSpeedLevel(speedLevel);
  const durations = {
    1: 700,
    2: 550,
    3: 420,
    4: 300,
    5: 200,
  };
  return durations[normalized] || durations[3];
};

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const normalizeMovePath = (move) => {
  if (!move) {
    return [];
  }
  const transitions = Array.isArray(move.transitions) ? move.transitions : [];
  const sanitized = transitions
    .map((cell) => clampCellNumber(cell))
    .filter((cell, index, arr) => Number.isFinite(cell) && (index === 0 || cell !== arr[index - 1]));

  const fromCell = clampCellNumber(move.fromCell);
  const toCell = clampCellNumber(move.toCell);

  if (sanitized.length >= 2) {
    const lastCell = sanitized[sanitized.length - 1];
    if (lastCell !== toCell) {
      sanitized.push(toCell);
    }
    return sanitized;
  }

  if (sanitized.length === 1) {
    if (sanitized[0] !== toCell) {
      sanitized.push(toCell);
    }
    return sanitized;
  }

  if (!toCell || toCell === fromCell) {
    return [fromCell];
  }

  return [fromCell, toCell];
};

const sanitizeSegmentPath = (path = []) => {
  if (!Array.isArray(path)) {
    return [];
  }
  return path
    .map((cell) => clampCellNumber(cell))
    .filter((cell, index, arr) => Number.isFinite(cell) && (index === 0 || cell !== arr[index - 1]));
};

const getMoveSegments = (move) => (Array.isArray(move?.segments) ? move.segments : []);

const isEntryMove = (move) => {
  if (!move || typeof move !== 'object') {
    return false;
  }
  if (move?.isEntry === true) {
    return true;
  }
  const segments = getMoveSegments(move);
  if (segments.some((segment) => segment?.type === 'ENTRY')) {
    return true;
  }
  return Number(move?.fromCell) === 0 && Number(move?.diceValue) === 6;
};

const shouldHoldPlayerPositionDuringPendingMove = (player) => Boolean(player?.pendingMoveId);

const cloneSegment = (segment = null) => {
  if (!segment || typeof segment !== 'object') {
    return null;
  }
  const cloned = { ...segment };
  if (Array.isArray(segment.path)) {
    cloned.path = [...segment.path];
  }
  return cloned;
};

const mergeSeriesSegments = (movesList = []) => {
  if (!Array.isArray(movesList) || movesList.length === 0) {
    return [];
  }

  const merged = [];
  movesList.forEach((move) => {
    const moveSegments = getMoveSegments(move);
    if (moveSegments.length) {
      moveSegments.forEach((segment) => {
        const cloned = cloneSegment(segment);
        if (cloned) {
          merged.push(cloned);
        }
      });
      return;
    }

    const fallbackPath = normalizeMovePath(move);
    if (fallbackPath.length) {
      merged.push({ type: 'steps', path: fallbackPath });
    }
  });

  return merged;
};

const resolveSegmentedStepsPath = (move) => {
  const segments = getMoveSegments(move);
  if (!segments.length) {
    return [];
  }

  const normalizedPath = [];
  segments.forEach((segment) => {
    if (!segment || typeof segment !== 'object') {
      return;
    }

    if ((segment.type === 'steps' || segment.type === 'MOVE') && Array.isArray(segment.path)) {
      const pathPart = sanitizeSegmentPath(segment.path);
      pathPart.forEach((cell) => {
        if (!normalizedPath.length || normalizedPath[normalizedPath.length - 1] !== cell) {
          normalizedPath.push(cell);
        }
      });
      return;
    }

    if (segment.type === 'drop' || segment.type === 'ENTRY') {
      const fromCell = clampCellNumber(segment?.from);
      const toCell = clampCellNumber(segment?.to);
      if (Number.isFinite(fromCell) && (!normalizedPath.length || normalizedPath[normalizedPath.length - 1] !== fromCell)) {
        normalizedPath.push(fromCell);
      }
      if (Number.isFinite(toCell) && (!normalizedPath.length || normalizedPath[normalizedPath.length - 1] !== toCell)) {
        normalizedPath.push(toCell);
      }
      return;
    }

    if (segment.type === 'teleport' || segment.type === 'TELEPORT') {
      const fromCell = clampCellNumber(segment?.fromCell ?? segment?.from);
      const toCell = clampCellNumber(segment?.toCell ?? segment?.to);
      if (Number.isFinite(fromCell) && (!normalizedPath.length || normalizedPath[normalizedPath.length - 1] !== fromCell)) {
        normalizedPath.push(fromCell);
      }
      if (Number.isFinite(toCell) && (!normalizedPath.length || normalizedPath[normalizedPath.length - 1] !== toCell)) {
        normalizedPath.push(toCell);
      }
    }
  });

  return normalizedPath;
};

const resolveSegmentedDropTarget = (move) => {
  const segments = getMoveSegments(move);
  if (!segments.length) {
    return null;
  }
  const dropSegment = segments.find((segment) =>
    (segment?.type === 'drop' || segment?.type === 'ENTRY') && Number.isFinite(segment?.to));
  if (!dropSegment) {
    return null;
  }
  return clampCellNumber(dropSegment.to);
};

const mergeMovePaths = (movesList = []) => {
  if (!Array.isArray(movesList) || movesList.length === 0) {
    return [];
  }
  const merged = [];
  movesList.forEach((move) => {
    const path = normalizeMovePath(move);
    if (!path.length) {
      return;
    }
    if (!merged.length) {
      merged.push(...path);
      return;
    }
    const startIndex = merged[merged.length - 1] === path[0] ? 1 : 0;
    for (let index = startIndex; index < path.length; index += 1) {
      merged.push(path[index]);
    }
  });
  return merged;
};

const buildAggregatedSeriesMove = (seriesMoves = []) => {
  if (!Array.isArray(seriesMoves) || seriesMoves.length === 0) {
    return null;
  }
  const ordered = [...seriesMoves].sort((first, second) => {
    const firstIndex = Number.isFinite(first?.moveIndex) ? first.moveIndex : 0;
    const secondIndex = Number.isFinite(second?.moveIndex) ? second.moveIndex : 0;
    return firstIndex - secondIndex;
  });
  const lastMove = ordered[ordered.length - 1];
  const firstMove = ordered[0];
  const mergedSegments = mergeSeriesSegments(ordered);
  const mergedTransitions = mergeMovePaths(ordered);
  const fallbackSeries = ordered
    .map((move) => Number(move?.diceValue))
    .filter((value) => Number.isInteger(value) && value > 0);
  const diceSeries = Array.isArray(lastMove?.diceSeries) && lastMove.diceSeries.length
    ? lastMove.diceSeries
    : fallbackSeries;
  const aggregatedKind = ordered.some((move) => move?.kind === 'burn_reentry')
    ? 'burn_reentry'
    : ordered.some((move) => move?.kind === 'burn_reentry_drop')
      ? 'burn_reentry_drop'
      : lastMove?.kind || null;

  return {
    ...lastMove,
    fromCell: firstMove?.fromCell ?? lastMove?.fromCell,
    toCell: lastMove?.toCell,
    transitions: mergedTransitions,
    segments: mergedSegments,
    diceSeries,
    kind: aggregatedKind,
    seriesMoves: ordered,
  };
};

const buildSequentialPath = (fromCell, toCell) => {
  if (!Number.isFinite(fromCell) || !Number.isFinite(toCell)) {
    return [];
  }
  if (fromCell === toCell) {
    return [fromCell];
  }
  const path = [];
  const step = fromCell < toCell ? 1 : -1;
  for (let cell = fromCell; step > 0 ? cell <= toCell : cell >= toCell; cell += step) {
    path.push(clampCellNumber(cell));
  }
  return path;
};

const resolveMoveAnimationPath = (move, pinnedFromCell, transitionMap) => {
  if (!move) {
    return [];
  }

  const segmentedPath = resolveSegmentedStepsPath(move);
  if (segmentedPath.length) {
    return segmentedPath;
  }

  const path = normalizeMovePath(move);
  const fromCellRaw = Number.isFinite(pinnedFromCell) ? pinnedFromCell : move?.fromCell;
  const hasValidFromCell = Number.isFinite(fromCellRaw) && fromCellRaw >= 1;
  const fromCell = hasValidFromCell ? clampCellNumber(fromCellRaw) : null;
  const toCell = clampCellNumber(move?.toCell);

  if (path.length >= 2) {
    if (fromCell !== null && path[0] !== fromCell) {
      return [fromCell, ...path];
    }
    return path;
  }

  if (fromCell === null) {
    return [];
  }

  if (Number.isFinite(toCell) && fromCell === toCell) {
    return [fromCell];
  }

  const stepValueRaw = resolveMoveStepValue(move);
  const stepValue = Number.isFinite(stepValueRaw) ? Number(stepValueRaw) : null;
  const landingCell = Number.isFinite(stepValue) ? clampCellNumber(fromCell + stepValue) : null;
  const transitionTarget = landingCell !== null && transitionMap?.has?.(landingCell)
    ? transitionMap.get(landingCell)
    : null;

  if (landingCell !== null && Number.isFinite(transitionTarget) && transitionTarget !== landingCell) {
    const pathToLanding = buildSequentialPath(fromCell, landingCell);
    const targetCell = clampCellNumber(transitionTarget);
    if (pathToLanding.length && pathToLanding[pathToLanding.length - 1] !== targetCell) {
      return [...pathToLanding, targetCell];
    }
    if (pathToLanding.length) {
      return pathToLanding;
    }
  }

  if (Number.isFinite(toCell)) {
    return buildSequentialPath(fromCell, toCell);
  }

  return [fromCell];
};

const extractMoveJumps = (resolvedPath = [], transitionMap) => {
  const jumps = [];
  if (!Array.isArray(resolvedPath) || resolvedPath.length < 2) {
    return jumps;
  }

  for (let index = 0; index < resolvedPath.length - 1; index += 1) {
    const fromCell = resolvedPath[index];
    const toCell = resolvedPath[index + 1];
    const transitionTarget = transitionMap?.has?.(fromCell) ? transitionMap.get(fromCell) : null;
    if (Number.isFinite(transitionTarget) && transitionTarget === toCell) {
      jumps.push({
        trigger: fromCell,
        to: toCell,
        direction: toCell > fromCell ? 'up' : 'down',
      });
    }
  }

  return jumps;
};

const buildFullLogRouteSegments = ({
  move,
  pinnedFromCell,
  transitionMap,
  cellTitles = {},
}) => {
  if (!move) {
    return [];
  }

  const resolvedPath = resolveMoveAnimationPath(move, pinnedFromCell, transitionMap);
  const rawStartCell = Number.isFinite(pinnedFromCell) ? pinnedFromCell : move?.fromCell;
  const startCell = isEntryMove(move)
    ? FINISH_CELL
    : Number.isFinite(rawStartCell)
      ? clampCellNumber(rawStartCell)
      : resolvedPath[0];
  const rawFinalCell = move?.toCell;
  const finalCell = Number.isFinite(rawFinalCell)
    ? clampCellNumber(rawFinalCell)
    : resolvedPath[resolvedPath.length - 1];
  if (!Number.isFinite(startCell)) {
    return Number.isFinite(finalCell) ? [{ type: 'cell', value: finalCell }] : [];
  }

  const jumps = extractMoveJumps(resolvedPath, transitionMap);
  const segments = [{ type: 'cell', value: startCell }];
  let currentCell = startCell;

  jumps.forEach((jump) => {
    const rawTitle = cellTitles?.[jump.trigger];
    const fromTitle = typeof rawTitle === 'string' ? rawTitle.trim() : '';
    segments.push({
      type: 'jump',
      from: jump.trigger,
      to: jump.to,
      direction: jump.direction,
      fromTitle,
    });
    currentCell = jump.to;
  });

  if (Number.isFinite(finalCell) && finalCell !== currentCell) {
    segments.push({ type: 'cell', value: finalCell });
  }

  return segments;
};

const splitPathIntoHistorySegments = (pathCells = []) => {
  if (!Array.isArray(pathCells) || pathCells.length < 2) {
    return [];
  }

  const segments = [];
  let current = [pathCells[0]];

  for (let index = 1; index < pathCells.length; index += 1) {
    const previous = pathCells[index - 1];
    const currentCell = pathCells[index];
    const isAdjacent = Math.abs(currentCell - previous) === 1;

    if (isAdjacent) {
      current.push(currentCell);
      continue;
    }

    if (current.length >= 2) {
      segments.push({ type: 'path', cells: current });
    }

    segments.push({ type: 'teleport', fromCell: previous, toCell: currentCell });
    current = [currentCell];
  }

  if (current.length >= 2) {
    segments.push({ type: 'path', cells: current });
  }

  return segments;
};

const getCommonPrefixLength = (first = [], second = []) => {
  const minLength = Math.min(first.length, second.length);
  let index = 0;
  while (index < minLength && first[index] === second[index]) {
    index += 1;
  }
  return index;
};

const isFinishedStatus = (status) => status === 'finishedOn68' || status === 'finished';
const PAUSE_BUTTON_SELECTOR = '[data-player-pause-button="true"]';

const normalizeDiceSeriesItems = (series = []) =>
  (Array.isArray(series) ? series : [])
    .map((item) => {
      const rawValue = item && typeof item === 'object' ? item.value : item;
      const value = Number(rawValue);
      if (!Number.isInteger(value)) {
        return null;
      }
      return {
        value,
        isBurned: Boolean(item && typeof item === 'object' ? item.isBurned : false),
        burnGroupId: item && typeof item === 'object' && typeof item.burnGroupId === 'string' ? item.burnGroupId : null,
      };
    })
    .filter(Boolean);

const normalizeDiceSeries = (series = []) => normalizeDiceSeriesItems(series).map((item) => item.value);

const normalizeLastDiceRoll = (roll) => {
  if (!roll || typeof roll !== 'object') {
    return null;
  }
  const playerId = Number(roll.playerId);
  const value = Number(roll.value ?? roll.diceValue);
  if (!Number.isInteger(playerId) || !Number.isInteger(value)) {
    return null;
  }
  const series = normalizeDiceSeries(roll.series);
  return {
    playerId,
    value,
    series: series.length ? series : null,
    timestamp: roll.timestamp || null,
    rollId: typeof roll.rollId === 'string' ? roll.rollId : null,
  };
};

const resolvePendingSeriesValue = (key, ...sources) => {
  for (const source of sources) {
    if (!source) {
      continue;
    }
    const value = source[key];
    if (value !== undefined && value !== null) {
      return value;
    }
  }
  return [];
};

const resolveLastDiceRoll = (...sources) => {
  for (const source of sources) {
    if (!source) {
      continue;
    }
    if (source.lastDiceRoll) {
      return source.lastDiceRoll;
    }
  }
  return null;
};

const resolveDiceLock = (...sources) => {
  for (const source of sources) {
    if (!source) {
      continue;
    }
    if (source.diceLock) {
      return source.diceLock;
    }
  }
  return null;
};

const renderDiceSeries = (series = [], classNamePrefix = 'current-roll-series') => {
  const items = normalizeDiceSeriesItems(series);
  if (!items.length) {
    return '';
  }
  return items.map((item, index) => (
    <span key={`${classNamePrefix}-${item.value}-${index}`} className={`${classNamePrefix}-token`}>
      {index > 0 && <span className={`${classNamePrefix}-separator`}>+</span>}
      <span className={item.isBurned ? `${classNamePrefix}-burned` : `${classNamePrefix}-part`}>
        {item.value}
      </span>
    </span>
  ));
};

const hasUnanimatedMovesForPlayer = (playerId, movesList, lastAnimatedMoveIndexByPlayerRef) => {
  if (playerId === null || playerId === undefined) {
    return false;
  }
  const lastAnimatedRaw = lastAnimatedMoveIndexByPlayerRef.current.get(playerId);
  const lastAnimated = Number.isFinite(lastAnimatedRaw) ? lastAnimatedRaw : null;
  const baseline = Number.isFinite(lastAnimated) ? lastAnimated : -Infinity;
  return movesList.some((move) =>
    move
    && move.playerId === playerId
    && Number.isFinite(move.moveIndex)
    && move.moveIndex > baseline,
  );
};


const getLastDiceSeriesValue = (series = []) => {
  const items = normalizeDiceSeriesItems(series);
  return items.length ? items[items.length - 1].value : null;
};

const getPendingSeriesInfo = (pendingDiceSeries, pendingAppliedSeries) => {
  const normalizedPendingSeries = normalizeDiceSeries(pendingDiceSeries);
  if (normalizedPendingSeries.length) {
    return {
      series: normalizedPendingSeries,
      latestValue: normalizedPendingSeries[normalizedPendingSeries.length - 1],
      hasPending: true,
    };
  }

  const normalizedAppliedSeries = normalizeDiceSeries(pendingAppliedSeries);
  if (normalizedAppliedSeries.length) {
    return {
      series: normalizedAppliedSeries,
      latestValue: normalizedAppliedSeries[normalizedAppliedSeries.length - 1],
      hasPending: true,
    };
  }

  return {
    series: [],
    latestValue: null,
    hasPending: false,
  };
};

const buildTurnStateFromPlayers = ({
  players = [],
  currentPlayerId = null,
  sessionStatus = null,
  manualTurn = false,
  lastDiceRoll = null,
  diceLock = null,
  manualTurnHandoverRequired = false,
}) => {
  let currentPlayer = currentPlayerId
    ? players.find((player) => player.id === currentPlayerId)
    : null;
  // At session start (beforeEnter), currentPlayerId may be null; find player with pending series
  if (!currentPlayer) {
    currentPlayer = players.find((player) =>
      (Array.isArray(player.pendingAppliedSeries) && player.pendingAppliedSeries.length > 0)
      || (Array.isArray(player.pendingDiceSeries) && player.pendingDiceSeries.length > 0)
    ) || null;
  }
  const resolvedCurrentPlayerId = currentPlayerId || currentPlayer?.id || null;
  const pendingDiceSeries = currentPlayer?.pendingDiceSeries || [];
  const pendingAppliedSeries = currentPlayer?.pendingAppliedSeries || [];
  const hasPendingSeries = pendingDiceSeries.length > 0 || pendingAppliedSeries.length > 0;
  const pendingSeriesRolls = pendingDiceSeries.length ? pendingDiceSeries : pendingAppliedSeries;
  const pendingSeries = hasPendingSeries
    ? {
      playerId: currentPlayer?.id || null,
      moveId: currentPlayer?.pendingMoveId || null,
      rolls: pendingSeriesRolls,
      consecutiveSixes: Number.isInteger(currentPlayer?.consecutiveSixes) ? currentPlayer.consecutiveSixes : 0,
      isPausedForHandover: Boolean(manualTurnHandoverRequired && !resolvedCurrentPlayerId),
      startedAt: null,
    }
    : null;
  const awaitingAction = Boolean(
    pendingAppliedSeries.length > 0
      || (pendingDiceSeries.length > 0 && getLastDiceSeriesValue(pendingDiceSeries) === 6)
      || currentPlayer?.pendingMoveId,
  );
  const turnStatus = manualTurn && sessionStatus === 'running'
    ? (resolvedCurrentPlayerId ? 'ACTIVE' : 'AWAITING_PASS')
    : null;

  return {
    currentPlayerId: resolvedCurrentPlayerId,
    status: turnStatus,
    pendingSixSum: currentPlayer?.pendingSixSum || 0,
    pendingDiceSeries,
    pendingAppliedSeries,
    hasPendingSeries,
    awaitingAction,
    isSeriesReadyToMove: pendingAppliedSeries.length > 0,
    consecutiveSixes: Number.isInteger(currentPlayer?.consecutiveSixes) ? currentPlayer.consecutiveSixes : 0,
    manualTurnHandoverRequired,
    pendingSeries,
    lastDiceRoll,
    diceLock,
  };
};

const parseError = async (response, fallbackMessage) => {
  try {
    const data = await response.json();
    if (data?.error) {
      return data.error;
    }
  } catch (error) {
    // ignore
  }
  return fallbackMessage;
};

const parseResponseError = async (response, fallbackMessage) => {
  try {
    const data = await response.json();
    return {
      message: data?.error || fallbackMessage,
      code: data?.code || data?.error || '',
      payload: data,
    };
  } catch (error) {
    return { message: fallbackMessage, code: '', payload: null };
  }
};

const TURN_CONFLICT_ERROR_KEYS = new Set([
  'Сейчас ход другого игрока',
  'room.roll.reasons.otherTurn',
  'room.roll.continueReasons.otherTurn',
]);

const localizeApiErrorMessage = (errorData, t) => {
  if (!errorData) {
    return t('errors.requestFailed');
  }
  const rawMessage = typeof errorData.message === 'string' ? errorData.message.trim() : '';
  if (TURN_CONFLICT_ERROR_KEYS.has(rawMessage)) {
    return t('room.roll.continueReasons.otherTurn');
  }
  if (isRawSystemText(rawMessage)) {
    return `${t('error.genericBody', { ns: 'common' })} ${t('error.supportHint', { ns: 'common' })}`;
  }
  return rawMessage || `${t('error.genericBody', { ns: 'common' })} ${t('error.supportHint', { ns: 'common' })}`;
};

const SYSTEM_ERROR_MESSAGE_PATTERNS = [
  /failed to fetch/i,
  /networkerror/i,
  /network request failed/i,
  /cors/i,
  /load failed/i,
  /eventsource/i,
  /timeout/i,
  /fetch/i,
  /TypeError:\s*Failed/i,
];

const isSystemErrorMessage = (value) => {
  if (!value || typeof value !== 'string') {
    return false;
  }
  return SYSTEM_ERROR_MESSAGE_PATTERNS.some((pattern) => pattern.test(value));
};

const normalizeUiErrorMessage = (error, fallbackMessage = '') => {
  const rawMessage = typeof error === 'string' ? error : error?.message;
  if (!rawMessage) {
    return fallbackMessage;
  }
  if (isSystemErrorMessage(rawMessage)) {
    return '';
  }
  return rawMessage;
};

const INVALID_DICE_TYPE_ERROR_KEYS = new Set([
  'Некорректный тип кубика',
  'Невірний тип кубика',
  'Invalid dice type',
]);

const localizeDiceTypeErrorMessage = (message, t) => {
  if (!message) {
    return message;
  }
  if (INVALID_DICE_TYPE_ERROR_KEYS.has(message)) {
    return t('errors.invalidDiceType');
  }
  return message;
};

const getSseReconnectDelay = (attempt) => {
  const baseDelayMs = [1000, 2000, 5000, 10000, 15000, 30000][Math.min(Math.max(attempt, 0), 5)] || 30000;
  const jitterMs = Math.floor(Math.random() * 1200);
  return baseDelayMs + jitterMs;
};

const parseRetryAtDelayMs = (retryAt) => {
  if (!retryAt) {
    return 0;
  }
  const retryAtMs = new Date(retryAt).getTime();
  if (!Number.isFinite(retryAtMs)) {
    return 0;
  }
  const nowMs = Date.now();
  if (retryAtMs <= nowMs) {
    return 0;
  }
  return retryAtMs - nowMs;
};

const formatMessageTime = (value, locale) => {
  if (!value) {
    return '';
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '';
  }
  return date.toLocaleTimeString(locale || 'ru-RU', { hour: '2-digit', minute: '2-digit' });
};

const formatMoveTimestamp = (value, locale) => {
  if (!value) {
    return '';
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '';
  }
  return date.toLocaleString(locale || 'ru-RU', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const createConnectionId = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `conn-${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

const CONNECTION_ERROR_CODES = new Set(['ALREADY_CONNECTED', 'SESSION_REPLACED', 'NOT_ACTIVE_CONNECTION']);

const GamePage = ({ mode = 'conductor' }) => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const { navigate } = useLanguageNavigation();
  const { token, user } = useAuth();
  const { t, i18n } = useTranslation(['game', 'dashboard', 'common']);
  const location = useLocation();

  const inviteToken = useMemo(() => searchParams.get('token') || '', [searchParams]);
  const roomId = useMemo(() => searchParams.get('room') || '', [searchParams]);
  const playerId = useMemo(() => {
    const playerParam = searchParams.get('player');
    if (!playerParam) {
      return null;
    }
    const parsed = Number(playerParam);
    return Number.isInteger(parsed) ? parsed : null;
  }, [searchParams]);
  const playerTokenFromUrl = useMemo(
    () => searchParams.get('playerToken') || searchParams.get('participantToken') || '',
    [searchParams],
  );
  const tipOrderReference = useMemo(() => searchParams.get('tipOrderReference') || '', [searchParams]);

  const localizedUserName = useMemo(
    () => getLocalizedProfileField(user, 'name', i18n.language),
    [i18n.language, user],
  );
  const isGuestPlayer = mode === 'player' && !user;
  const isPlayerMode = mode === 'player';
  const [viewportWidth, setViewportWidth] = useState(
    typeof window === 'undefined' ? 1280 : window.innerWidth,
  );

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    let resizeTimer = null;
    const handleResize = () => {
      if (resizeTimer) {
        window.clearTimeout(resizeTimer);
      }

      resizeTimer = window.setTimeout(() => {
        setViewportWidth(window.innerWidth);
      }, 180);
    };

    window.addEventListener('resize', handleResize);
    return () => {
      if (resizeTimer) {
        window.clearTimeout(resizeTimer);
      }
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const [game, setGame] = useState(null);
  const [board, setBoard] = useState(null);
  const [settings, setSettings] = useState(defaultSettings);
  const [speedSaving, setSpeedSaving] = useState(false);
  const [speedSaveError, setSpeedSaveError] = useState('');
  const [cards, setCards] = useState([]);
  const [cardBackgroundUrl, setCardBackgroundUrl] = useState('');
  const [cardTextColor, setCardTextColor] = useState('');
  const playerStorageKey = useMemo(
    () => (roomId && playerId ? `game-session-${roomId}-player-${playerId}-token` : ''),
    [playerId, roomId],
  );
  const connectionStorageKey = useMemo(
    () => (roomId ? `game-session-${roomId}-connection` : ''),
    [roomId],
  );
  const thanksStorageKey = useMemo(
    () => (roomId && playerId ? `game-session-${roomId}-player-${playerId}-thanks-event` : ''),
    [playerId, roomId],
  );
  const playerVisualHistoryStorageKey = useMemo(
    () => (roomId && playerId ? `game-session-${roomId}-player-${playerId}-visual-history` : ''),
    [playerId, roomId],
  );
  const notesStorageKey = useMemo(
    () => (id && playerId ? `game-notes:${id}:${playerId}` : ''),
    [id, playerId],
  );
  const [participantToken, setParticipantToken] = useState('');
  const [connectionId, setConnectionId] = useState('');
  const [connectionIssue, setConnectionIssue] = useState(null);
  const [connectionActionLoading, setConnectionActionLoading] = useState(false);
  const resolvedPlayerToken = useMemo(
    () => participantToken || playerTokenFromUrl,
    [participantToken, playerTokenFromUrl],
  );
  const tipsIdentity = useMemo(() => {
    if (Number.isInteger(playerId)) {
      return `player-${playerId}`;
    }

    const hashedToken = hashTipPlayerToken(resolvedPlayerToken || inviteToken);
    return hashedToken ? `token-${hashedToken}` : '';
  }, [inviteToken, playerId, resolvedPlayerToken]);
  const tipOfferDismissedStorageKey = useMemo(
    () => (id && tipsIdentity ? `tip_offer_dismissed:${id}:${tipsIdentity}` : ''),
    [id, tipsIdentity],
  );

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const isConnectionBlocked = mode === 'player' && Boolean(connectionIssue);

  const [selectedPlayerId, setSelectedPlayerId] = useState(null);
  const playersTabsRef = useRef(null);
  const didAutoPromptTokenPickerRef = useRef(false);
  const [manualViewLock, setManualViewLock] = useState(false);
  const [armedPassPlayerId, setArmedPassPlayerId] = useState(null);
  const [manualValue, setManualValue] = useState('');
  const [rolling, setRolling] = useState(false);
  const [diceRolling, setDiceRolling] = useState(false);
  const [isDiceAnimating, setIsDiceAnimating] = useState(false);
  const [lastRoll, setLastRoll] = useState(null);
  const [lastDiceRolls, setLastDiceRolls] = useState({});
  const [diceLockState, setDiceLockState] = useState(null);
  const [sseActive, setSseActive] = useState(false);
  const [presenceSseActive, setPresenceSseActive] = useState(false);
  const [onlinePlayerIds, setOnlinePlayerIds] = useState(null);
  const [presenceIssue, setPresenceIssue] = useState(false);
  const lastPresenceSnapshotRef = useRef({ onlinePlayerIds: [] });
  const gameSseConnectedRef = useRef(false);
  const streamSseConnectedRef = useRef(false);
  const gameSseRetryTimeoutRef = useRef(null);
  const streamSseRetryTimeoutRef = useRef(null);
  const presenceIntervalRef = useRef(null);
  const presenceAbortRef = useRef(null);
  const streamAbortRef = useRef(null);
  const presenceInFlightRef = useRef(false);
  const isPresenceRunningRef = useRef(false);
  const isStreamRunningRef = useRef(false);
  const presenceStartCountRef = useRef(0);
  const streamStartCountRef = useRef(0);
  const presenceBackoffStepRef = useRef(0);
  const presencePingRateLimitedUntilRef = useRef(0);
  const presenceSnapshotRateLimitedUntilRef = useRef(0);
  const [diceLockTick, setDiceLockTick] = useState(0);
  const [rollError, setRollError] = useState('');
  const [rollToast, setRollToast] = useState({ message: '', visible: false, variant: 'warning' });
  const [turnToast, setTurnToast] = useState({ message: '', visible: false, variant: 'success' });
  const [pendingRollResult, setPendingRollResult] = useState(null);
  const previousTurnPlayerIdRef = useRef(null);

  const rollToastTimerRef = useRef(null);
  const rollToastExitTimerRef = useRef(null);
  const turnToastTimerRef = useRef(null);
  const turnToastExitTimerRef = useRef(null);
  const chatToastTimerRef = useRef(null);
  const chatToastExitTimerRef = useRef(null);
  const knownChatMessageIdsRef = useRef(new Set());
  const chatMessagesContainerRef = useRef(null);
  const chatNextCursorRef = useRef(null);
  const chatHasMoreRef = useRef(false);
  const chatLoadingOlderRef = useRef(false);
  const chatInitialLoadDoneRef = useRef(false);
  const chatNotificationAudioRef = useRef(null);
  const chatNotificationAudioContextRef = useRef(null);
  const chatNotificationGainNodeRef = useRef(null);
  const chatNotificationSourceNodeRef = useRef(null);
  const wasMyTurnRef = useRef(false);
  const seenRollIdsRef = useRef(new Set());
  const diceAnimationRef = useRef({
    inFlight: false,
    promise: null,
    resolve: null,
    playerId: null,
  });
  const [continuingMove, setContinuingMove] = useState(false);
  const [continueError, setContinueError] = useState('');
  const [turnChangeError, setTurnChangeError] = useState('');
  const [switchingPlayerId, setSwitchingPlayerId] = useState(null);
  const [diceTypeUpdatingId, setDiceTypeUpdatingId] = useState(null);
  const [playersPanelError, setPlayersPanelError] = useState('');
  const [superScenarioModalOpen, setSuperScenarioModalOpen] = useState(false);
  const [superScenarioModalMode, setSuperScenarioModalMode] = useState('menu');
  const [superScenarioTargetCell, setSuperScenarioTargetCell] = useState('');
  const [superScenarioFutureMoves, setSuperScenarioFutureMoves] = useState('');
  const [superScenarioApplying, setSuperScenarioApplying] = useState(false);
  const [superScenarioError, setSuperScenarioError] = useState('');

  const [livekitBoundaryKey, setLivekitBoundaryKey] = useState(0);
  const [livekitOpenRequestId, setLivekitOpenRequestId] = useState(0);
  const [livekitStatus, setLivekitStatus] = useState('idle');
  const [livekitEnabled, setLivekitEnabled] = useState(false);
  const [recordingAudioRequestId, setRecordingAudioRequestId] = useState(0);

  const [visualHistoryPlayers, setVisualHistoryPlayers] = useState([]);
  const [visualHistoryEnabled, setVisualHistoryEnabled] = useState(false);

  const [cardModal, setCardModal] = useState({ open: false, cellNumber: null, card: null, error: '' });
  const [notesText, setNotesText] = useState('');
  const notesSaveTimeoutRef = useRef(null);
  const notesLoadedRef = useRef(false);

  useEffect(() => {
    if (!cardModal.open || typeof document === 'undefined') {
      return undefined;
    }

    const { overflow } = document.body.style;
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = overflow;
    };
  }, [cardModal.open]);

  const [requestModalOpen, setRequestModalOpen] = useState(false);
  const [requestDraft, setRequestDraft] = useState('');
  const [savingRequest, setSavingRequest] = useState(false);
  const [requestError, setRequestError] = useState('');

  const [moves, setMoves] = useState([]);
  const [movesLoaded, setMovesLoaded] = useState(false);
  const [movesError, setMovesError] = useState('');
  const [undoingMove, setUndoingMove] = useState(false);
  const [undoError, setUndoError] = useState('');

  const [displayedPositions, setDisplayedPositions] = useState({});
  const [introDropPositions, setIntroDropPositions] = useState({});
  const [introDropPlayedByPlayer, setIntroDropPlayedByPlayer] = useState({});
  const animationQueueRef = useRef([]);
  const isAnimatingRef = useRef(false);
  const isAnimatingMoveRef = useRef(false);
  const [isMoveAnimationRunning, setIsMoveAnimationRunning] = useState(false);
  const [isTurnAnimationInProgress, setIsTurnAnimationInProgress] = useState(false);
  const [pendingAutoHandover, setPendingAutoHandover] = useState(null);
  const turnAnimationAckInFlightRef = useRef('');
  const isMoveAnimating = isMoveAnimationRunning;
  const isMoveAnimationRunningRef = useRef(false);
  const [highlightPlayerId, setHighlightPlayerId] = useState(null);
  const activeMoveAnimationIdRef = useRef(null);
  const pinnedFromCellByMoveKeyRef = useRef(new Map());
  const animatedPathByMoveKeyRef = useRef(new Map());
  const plannedPathByMoveKeyRef = useRef(new Map());
  const trailPathCellsRef = useRef([]);
  const queuedMoveKeysRef = useRef(new Set());
  const processedMoveKeysRef = useRef(new Set());
  const pendingAnimationPlayersRef = useRef(new Map());
  const lastAnimatedMoveIndexByPlayerRef = useRef(new Map());
  const lastAnimatedMoveIdRef = useRef(null);
  const lastProcessedMoveIdRef = useRef(null);
  const introDropPlayedRef = useRef(new Map());
  const introDropAnimationIdRef = useRef(0);
  const introDropAnimatingPlayersRef = useRef(new Set());
  const introDropTokenRefByPlayerId = useRef(new Map());
  const movesInitializedRef = useRef(false);
  const positionsInitializedRef = useRef(false);
  const userInitiatedMoveRef = useRef(false);
  const previousConductorTurnPlayerIdRef = useRef(null);
  const suppressConductorAutoSelectRef = useRef(false);
  const selectedPlayerInitializedRef = useRef(false);
  const continueMoveInFlightRef = useRef(false);
  const continueMoveKeyRef = useRef('');
  const lastAutoContinueKeyRef = useRef('');
  const autoSeriesFirstStepKeyRef = useRef('');
  const autoSeriesFirstStepTriggeredRef = useRef(false);
  const animationSessionRef = useRef(0);
  const animationWatchdogRef = useRef(null);
  const animationLockRef = useRef(ANIMATION_LOCK_NONE);
  const moveAnimationLifecycleRef = useRef({ id: 0, finalized: true });
  const queuedTurnStateRef = useRef(null);
  const appliedTurnStateRef = useRef({ turnState: null, currentPlayerId: null });
  const [turnStateSyncTick, setTurnStateSyncTick] = useState(0);
  const movesLoadSourceRef = useRef('snapshot');
  const lastAppliedStateVersionRef = useRef(0);
  const lastThanksEventIdRef = useRef('');
  const boardAreaRef = useRef(null);
  const requestTextRef = useRef(null);
  const cellStopCountsByPlayerRef = useRef(new Map());
  const rowStopCountsByPlayerRef = useRef(new Map());
  const lastStoppedRowByPlayerRef = useRef(new Map());
  const lastProcessedMoveIndexByPlayerRef = useRef(new Map());
  const processedStopAlertKeysRef = useRef(new Set());
  const stopAlertsInitializedRef = useRef(false);
  const pendingStopAlertsByMoveRef = useRef(new Map());
  const stopAlertsQueueRef = useRef([]);
  const stopAlertsQueueTimerRef = useRef(null);

  const updateDisplayedPositions = useCallback((updater, source = 'unknown') => {
    setDisplayedPositions((prev) => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      if (next === prev || !next || animationLockRef.current !== ANIMATION_LOCK_ENTRY) {
        return next;
      }

      let blocked = false;
      const sanitized = { ...next };
      Object.entries(next).forEach(([playerId, cell]) => {
        const previousCell = prev[playerId];
        if (cell === FINISH_CELL && previousCell !== FINISH_CELL && Number.isFinite(previousCell)) {
          blocked = true;
          sanitized[playerId] = previousCell;
          console.warn('[GamePage] blocked visual overwrite to 68 during ENTRY lock', {
            source,
            playerId: Number(playerId),
            previousCell,
            nextCell: cell,
            stack: new Error().stack,
          });
        }
      });

      if (!blocked) {
        return next;
      }
      return sanitized;
    });
  }, []);

  useEffect(() => {
    isMoveAnimationRunningRef.current = isMoveAnimationRunning;
  }, [isMoveAnimationRunning]);

  useEffect(() => {
    const latestState = {
      turnState: appliedTurnStateRef.current?.turnState || null,
      currentPlayerId: game?.session?.currentPlayerId ?? null,
    };

    if (isMoveAnimationRunningRef.current) {
      queuedTurnStateRef.current = latestState;
      return;
    }

    appliedTurnStateRef.current = latestState;
    queuedTurnStateRef.current = null;
  }, [game?.session?.currentPlayerId, game?.session?.turnState]);

  const [chatMessages, setChatMessages] = useState([]);
  const [chatLoaded, setChatLoaded] = useState(false);
  const [chatFetchError, setChatFetchError] = useState('');
  const [chatFormError, setChatFormError] = useState('');
  const [chatInput, setChatInput] = useState('');
  const [chatSending, setChatSending] = useState(false);
  const [chatUnreadCount, setChatUnreadCount] = useState(0);
  const [chatToast, setChatToast] = useState({ message: '', visible: false, variant: 'neutral' });
  const [chatLoadingOlder, setChatLoadingOlder] = useState(false);

  const [startingSession, setStartingSession] = useState(false);
  const [startError, setStartError] = useState('');
  const [finishingSession, setFinishingSession] = useState(false);
  const [finishError, setFinishError] = useState('');
  const [finishSessionConfirmOpen, setFinishSessionConfirmOpen] = useState(false);

  const [tipsOfferOpen, setTipsOfferOpen] = useState(false);
  const [tipsSelectedAmount, setTipsSelectedAmount] = useState(5);
  const [tipsPaymentLoading, setTipsPaymentLoading] = useState(false);
  const [tipsPaymentError, setTipsPaymentError] = useState('');
  const [globalErrorModalOpen, setGlobalErrorModalOpen] = useState(false);
  const [tipsNoticeVisible, setTipsNoticeVisible] = useState(false);
  const [tipsCustomAmount, setTipsCustomAmount] = useState('');
  const [tipPaymentResult, setTipPaymentResult] = useState(null);
  const [tipPaymentAmount, setTipPaymentAmount] = useState(0);
  const [tipPaymentCurrency, setTipPaymentCurrency] = useState('EUR');
  const [tipPaymentState, setTipPaymentState] = useState('idle');
  const [returnOfferAfterFail, setReturnOfferAfterFail] = useState(false);
  const [tipPaymentOrderRef, setTipPaymentOrderRef] = useState(null);
  const [tipsFlowStartedAt, setTipsFlowStartedAt] = useState(0);
  const [conductorFinishedModalOpen, setConductorFinishedModalOpen] = useState(false);
  const [finishChoiceLoading, setFinishChoiceLoading] = useState(false);
  const [finishChoiceError, setFinishChoiceError] = useState('');

  const [activeRightPanel, setActiveRightPanel] = useState('chat');
  const [layoutMode, setLayoutMode] = useState(() => {
    if (typeof window === 'undefined') {
      return 'classic';
    }
    const storedMode = localStorage.getItem('game-room-layout');
    return storedMode === 'players-left' ? storedMode : 'classic';
  });
  const isPlayersLeftLayout = layoutMode === 'players-left';
  const [showStopAlerts, setShowStopAlerts] = useState(true);
  const [showChatAlerts, setShowChatAlerts] = useState(true);
  const [chatNotificationSoundEnabled, setChatNotificationSoundEnabled] = useState(
    defaultSettings.chatNotificationSoundEnabled,
  );
  const [settingsModalOpen, setSettingsModalOpen] = useState(false);
  const [requestDetailsOpen, setRequestDetailsOpen] = useState(false);
  const [requestTextOverflowing, setRequestTextOverflowing] = useState(false);

  const tipsInFlightRef = useRef(false);
  const tipsPollingTimerRef = useRef(null);
  const tipsPollAbortControllerRef = useRef(null);
  const tipsSyncRequestedAtRef = useRef(0);
  const tipsFinalizedRef = useRef(false);
  const consumedTipOrderRef = useRef('');
  const openedPlayerEndPopupTriggerRef = useRef('');

  const [setupModalOpen, setSetupModalOpen] = useState(false);
  const [setupName, setSetupName] = useState('');
  const [setupChipKey, setSetupChipKey] = useState('');
  const [setupError, setSetupError] = useState('');
  const [setupSaving, setSetupSaving] = useState(false);

  const locale = useMemo(() => {
    if (i18n.language === 'ua') {
      return 'uk-UA';
    }
    if (i18n.language === 'en') {
      return 'en-US';
    }
    return 'ru-RU';
  }, [i18n.language]);

  const isDev = import.meta.env?.MODE !== 'production';
  const apiBaseUrl = import.meta.env?.VITE_API_URL || '';

  const tipTariffs = useMemo(() => {
    return DEFAULT_TIP_TARIFFS;
  }, []);
  const formatCurrency = useMemo(() => {
    try {
      return new Intl.NumberFormat(i18n.language || 'ua', {
        style: 'currency',
        currency: 'EUR',
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      });
    } catch (error) {
      return new Intl.NumberFormat('en', { style: 'currency', currency: 'EUR', minimumFractionDigits: 0, maximumFractionDigits: 2 });
    }
  }, [i18n.language]);

  const handleCloseTipsModal = useCallback(() => {
    setTipsOfferOpen(false);
    setTipsPaymentError('');
  }, []);

  const handleTipClick = useCallback((amount) => {
    setTipsSelectedAmount(amount);
    setTipsPaymentError('');
  }, []);


  const handleCustomTipChange = useCallback((event) => {
    const nextValue = event.target.value;
    setTipsCustomAmount(nextValue);
    setTipsSelectedAmount(null);
    setTipsNoticeVisible(Boolean(nextValue));
    setTipsPaymentError('');
  }, []);

  const handleTipPaymentResultClose = useCallback(() => {
    setTipPaymentState('idle');
    setTipPaymentResult(null);
    setTipPaymentAmount(0);
    setTipPaymentCurrency('EUR');
    setReturnOfferAfterFail(false);
    setTipsOfferOpen(false);
  }, []);

  const markPendingAnimationPlayer = useCallback((playerId) => {
    if (playerId === null || playerId === undefined) {
      return;
    }
    const map = pendingAnimationPlayersRef.current;
    const current = map.get(playerId) || 0;
    map.set(playerId, current + 1);
  }, []);

  const unmarkPendingAnimationPlayer = useCallback((playerId) => {
    if (playerId === null || playerId === undefined) {
      return;
    }
    const map = pendingAnimationPlayersRef.current;
    const current = map.get(playerId) || 0;
    if (current <= 1) {
      map.delete(playerId);
    } else {
      map.set(playerId, current - 1);
    }
  }, []);

  const buildBoardFromGame = useCallback((gamePayload) => {
    if (!gamePayload) {
      return null;
    }

    if (gamePayload.board) {
      return gamePayload.board;
    }

    const snapshot = gamePayload.fieldConfig || gamePayload.field || null;
    if (!snapshot) {
      return null;
    }

    const boardConfig = snapshot.boardConfig || snapshot.config || null;
    const backgroundImageUrl =
      snapshot.backgroundImageUrl
      || snapshot.backgroundUrl
      || snapshot.boardBackgroundUrl
      || snapshot.background?.imageUrl
      || null;

    return {
      id: snapshot.boardId || snapshot.id || gamePayload.boardId || null,
      name: snapshot.boardName || snapshot.name || null,
      backgroundImageUrl,
      background: snapshot.background || (backgroundImageUrl ? { imageUrl: backgroundImageUrl } : null),
      configJson: boardConfig || null,
      cells: snapshot.cells || null,
      grid: snapshot.grid || null,
      cellSchemes: snapshot.cellSchemes || null,
    };
  }, []);

  const transitionMap = useMemo(() => {
    const config =
      game?.fieldConfig?.boardConfig
      || game?.fieldConfig?.config
      || board?.configJson
      || null;
    return createTransitionMap(config || {});
  }, [board?.configJson, game?.fieldConfig?.boardConfig, game?.fieldConfig?.config]);

  const requestedPlayerId = useMemo(() => playerId, [playerId]);

  const setupStorageKey = useMemo(
    () => (selectedPlayerId ? `game-${id}-player-${selectedPlayerId}-setup` : ''),
    [id, selectedPlayerId],
  );
  const stopAlertsStorageKey = useMemo(
    () => (id ? `game-${id}-conductor-show-alerts` : ''),
    [id],
  );
  const chatAlertsStorageKey = useMemo(() => {
    if (!id) {
      return '';
    }

    if (mode === 'conductor') {
      return `game-${id}-conductor-chat-alerts`;
    }

    if (Number.isInteger(playerId)) {
      return `game-${id}-player-${playerId}-chat-alerts`;
    }

    const hashedToken = hashTipPlayerToken(resolvedPlayerToken || inviteToken);
    const playerIdentity = hashedToken ? `token-${hashedToken}` : 'player';
    return `game-${id}-${playerIdentity}-chat-alerts`;
  }, [id, inviteToken, mode, playerId, resolvedPlayerToken]);
  const chatNotificationSoundStorageKey = useMemo(() => {
    if (!id) {
      return '';
    }

    if (mode === 'conductor') {
      return `game-${id}-conductor-chat-notification-sound`;
    }

    if (Number.isInteger(playerId)) {
      return `game-${id}-player-${playerId}-chat-notification-sound`;
    }

    const hashedToken = hashTipPlayerToken(resolvedPlayerToken || inviteToken);
    const playerIdentity = hashedToken ? `token-${hashedToken}` : 'player';
    return `game-${id}-${playerIdentity}-chat-notification-sound`;
  }, [id, inviteToken, mode, playerId, resolvedPlayerToken]);
  const getIntroDropStorageKey = useCallback(
    (playerId) => (id && Number.isInteger(playerId) ? `game-${id}-player-${playerId}-intro-drop` : ''),
    [id],
  );
  const markIntroDropPlayed = useCallback(
    (playerId) => {
      if (!Number.isInteger(playerId)) {
        return;
      }
      if (introDropPlayedRef.current.get(playerId)) {
        return;
      }
      introDropPlayedRef.current.set(playerId, true);
      setIntroDropPlayedByPlayer((prev) => {
        if (prev[playerId]) {
          return prev;
        }
        return { ...prev, [playerId]: true };
      });
      if (typeof window !== 'undefined') {
        const storageKey = getIntroDropStorageKey(playerId);
        if (storageKey) {
          localStorage.setItem(storageKey, '1');
        }
      }
    },
    [getIntroDropStorageKey],
  );

  const personalGame = game?.type === 'personal';
  const isConductorRole = user?.role === 'conductor';
  const isPlayerRole = user?.role === 'player';
  const forcePlayerMode = mode === 'player';
  const hasConductorPrivileges =
    !forcePlayerMode
    && (isConductorRole || game?.createdById === user?.id || personalGame);
  const isRestrictedPlayer =
    forcePlayerMode
    || (isPlayerRole && !personalGame);
  const isPlayerVisualHistoryMode = isRestrictedPlayer && Number.isInteger(playerId);
  const canViewLog = hasConductorPrivileges || forcePlayerMode || isPlayerRole;
  const canOpenSettings = hasConductorPrivileges || isPlayerRole || forcePlayerMode;
  const isSuperRole = !forcePlayerMode && user?.role === 'super';
  const isConductorView = hasConductorPrivileges && !forcePlayerMode && !personalGame;
  const canShowNotes = (isPlayerRole || forcePlayerMode || isGuestPlayer) && Number.isInteger(playerId);
  const leaveFallbackPath = useMemo(
    () => (forcePlayerMode ? '/dashboard/create-game' : '/dashboard/games/group'),
    [forcePlayerMode],
  );

  const session = game?.session || null;
  const sessionParticipants = useMemo(
    () => (Array.isArray(session?.participants) ? session.participants : []),
    [session],
  );
  const sessionStatus = session?.status || null;
  const isSessionRunning = sessionStatus === 'running';
  const canEditDiceType = useMemo(
    () => hasConductorPrivileges
      && !['finished', 'cancelled', 'archived'].includes(game?.status || '')
      && sessionStatus !== 'finished',
    [game?.status, hasConductorPrivileges, sessionStatus],
  );
  const playersList = useMemo(() => (Array.isArray(game?.players) ? game.players : []), [game?.players]);
  const players = playersList ?? [];
  const livekitDisabledByCompletedGame = useMemo(() => {
    const completedAtMs = resolveCompletedGameAtMs({ game, players, session });
    if (!Number.isFinite(completedAtMs)) {
      return false;
    }
    return Date.now() - completedAtMs >= LIVEKIT_COMPLETED_GAME_GRACE_MS;
  }, [game, players, session]);
  const firstMoveIndexByPlayer = useMemo(() => {
    const map = new Map();
    moves.forEach((move) => {
      const playerId = move?.playerId;
      const moveIndex = move?.moveIndex;
      if (playerId === null || playerId === undefined || !Number.isFinite(moveIndex)) {
        return;
      }
      const existing = map.get(playerId);
      if (!Number.isFinite(existing) || moveIndex < existing) {
        map.set(playerId, moveIndex);
      }
    });
    return map;
  }, [moves]);
  const sessionType = session?.gameType || game?.type || null;
  const manualTurn = useMemo(
    () => sessionType !== 'personal' && playersList.length > 1,
    [playersList.length, sessionType],
  );
  const hasPresenceSnapshot = Array.isArray(onlinePlayerIds);
  const onlinePlayersSet = useMemo(
    () => (hasPresenceSnapshot ? new Set(onlinePlayerIds) : null),
    [hasPresenceSnapshot, onlinePlayerIds],
  );
  const onlineCount = useMemo(
    () => (hasPresenceSnapshot ? onlinePlayerIds.length : playersList.length),
    [hasPresenceSnapshot, onlinePlayerIds, playersList.length],
  );
  const isSoloOnline = manualTurn && onlineCount <= 1;
  const manualTurnRequired = manualTurn && !isSoloOnline && players.length > 1;
  const soleOnlinePlayerId = useMemo(() => {
    if (!isSoloOnline || !Array.isArray(onlinePlayerIds) || onlinePlayerIds.length !== 1) {
      return null;
    }
    return onlinePlayerIds[0];
  }, [isSoloOnline, onlinePlayerIds]);
  const isPlayerOnline = useCallback(
    (playerId) => {
      if (hasConductorPrivileges) {
        return true;
      }
      return !hasPresenceSnapshot || onlinePlayersSet?.has(playerId);
    },
    [hasConductorPrivileges, hasPresenceSnapshot, onlinePlayersSet],
  );
  const ensureSoloCurrentPlayer = useCallback(() => {
    if (!isSoloOnline || !soleOnlinePlayerId) {
      return;
    }
    setGame((prev) => {
      if (!prev) {
        return prev;
      }
      const nextSession = prev.session ? { ...prev.session } : null;
      let changed = false;
      if (nextSession && nextSession.currentPlayerId !== soleOnlinePlayerId) {
        nextSession.currentPlayerId = soleOnlinePlayerId;
        changed = true;
      }
      if (prev.currentPlayerId !== soleOnlinePlayerId) {
        changed = true;
      }
      if (!changed) {
        return prev;
      }
      return {
        ...prev,
        session: nextSession,
        currentPlayerId: soleOnlinePlayerId,
      };
    });
  }, [isSoloOnline, soleOnlinePlayerId]);

  useEffect(() => {
    ensureSoloCurrentPlayer();
  }, [ensureSoloCurrentPlayer]);

  const applyLastDiceRollUpdateRef = useCallback((roll, { preferForDisplay = false } = {}) => {
    const normalized = normalizeLastDiceRoll(roll);
    if (!normalized) {
      return null;
    }
    if (normalized.rollId && seenRollIdsRef.current.has(normalized.rollId)) {
      return normalized;
    }
    if (normalized.rollId) {
      seenRollIdsRef.current.add(normalized.rollId);
    }
    setLastDiceRolls((prev) => {
      const existing = prev[normalized.playerId];
      if (existing?.rollId && normalized.rollId && existing.rollId === normalized.rollId) {
        return prev;
      }
      return { ...prev, [normalized.playerId]: { ...existing, ...normalized } };
    });
    if (preferForDisplay) {
      setLastRoll(normalized.value);
    }
    return normalized;
  }, []);

  const applyMoveResponse = useCallback(
    (data) => {
      const nextStateVersion = Number.isInteger(data?.stateVersion) ? data.stateVersion : null;
      if (Number.isInteger(nextStateVersion) && nextStateVersion <= lastAppliedStateVersionRef.current) {
        return;
      }
      if (Number.isInteger(nextStateVersion)) {
        lastAppliedStateVersionRef.current = nextStateVersion;
      }
      const hasMovesPayload = Array.isArray(data?.moves);
      const updatedMoves = hasMovesPayload ? data.moves : null;
      const sessionFromServer = data?.session || null;
      const turnStateFromServer = sessionFromServer?.turnState || data?.turnState || null;
      const playersFromServer = Array.isArray(data?.players) ? data.players : null;
      const lastDiceRollFromServer = resolveLastDiceRoll(
        turnStateFromServer,
        sessionFromServer,
        data?.turnState,
        data,
      );
      const diceLockFromServer = resolveDiceLock(
        turnStateFromServer,
        sessionFromServer,
        data?.turnState,
        data,
      );
      const nextCurrentPlayerId =
        data?.currentPlayerId
        ?? sessionFromServer?.currentPlayerId
        ?? turnStateFromServer?.currentPlayerId
        ?? null;
      const resolvedCurrentPlayerId = isSoloOnline && soleOnlinePlayerId
        ? soleOnlinePlayerId
        : nextCurrentPlayerId;
      const resolvedTurnStateFromServer = turnStateFromServer && isSoloOnline && soleOnlinePlayerId
        ? { ...turnStateFromServer, currentPlayerId: soleOnlinePlayerId }
        : turnStateFromServer;
      const gateFromPayload = data?.pendingAutoHandover
        || sessionFromServer?.turnAnimationGate?.pendingAutoHandover
        || null;

      movesLoadSourceRef.current = 'live';
      movesInitializedRef.current = true;
      if (hasMovesPayload) {
        setMoves(updatedMoves);
      }
      setMovesLoaded(true);
      setMovesError('');

      const shouldShowOwnRoll = mode === 'player'
        ? Number(lastDiceRollFromServer?.playerId) === Number(playerId)
        : !hasConductorPrivileges;
      const normalizedLastDiceRoll = applyLastDiceRollUpdateRef(lastDiceRollFromServer, {
        preferForDisplay: shouldShowOwnRoll,
      });
      setDiceLockState(diceLockFromServer || null);

      setGame((prev) => {
        const baseGame = prev || data?.game || null;
        if (!baseGame) {
          return baseGame;
        }

        const mergedSession = sessionFromServer
          ? { ...(baseGame.session || {}), ...sessionFromServer }
          : baseGame.session
            ? { ...baseGame.session }
            : null;

        if (mergedSession && !turnStateFromServer && playersFromServer) {
          mergedSession.turnState = buildTurnStateFromPlayers({
            players: playersFromServer,
            currentPlayerId: resolvedCurrentPlayerId ?? mergedSession.currentPlayerId ?? null,
            sessionStatus: mergedSession.status || null,
            manualTurn,
            lastDiceRoll: normalizedLastDiceRoll,
            diceLock: diceLockFromServer,
            manualTurnHandoverRequired: mergedSession.manualTurnHandoverRequired,
          });
        }

        if (mergedSession) {
          if (resolvedCurrentPlayerId !== null && resolvedCurrentPlayerId !== undefined) {
            mergedSession.currentPlayerId = resolvedCurrentPlayerId;
          }
          if (resolvedTurnStateFromServer) {
            mergedSession.turnState = resolvedTurnStateFromServer;
          }
        }

        return {
          ...baseGame,
          players: playersFromServer || baseGame.players || [],
          moves: hasMovesPayload ? updatedMoves : baseGame.moves || [],
          session: mergedSession,
          currentPlayerId: resolvedCurrentPlayerId ?? baseGame.currentPlayerId ?? null,
        };
      });

      const pendingDiceSeriesFromPayload = resolvePendingSeriesValue(
        'pendingDiceSeries',
        turnStateFromServer,
        sessionFromServer,
        sessionFromServer?.turnState,
        data?.turnState,
        data,
      );
      const pendingAppliedSeriesFromPayload = resolvePendingSeriesValue(
        'pendingAppliedSeries',
        turnStateFromServer,
        sessionFromServer,
        sessionFromServer?.turnState,
        data?.turnState,
        data,
      );

      const pendingSeriesInfo = getPendingSeriesInfo(
        pendingDiceSeriesFromPayload,
        pendingAppliedSeriesFromPayload,
      );

      if (pendingSeriesInfo.hasPending) {
        setLastRoll(pendingSeriesInfo.latestValue);
      } else if (normalizedLastDiceRoll?.value !== null && normalizedLastDiceRoll?.value !== undefined) {
        setLastRoll(normalizedLastDiceRoll.value);
      } else if (data?.diceValue !== undefined) {
        setLastRoll(data.diceValue);
      }

      if (gateFromPayload?.turnId) {
        setPendingAutoHandover(gateFromPayload);
      } else if (sessionFromServer?.turnAnimationGate?.pendingAutoHandover === null) {
        setPendingAutoHandover(null);
      }

      if (Array.isArray(data?.moves) || gateFromPayload?.turnId) {
        setIsTurnAnimationInProgress(true);
      }
    },
    [applyLastDiceRollUpdateRef, hasConductorPrivileges, isSoloOnline, manualTurn, setGame, soleOnlinePlayerId],
  );

  const getRollValueFromResponse = useCallback((data) => {
    const pendingDiceSeriesFromPayload = resolvePendingSeriesValue(
      'pendingDiceSeries',
      data?.turnState,
      data?.session,
      data?.session?.turnState,
      data,
    );
    const pendingAppliedSeriesFromPayload = resolvePendingSeriesValue(
      'pendingAppliedSeries',
      data?.turnState,
      data?.session,
      data?.session?.turnState,
      data,
    );

    const pendingSeriesInfo = getPendingSeriesInfo(
      pendingDiceSeriesFromPayload,
      pendingAppliedSeriesFromPayload,
    );

    if (pendingSeriesInfo.hasPending) {
      return pendingSeriesInfo.latestValue;
    }

    const lastDiceRollFromPayload = normalizeLastDiceRoll(
      resolveLastDiceRoll(
        data?.turnState,
        data?.session,
        data,
      ),
    );
    if (lastDiceRollFromPayload?.value !== null && lastDiceRollFromPayload?.value !== undefined) {
      return lastDiceRollFromPayload.value;
    }

    if (data?.diceValue !== undefined) {
      return data.diceValue;
    }

    return null;
  }, []);

  useEffect(() => {
    if (mode !== 'player') {
      setParticipantToken('');
      return;
    }
    if (!playerStorageKey) {
      setParticipantToken('');
      return;
    }

    try {
      const stored = localStorage.getItem(playerStorageKey) || '';
      setParticipantToken(stored);
    } catch (storageError) {
      console.error('[PlayerGame] token restore error', storageError);
      setParticipantToken('');
    }
  }, [mode, playerStorageKey]);

  useEffect(() => {
    if (mode !== 'player') {
      setConnectionId('');
      return;
    }
    if (!connectionStorageKey) {
      setConnectionId('');
      return;
    }
    try {
      const storedConnection = sessionStorage.getItem(connectionStorageKey) || '';
      if (storedConnection) {
        setConnectionId(storedConnection);
        return;
      }
      const nextConnectionId = createConnectionId();
      sessionStorage.setItem(connectionStorageKey, nextConnectionId);
      setConnectionId(nextConnectionId);
    } catch (storageError) {
      console.error('[PlayerGame] connection restore error', storageError);
      setConnectionId('');
    }
  }, [connectionStorageKey, mode]);

  const getActiveConnectionId = useCallback(() => {
    let resolvedConnectionId = connectionId;
    if (!resolvedConnectionId && connectionStorageKey) {
      try {
        resolvedConnectionId = sessionStorage.getItem(connectionStorageKey) || '';
      } catch (storageError) {
        console.error('[PlayerGame] connection read error', storageError);
        resolvedConnectionId = '';
      }
    }
    return resolvedConnectionId;
  }, [connectionId, connectionStorageKey]);

  const recordingController = useGameRecording({
    gameId: Number(game?.id || id) || null,
    canControl: hasConductorPrivileges && !forcePlayerMode,
    token,
    participantToken: resolvedPlayerToken,
    getConnectionId: getActiveConnectionId,
  });

  // The uploader belongs to GamePage, NOT the optional video modal: it keeps
  // draining confirmed and interrupted segments after the call window closes.
  const recordingAudioUpload = useAudioUploadSession({
    gameId: Number(game?.id || id) || null,
    token,
    recording: recordingController.status?.recording || null,
    connected: livekitStatus === 'connected' && Boolean(recordingController.status?.active),
    conductor: hasConductorPrivileges && !forcePlayerMode,
  });

  const [recordingAudioDiagnostic, setRecordingAudioDiagnostic] = useState(null);
  useEffect(() => {
    if (!recordingController.status?.active) setRecordingAudioDiagnostic(null);
  }, [recordingController.status?.active]);

  const [recordingActiveSpeakers, setRecordingActiveSpeakers] = useState([]);
  const lastRecordingSpeakersRef = useRef('');
  const recordingSpeakersTimerRef = useRef(null);
  const handleRecordingSpeakers = useCallback((identities) => {
    if (!hasConductorPrivileges || !recordingController.status?.active || !token || !game?.id) {
      setRecordingActiveSpeakers([]);
      return;
    }
    const sorted = [...new Set(Array.isArray(identities) ? identities : [])].sort();
    setRecordingActiveSpeakers(sorted);
    const signature = sorted.join(',');
    if (lastRecordingSpeakersRef.current === signature) return;
    lastRecordingSpeakersRef.current = signature;
    if (recordingSpeakersTimerRef.current) clearTimeout(recordingSpeakersTimerRef.current);
    recordingSpeakersTimerRef.current = setTimeout(() => {
      recordingSpeakersTimerRef.current = null;
      const requestId = crypto.randomUUID();
      fetch(apiUrl(`/api/games/${game.id}/recording/speakers`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ identities: sorted, requestId }),
      }).then((response) => {
        if (!response.ok) console.warn('[Recording] speaker timeline rejected', response.status);
      }).catch((error) => console.warn('[Recording] speaker timeline failed', error));
    }, 300);
  }, [game?.id, hasConductorPrivileges, recordingController.status?.active, token]);

  useEffect(() => {
    if (recordingController.status?.active) return undefined;
    lastRecordingSpeakersRef.current = '';
    setRecordingActiveSpeakers([]);
    if (recordingSpeakersTimerRef.current) clearTimeout(recordingSpeakersTimerRef.current);
    recordingSpeakersTimerRef.current = null;
    return undefined;
  }, [recordingController.status?.active]);

  useEffect(() => () => {
    if (recordingSpeakersTimerRef.current) clearTimeout(recordingSpeakersTimerRef.current);
  }, []);

  const openConnectionIssue = useCallback(
    (issueType) => {
      if (mode !== 'player') {
        return;
      }
      setConnectionIssue(issueType);
      setError('');
      setLoading(false);
    },
    [mode],
  );

  const applyLastDiceRollUpdate = useCallback((roll, { preferForDisplay = false } = {}) => {
    const normalized = normalizeLastDiceRoll(roll);
    if (!normalized) {
      return null;
    }
    if (normalized.rollId && seenRollIdsRef.current.has(normalized.rollId)) {
      return normalized;
    }
    if (normalized.rollId) {
      seenRollIdsRef.current.add(normalized.rollId);
    }
    setLastDiceRolls((prev) => {
      const existing = prev[normalized.playerId];
      if (existing?.rollId && normalized.rollId && existing.rollId === normalized.rollId) {
        return prev;
      }
      return { ...prev, [normalized.playerId]: { ...existing, ...normalized } };
    });
    if (preferForDisplay) {
      setLastRoll(normalized.value);
    }
    return normalized;
  }, []);

  const fetchPlayerState = useCallback(
    async ({ skipLoading = false, ignoreErrors = false, signal, forceTakeover = false } = {}) => {
      if (mode !== 'player') {
        return null;
      }

      if (!roomId || !inviteToken || !playerId) {
        openConnectionIssue('loadFailed');
        return null;
      }

      if (!skipLoading) {
        setLoading(true);
      }
      setError('');

      try {
        const resolvedConnectionId = getActiveConnectionId();
        const queryParts = [
          `token=${inviteToken}`,
          participantToken ? `participantToken=${participantToken}` : null,
          resolvedConnectionId ? `connectionId=${resolvedConnectionId}` : null,
          forceTakeover ? 'force=1' : null,
          `language=${i18n.language}`,
        ].filter(Boolean);

        const response = await fetch(apiUrl(`/api/game-sessions/${roomId}/state?${queryParts.join('&')}`), {
          method: 'GET',
          signal,
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          const errorCode = errorData?.code || errorData?.error || '';
          if (response.status === 409 && CONNECTION_ERROR_CODES.has(errorCode)) {
            openConnectionIssue('singleConnection');
            return null;
          }
          throw new Error('STATE_LOAD_FAILED');
        }

        const data = await response.json();

        const gameFromResponse = data.game || data.state?.game || null;
        const stateFieldConfig = data.state?.fieldConfig || data.state?.field || null;

        if (data.playerToken && playerStorageKey) {
          localStorage.setItem(playerStorageKey, data.playerToken);
          setParticipantToken(data.playerToken);
        }

        const sessionFromState = data.session || data.game?.session || data.state?.session || null;
        const turnStateFromState = data.state?.turnState || null;
        const playersFromState = Array.isArray(data.state?.players) ? data.state.players : null;
        const gameStatusFromState = data.state?.gameStatus || data.game?.status || null;
        const lastDiceRollFromState = resolveLastDiceRoll(
          turnStateFromState,
          sessionFromState,
          data.state,
          data.session,
        );
        const diceLockFromState = resolveDiceLock(
          turnStateFromState,
          sessionFromState,
          data.state,
          data.session,
        );

        const boardFromResponse = buildBoardFromGame(gameFromResponse || null);
        const boardFromStateSnapshot = stateFieldConfig
          ? buildBoardFromGame({ fieldConfig: stateFieldConfig, boardId: gameFromResponse?.boardId })
          : null;
        const resolvedBoard = boardFromResponse || boardFromStateSnapshot || null;

        setGame((prev) => {
          const mergedGame = { ...(prev || {}), ...(gameFromResponse || {}) };

          if (!mergedGame.fieldConfig && stateFieldConfig) {
            mergedGame.fieldConfig = stateFieldConfig;
          }

          if (resolvedBoard && !mergedGame.board) {
            mergedGame.board = resolvedBoard;
          }

          if (sessionFromState || turnStateFromState) {
            mergedGame.session = {
              ...(prev?.session || {}),
              ...(gameFromResponse?.session || {}),
              ...(sessionFromState || {}),
            };
            if (turnStateFromState) {
              mergedGame.session.turnState = turnStateFromState;
            }
          }

          if (playersFromState) {
            mergedGame.players = playersFromState;
          }

          if (gameStatusFromState) {
            mergedGame.status = gameStatusFromState;
          }

          const hasData = Object.keys(mergedGame).length > 0;
          return hasData ? mergedGame : null;
        });

        setBoard((prevBoard) => resolvedBoard || prevBoard || null);

        const movesFromState = Array.isArray(data.state?.moves) ? data.state.moves : [];
        movesLoadSourceRef.current = 'snapshot';
        setMoves(movesFromState);
        setMovesLoaded(true);
        setMovesError('');

        const shouldShowOwnRoll = mode === 'player'
          ? Number(lastDiceRollFromState?.playerId) === Number(playerId)
          : mode !== 'conductor';
        const normalizedLastDiceRoll = applyLastDiceRollUpdateRef(lastDiceRollFromState, {
          preferForDisplay: shouldShowOwnRoll,
        });
        setDiceLockState(diceLockFromState || null);

        const pendingDiceSeriesFromState = resolvePendingSeriesValue(
          'pendingDiceSeries',
          turnStateFromState,
          sessionFromState,
          sessionFromState?.turnState,
          data.state,
        );
        const pendingAppliedSeriesFromState = resolvePendingSeriesValue(
          'pendingAppliedSeries',
          turnStateFromState,
          sessionFromState,
          sessionFromState?.turnState,
          data.state,
        );

        const pendingSeriesInfo = getPendingSeriesInfo(
          pendingDiceSeriesFromState,
          pendingAppliedSeriesFromState,
        );

        if (pendingSeriesInfo.hasPending) {
          setLastRoll(pendingSeriesInfo.latestValue);
        } else if (normalizedLastDiceRoll?.value !== null && normalizedLastDiceRoll?.value !== undefined) {
          setLastRoll(normalizedLastDiceRoll.value);
        } else {
          const completedMoves = movesFromState.filter((move) => move && !move.isDraft);
          if (completedMoves.length) {
            const latestCompletedRoll = completedMoves[completedMoves.length - 1]?.diceValue || null;
            setLastRoll((prev) => (prev !== null && prev !== undefined ? prev : latestCompletedRoll));
          }
        }

        const deckSnapshot = gameFromResponse?.deckSnapshot || data.state?.deckSnapshot || null;
        const cardsFromResponse = Array.isArray(deckSnapshot?.cards) ? deckSnapshot.cards : [];
        setCards(cardsFromResponse);

        const backgroundFromDeck =
          deckSnapshot?.cardBackgroundUrl
          || deckSnapshot?.cardBack?.imageUrl
          || deckSnapshot?.cardBack?.backgroundUrl
          || deckSnapshot?.cardBack?.image?.url
          || deckSnapshot?.background?.imageUrl
          || deckSnapshot?.background?.url
          || deckSnapshot?.backgroundUrl
          || '';
        setCardBackgroundUrl(backgroundFromDeck);
        setCardTextColor(deckSnapshot?.textColor || '');

        setError('');
        setConnectionIssue(null);
        if (!skipLoading) {
          setLoading(false);
        }

        const latestSessionStatus = sessionFromState?.status || gameStatusFromState || null;
        return { sessionStatus: latestSessionStatus };
      } catch (stateError) {
        if (stateError.name === 'AbortError') return null;
        console.error('[PlayerGame] load error', stateError);
        openConnectionIssue('loadFailed');
        return null;
      }
    },
    [
      applyLastDiceRollUpdateRef,
      buildBoardFromGame,
      getActiveConnectionId,
      i18n.language,
      inviteToken,
      mode,
      openConnectionIssue,
      participantToken,
      playerId,
      playerStorageKey,
      roomId,
      t,
    ],
  );

  const sendPresencePing = useCallback(
    async ({ useBeacon = false } = {}) => {
      if (!id) {
        return;
      }
      if (mode === 'conductor' && !token) {
        return;
      }
      if (mode === 'player' && (isConnectionBlocked || !resolvedPlayerToken)) {
        return;
      }

      const payload = {};
      const headers = { 'Content-Type': 'application/json' };

      if (mode === 'conductor') {
        payload.token = token;
        headers.Authorization = `Bearer ${token}`;
      } else if (mode === 'player') {
        payload.participantToken = resolvedPlayerToken;
      }

      if (useBeacon && typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
        const blob = new Blob([JSON.stringify(payload)], { type: 'application/json' });
        navigator.sendBeacon(apiUrl(`/api/games/${id}/presence/ping`), blob);
        return;
      }

      const nowMs = Date.now();
      if (presencePingRateLimitedUntilRef.current > nowMs) {
        return;
      }

      try {
        const response = await fetch(apiUrl(`/api/games/${id}/presence/ping`), {
          method: 'POST',
          headers,
          body: JSON.stringify(payload),
        });
        if (response.status === 429) {
          let delayMs = 0;
          try {
            const data = await response.json();
            delayMs = parseRetryAtDelayMs(data?.retryAt);
          } catch (_error) {
            delayMs = 0;
          }
          if (delayMs > 0) {
            presencePingRateLimitedUntilRef.current = Date.now() + delayMs;
          }
          return;
        }
        if (response.ok) {
          presencePingRateLimitedUntilRef.current = 0;
        }
      } catch (pingError) {
        if (isDev) {
          console.warn('[Presence] ping failed', pingError);
        }
      }
    },
    [id, isConnectionBlocked, isDev, mode, resolvedPlayerToken, token],
  );

  useEffect(() => {
    if (!id) {
      return undefined;
    }
    if (mode === 'conductor' && !token) {
      return undefined;
    }
    if (mode === 'player' && (isConnectionBlocked || !resolvedPlayerToken)) {
      return undefined;
    }

    sendPresencePing();
    const interval = setInterval(() => {
      sendPresencePing();
    }, 10000);

    const handlePageHide = () => {
      sendPresencePing({ useBeacon: true });
    };

    if (typeof window !== 'undefined') {
      window.addEventListener('pagehide', handlePageHide);
      window.addEventListener('beforeunload', handlePageHide);
    }

    return () => {
      clearInterval(interval);
      if (typeof window !== 'undefined') {
        window.removeEventListener('pagehide', handlePageHide);
        window.removeEventListener('beforeunload', handlePageHide);
      }
    };
  }, [id, isConnectionBlocked, mode, resolvedPlayerToken, sendPresencePing, token]);

  const handleConnectionRetry = useCallback(
    async (forceTakeover) => {
      if (connectionActionLoading) {
        return;
      }
      setConnectionActionLoading(true);
      const result = await fetchPlayerState({ skipLoading: true, ignoreErrors: false, forceTakeover });
      if (result) {
        setConnectionIssue(null);
      }
      setConnectionActionLoading(false);
    },
    [connectionActionLoading, fetchPlayerState],
  );

  const disconnectSentRef = useRef(false);

  const sendDisconnect = useCallback(() => {
    if (disconnectSentRef.current) {
      return;
    }
    if (!inviteToken || !roomId || !participantToken) {
      return;
    }
    const resolvedConnectionId = getActiveConnectionId();
    if (!resolvedConnectionId) {
      return;
    }
    const payload = JSON.stringify({
      token: inviteToken,
      participantToken,
      connectionId: resolvedConnectionId,
    });
    const endpoint = apiUrl(`/api/game-sessions/${roomId}/disconnect`);
    disconnectSentRef.current = true;

    if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
      const blob = new Blob([payload], { type: 'application/json' });
      navigator.sendBeacon(endpoint, blob);
      return;
    }

    fetch(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: payload,
      keepalive: true,
      credentials: 'omit',
    }).catch(() => undefined);
  }, [getActiveConnectionId, inviteToken, participantToken, roomId]);

  useEffect(() => {
    disconnectSentRef.current = false;
  }, [inviteToken, participantToken, roomId]);

  const disconnectRef = useRef(() => undefined);

  useEffect(() => {
    disconnectRef.current = sendDisconnect;
  }, [sendDisconnect]);

  useEffect(() => () => {
    disconnectRef.current?.();
  }, []);

  const handleLeaveRoom = useCallback(() => {
    sendDisconnect();
    navigate(leaveFallbackPath);
  }, [leaveFallbackPath, navigate, sendDisconnect]);

  useEffect(() => {
    if (mode !== 'player') {
      return undefined;
    }
    if (!inviteToken || !roomId || !participantToken) {
      return undefined;
    }
    const handlePageHide = () => {
      sendDisconnect();
    };
    const handleBeforeUnload = () => {
      sendDisconnect();
    };
    const handleVisibilityChange = () => {
      if (document.hidden) {
        sendDisconnect();
      }
    };
    window.addEventListener('pagehide', handlePageHide);
    window.addEventListener('beforeunload', handleBeforeUnload);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      window.removeEventListener('pagehide', handlePageHide);
      window.removeEventListener('beforeunload', handleBeforeUnload);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [inviteToken, mode, participantToken, roomId, sendDisconnect]);

  useEffect(() => {
    animationSessionRef.current += 1;
    setMoves([]);
    setMovesLoaded(false);
    setMovesError('');
    setLastRoll(null);
    setLastDiceRolls({});
    setDiceLockState(null);
    if (animationWatchdogRef.current) {
      clearTimeout(animationWatchdogRef.current);
      animationWatchdogRef.current = null;
    }
    seenRollIdsRef.current = new Set();
    updateDisplayedPositions({});
    animationQueueRef.current = [];
    isAnimatingRef.current = false;
    isMoveAnimationRunningRef.current = false;
    setIsMoveAnimationRunning(false);
    moveAnimationLifecycleRef.current = { id: 0, finalized: true };
    queuedTurnStateRef.current = null;
    appliedTurnStateRef.current = {
      turnState: null,
      currentPlayerId: null,
    };
    setTurnStateSyncTick((prev) => prev + 1);
    setHighlightPlayerId(null);
    activeMoveAnimationIdRef.current = null;
    animationLockRef.current = ANIMATION_LOCK_NONE;
    pinnedFromCellByMoveKeyRef.current = new Map();
    animatedPathByMoveKeyRef.current = new Map();
    plannedPathByMoveKeyRef.current = new Map();
    queuedMoveKeysRef.current = new Set();
    processedMoveKeysRef.current = new Set();
    pendingAnimationPlayersRef.current = new Map();
    lastAnimatedMoveIndexByPlayerRef.current = new Map();
    lastAnimatedMoveIdRef.current = null;
    movesInitializedRef.current = false;
    positionsInitializedRef.current = false;
    userInitiatedMoveRef.current = false;
    movesLoadSourceRef.current = 'snapshot';
    setChatMessages([]);
    setChatLoaded(false);
    setChatFetchError('');
    setChatFormError('');
    setChatInput('');
    setChatUnreadCount(0);
    setChatToast({ message: '', visible: false, variant: 'neutral' });
    setChatLoadingOlder(false);
    knownChatMessageIdsRef.current = new Set();
    chatNextCursorRef.current = null;
    chatHasMoreRef.current = false;
    chatLoadingOlderRef.current = false;
    chatInitialLoadDoneRef.current = false;
  }, [id]);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('game-room-layout', layoutMode);
    }
  }, [layoutMode]);

  useEffect(() => {
    if (isPlayersLeftLayout) {
      if (activeRightPanel === 'chat') {
        if (canViewLog) {
          setActiveRightPanel('log');
          return;
        }
        if (canShowNotes) {
          setActiveRightPanel('notes');
          return;
        }
        setActiveRightPanel('none');
        return;
      }
      if (activeRightPanel === 'log' && !canViewLog) {
        setActiveRightPanel(canShowNotes ? 'notes' : 'none');
        return;
      }
      if (activeRightPanel === 'notes' && !canShowNotes) {
        setActiveRightPanel(canViewLog ? 'log' : 'none');
        return;
      }
      if (activeRightPanel === 'none') {
        if (canViewLog) {
          setActiveRightPanel('log');
          return;
        }
        if (canShowNotes) {
          setActiveRightPanel('notes');
        }
      }
      return;
    }
    if (activeRightPanel === 'none') {
      setActiveRightPanel('chat');
      return;
    }
    if (!canViewLog && activeRightPanel === 'log') {
      setActiveRightPanel('chat');
      return;
    }
    if (!canShowNotes && activeRightPanel === 'notes') {
      setActiveRightPanel('chat');
    }
  }, [activeRightPanel, canShowNotes, canViewLog, isPlayersLeftLayout]);

  useEffect(() => () => {
    animationSessionRef.current += 1;
  }, []);


  const fetchGame = useCallback(async () => {
    if (mode !== 'conductor') {
      return;
    }
    if (!token) {
      return;
    }
    setLoading(true);
    setError('');
    try {
      const response = await fetch(apiUrl(`/api/games/${id}`), {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (!response.ok) {
        throw new Error(await parseError(response, t('errors.requestFailed')));
      }
      const data = await response.json();
      setGame(data.game);
      if (Array.isArray(data.game?.moves)) {
        movesLoadSourceRef.current = 'snapshot';
        setMoves(data.game.moves);
        setMovesLoaded(true);
        const lastDiceRollFromServer = resolveLastDiceRoll(
          data.game?.session?.turnState,
          data.game?.session,
          data.game,
        );
        const diceLockFromServer = resolveDiceLock(
          data.game?.session?.turnState,
          data.game?.session,
          data.game,
        );
        const normalizedLastDiceRoll = applyLastDiceRollUpdateRef(lastDiceRollFromServer, {
          preferForDisplay: false,
        });
        setDiceLockState(diceLockFromServer || null);
        const turnStateFromServer = data.game?.session?.turnState || null;
        const pendingSeriesInfo = getPendingSeriesInfo(
          resolvePendingSeriesValue(
            'pendingDiceSeries',
            turnStateFromServer,
            data.game?.session,
            data.game?.session?.turnState,
          ),
          resolvePendingSeriesValue(
            'pendingAppliedSeries',
            turnStateFromServer,
            data.game?.session,
            data.game?.session?.turnState,
          ),
        );
        if (pendingSeriesInfo.hasPending) {
          setLastRoll(pendingSeriesInfo.latestValue);
        } else if (normalizedLastDiceRoll?.value !== null && normalizedLastDiceRoll?.value !== undefined) {
          setLastRoll(normalizedLastDiceRoll.value);
        } else {
          const completedMoves = data.game.moves.filter((move) => move && !move.isDraft);
          if (completedMoves.length) {
            setLastRoll(completedMoves[completedMoves.length - 1]?.diceValue || null);
          }
        }
      }
    } catch (fetchError) {
      const safeMessage = normalizeUiErrorMessage(fetchError, t('errors.requestFailed'));
      setError(safeMessage);
    } finally {
      setLoading(false);
    }
  }, [applyLastDiceRollUpdateRef, id, mode, t, token]);

  const handleUiRetry = useCallback(() => {
    if (mode === 'player') {
      fetchPlayerState({ skipLoading: false, ignoreErrors: false });
      return;
    }
    fetchGame();
  }, [fetchGame, fetchPlayerState, mode]);

  const syncGameState = useCallback(async () => {
    if (mode !== 'conductor') {
      return;
    }
    if (!token) {
      return;
    }
    try {
      const response = await fetch(apiUrl(`/api/games/${id}`), {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) {
        throw new Error(await parseError(response, t('errors.requestFailed')));
      }
      const data = await response.json();
      const movesFromServer = Array.isArray(data.game?.moves) ? data.game.moves : [];
      setGame(data.game || null);
      movesLoadSourceRef.current = 'snapshot';
      setMoves(movesFromServer);
      setMovesLoaded(true);
      setMovesError('');

      const lastDiceRollFromServer = resolveLastDiceRoll(
        data.game?.session?.turnState,
        data.game?.session,
        data.game,
      );
      const diceLockFromServer = resolveDiceLock(
        data.game?.session?.turnState,
        data.game?.session,
        data.game,
      );
      const normalizedLastDiceRoll = applyLastDiceRollUpdateRef(lastDiceRollFromServer, {
        preferForDisplay: false,
      });
      setDiceLockState(diceLockFromServer || null);
      const turnStateFromServer = data.game?.session?.turnState || null;
      const pendingSeriesInfo = getPendingSeriesInfo(
        resolvePendingSeriesValue(
          'pendingDiceSeries',
          turnStateFromServer,
          data.game?.session,
          data.game?.session?.turnState,
        ),
        resolvePendingSeriesValue(
          'pendingAppliedSeries',
          turnStateFromServer,
          data.game?.session,
          data.game?.session?.turnState,
        ),
      );
      if (pendingSeriesInfo.hasPending) {
        setLastRoll(pendingSeriesInfo.latestValue);
      } else if (normalizedLastDiceRoll?.value !== null && normalizedLastDiceRoll?.value !== undefined) {
        setLastRoll(normalizedLastDiceRoll.value);
      } else {
        const completedMoves = movesFromServer.filter((move) => move && !move.isDraft);
        if (completedMoves.length) {
          setLastRoll(completedMoves[completedMoves.length - 1]?.diceValue || null);
        }
      }
    } catch (fetchError) {
      setMovesError((prev) => prev || fetchError.message);
    }
  }, [applyLastDiceRollUpdateRef, id, mode, t, token]);

  const chatPanelVisible = isPlayersLeftLayout || activeRightPanel === 'chat';

  const showIncomingChatToast = useCallback((message) => {
    if (!message) {
      return;
    }

    setChatToast({ message, visible: true, variant: 'neutral' });

    if (chatToastTimerRef.current) {
      clearTimeout(chatToastTimerRef.current);
    }
    if (chatToastExitTimerRef.current) {
      clearTimeout(chatToastExitTimerRef.current);
    }

    chatToastTimerRef.current = setTimeout(() => {
      setChatToast((prev) => (prev.message ? { ...prev, visible: false } : prev));
      chatToastExitTimerRef.current = setTimeout(() => {
        setChatToast({ message: '', visible: false, variant: 'neutral' });
      }, 350);
    }, 3000);
  }, []);

  const playIncomingChatSoundEffect = useCallback(() => {
    if (!chatNotificationSoundEnabled || typeof Audio === 'undefined') {
      return;
    }

    if (!chatNotificationAudioRef.current) {
      chatNotificationAudioRef.current = new Audio(CHAT_NOTIFICATION_SOUND_URL);
      chatNotificationAudioRef.current.preload = 'auto';
      chatNotificationAudioRef.current.volume = CHAT_NOTIFICATION_BASE_VOLUME;
    }

    if (
      typeof window !== 'undefined'
      && typeof window.AudioContext !== 'undefined'
      && !chatNotificationGainNodeRef.current
    ) {
      const audioContext = new window.AudioContext();
      const source = audioContext.createMediaElementSource(chatNotificationAudioRef.current);
      const gainNode = audioContext.createGain();
      gainNode.gain.value = CHAT_NOTIFICATION_VOLUME_MULTIPLIER;
      source.connect(gainNode);
      gainNode.connect(audioContext.destination);
      chatNotificationAudioContextRef.current = audioContext;
      chatNotificationSourceNodeRef.current = source;
      chatNotificationGainNodeRef.current = gainNode;
    }

    const audio = chatNotificationAudioRef.current;
    chatNotificationAudioContextRef.current?.resume().catch(() => {});
    audio.currentTime = 0;
    audio.play().catch(() => {});
  }, [chatNotificationSoundEnabled]);

  const fetchChat = useCallback(async ({ beforeId = null, silent = false } = {}) => {
    const mergeChatMessages = (incoming, { prepend = false } = {}) => {
      const incomingList = Array.isArray(incoming) ? incoming : [];
      setChatMessages((prev) => {
        const prevList = Array.isArray(prev) ? prev : [];
        const merged = prepend ? [...incomingList, ...prevList] : [...prevList, ...incomingList];
        const map = new Map();
        merged.forEach((message) => {
          if (message && message.id !== undefined && message.id !== null) {
            map.set(String(message.id), message);
          }
        });
        return Array.from(map.values()).sort((left, right) => Number(left.id) - Number(right.id));
      });
    };

    if (mode === 'conductor') {
      if (!token) {
        if (isDev) {
          console.warn('[Chat] skip fetch, missing token', { role: 'conductor', gameId: id });
        }
        setChatFetchError(t('errors.requestFailed'));
        return;
      }
      try {
        const query = new URLSearchParams({ limit: '50' });
        if (beforeId) {
          query.set('beforeId', String(beforeId));
        }
        const response = await fetch(apiUrl(`/api/games/${id}/chat?${query.toString()}`), {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!response.ok) {
          throw new Error(await parseError(response, t('errors.requestFailed')));
        }
        const data = await response.json();
        const list = Array.isArray(data.messages) ? data.messages : [];
        chatHasMoreRef.current = Boolean(data?.hasMore);
        chatNextCursorRef.current = data?.nextCursor || null;
        const knownIds = knownChatMessageIdsRef.current;
        const incoming = list.filter((message) => message && !knownIds.has(String(message.id)));
        const hasKnownMessages = knownIds.size > 0;
        list.forEach((message) => {
          if (message && message.id !== undefined && message.id !== null) {
            knownIds.add(String(message.id));
          }
        });
        if (hasKnownMessages) {
          const incomingExternal = incoming.filter((message) => {
            const authorType = typeof message?.authorType === 'string' ? message.authorType.toLowerCase() : '';
            return (mode === 'player' && authorType !== 'player') || (mode === 'conductor' && authorType !== 'conductor');
          });
          if (incomingExternal.length > 0) {
            if (!chatPanelVisible) {
              setChatUnreadCount((prev) => prev + incomingExternal.length);
              const latestIncoming = incomingExternal[incomingExternal.length - 1];
              const toastText = `${latestIncoming.authorName || ''}: ${latestIncoming.text || ''}`.trim();
              if (toastText && showChatAlerts) {
                showIncomingChatToast(toastText);
              }
            }
            playIncomingChatSoundEffect();
          }
        }
        if (beforeId) {
          mergeChatMessages(list, { prepend: true });
        } else if (silent && chatInitialLoadDoneRef.current) {
          mergeChatMessages(incoming);
        } else {
          setChatMessages(list);
        }
        chatInitialLoadDoneRef.current = true;
        setChatLoaded(true);
        setChatFetchError('');
        if (isDev) {
          console.debug('[Chat] fetched', {
            role: 'conductor',
            gameId: id,
            messageCount: list.length,
          });
        }
      } catch (fetchError) {
        setChatFetchError(fetchError.message);
        if (isDev) {
          console.warn('[Chat] fetch failed', {
            role: 'conductor',
            gameId: id,
            error: fetchError.message,
          });
        }
      }
      return;
    }

    if (!roomId || !inviteToken) {
      if (isDev) {
        console.warn('[Chat] skip fetch, missing session data', { role: 'player', roomId, inviteToken });
      }
      setChatFetchError(t('errors.requestFailed'));
      return;
    }

    try {
      const query = new URLSearchParams({ token: inviteToken, limit: '50' });
      if (beforeId) {
        query.set('beforeId', String(beforeId));
      }
      if (resolvedPlayerToken) {
        query.set('participantToken', resolvedPlayerToken);
      }
      const resolvedConnectionId = getActiveConnectionId();
      if (resolvedConnectionId) {
        query.set('connectionId', resolvedConnectionId);
      }
      const response = await fetch(apiUrl(`/api/game-sessions/${roomId}/chat?${query.toString()}`));
      if (!response.ok) {
        const errorData = await parseResponseError(response, t('errors.requestFailed'));
        if (CONNECTION_ERROR_CODES.has(errorData.code)) {
          openConnectionIssue('singleConnection');
          return;
        }
        throw new Error(errorData.message);
      }
      const data = await response.json();
      const list = Array.isArray(data.messages) ? data.messages : [];
      chatHasMoreRef.current = Boolean(data?.hasMore);
      chatNextCursorRef.current = data?.nextCursor || null;
      const knownIds = knownChatMessageIdsRef.current;
      const incoming = list.filter((message) => message && !knownIds.has(String(message.id)));
      const hasKnownMessages = knownIds.size > 0;
      list.forEach((message) => {
        if (message && message.id !== undefined && message.id !== null) {
          knownIds.add(String(message.id));
        }
      });
      if (hasKnownMessages) {
        const incomingExternal = incoming.filter((message) => {
          const authorType = typeof message?.authorType === 'string' ? message.authorType.toLowerCase() : '';
          return (mode === 'player' && authorType !== 'player') || (mode === 'conductor' && authorType !== 'conductor');
        });
        if (incomingExternal.length > 0) {
          if (!chatPanelVisible) {
            setChatUnreadCount((prev) => prev + incomingExternal.length);
            const latestIncoming = incomingExternal[incomingExternal.length - 1];
            const toastText = `${latestIncoming.authorName || ''}: ${latestIncoming.text || ''}`.trim();
            if (toastText && showChatAlerts) {
              showIncomingChatToast(toastText);
            }
          }
          playIncomingChatSoundEffect();
        }
      }
      if (beforeId) {
        mergeChatMessages(list, { prepend: true });
      } else if (silent && chatInitialLoadDoneRef.current) {
        mergeChatMessages(incoming);
      } else {
        setChatMessages(list);
      }
      chatInitialLoadDoneRef.current = true;
      setChatLoaded(true);
      setChatFetchError('');
      if (isDev) {
        console.debug('[Chat] fetched', {
          role: 'player',
          playerId,
          sessionId: roomId,
          gameId: id,
          messageCount: list.length,
        });
      }
    } catch (fetchError) {
      setChatFetchError(fetchError.message);
      if (isDev) {
        console.warn('[Chat] fetch failed', {
          role: 'player',
          playerId,
          sessionId: roomId,
          gameId: id,
          error: fetchError.message,
        });
      }
    }
  }, [
    getActiveConnectionId,
    id,
    inviteToken,
    chatPanelVisible,
    isDev,
    mode,
    openConnectionIssue,
    playIncomingChatSoundEffect,
    playerId,
    resolvedPlayerToken,
    roomId,
    showChatAlerts,
    showIncomingChatToast,
    t,
    token,
  ]);

  const loadOlderChat = useCallback(async () => {
    if (chatLoadingOlderRef.current || !chatHasMoreRef.current || !chatNextCursorRef.current) {
      return;
    }
    const container = chatMessagesContainerRef.current;
    const previousHeight = container ? container.scrollHeight : 0;
    chatLoadingOlderRef.current = true;
    setChatLoadingOlder(true);
    try {
      await fetchChat({ beforeId: chatNextCursorRef.current, silent: true });
      if (container) {
        const nextHeight = container.scrollHeight;
        const delta = nextHeight - previousHeight;
        if (delta > 0) {
          container.scrollTop += delta;
        }
      }
    } finally {
      chatLoadingOlderRef.current = false;
      setChatLoadingOlder(false);
    }
  }, [fetchChat]);

  useEffect(() => {
    fetchGame();
  }, [fetchGame]);

  useEffect(() => {
    setOnlinePlayerIds(null);
  }, [id]);

  useEffect(() => {
    const controller = new AbortController();

    fetchPlayerState({ signal: controller.signal });

    return () => controller.abort();
  }, [fetchPlayerState]);

  useEffect(() => {
    if (mode !== 'conductor' || !token) {
      return undefined;
    }
    syncGameState();
    fetchChat();
    const interval = setInterval(() => {
      syncGameState();
      fetchChat({ silent: true });
    }, 5000);
    return () => clearInterval(interval);
  }, [fetchChat, mode, syncGameState, token]);

  useEffect(() => {
    if (mode !== 'player') {
      return undefined;
    }
    if (isConnectionBlocked) {
      return undefined;
    }

    if (!roomId || !inviteToken) {
      return undefined;
    }

    let intervalId = null;
    const controller = new AbortController();

    const pollState = async () => {
      const result = await fetchPlayerState({ skipLoading: true, ignoreErrors: true, signal: controller.signal });
      if (result?.sessionStatus === 'finished' && intervalId) {
        clearInterval(intervalId);
      }
    };

    pollState();
    intervalId = setInterval(pollState, 4000);

    return () => {
      controller.abort();
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [fetchPlayerState, inviteToken, isConnectionBlocked, mode, roomId]);

  const sseUrl = useMemo(() => {
    if (mode === 'conductor') {
      if (!id || !token) {
        return '';
      }
      const query = new URLSearchParams({ token });
      return apiUrl(`/api/games/${id}/events?${query.toString()}`);
    }
    if (mode !== 'player') {
      return '';
    }
    if (isConnectionBlocked || !roomId || !inviteToken) {
      return '';
    }
    const query = new URLSearchParams({ token: inviteToken });
    if (resolvedPlayerToken) {
      query.set('participantToken', resolvedPlayerToken);
    }
    const resolvedConnectionId = getActiveConnectionId();
    if (resolvedConnectionId) {
      query.set('connectionId', resolvedConnectionId);
    }
    return apiUrl(`/api/game-sessions/${roomId}/events?${query.toString()}`);
  }, [
    getActiveConnectionId,
    id,
    inviteToken,
    isConnectionBlocked,
    mode,
    resolvedPlayerToken,
    roomId,
    token,
  ]);

  const streamUrl = useMemo(() => {
    if (!id) {
      return '';
    }
    if (mode === 'conductor') {
      if (!token) {
        return '';
      }
      const query = new URLSearchParams({ token });
      return apiUrl(`/api/games/${id}/stream?${query.toString()}`);
    }
    if (mode !== 'player') {
      return '';
    }
    if (isConnectionBlocked || !resolvedPlayerToken) {
      return '';
    }
    const query = new URLSearchParams({ participantToken: resolvedPlayerToken });
    return apiUrl(`/api/games/${id}/stream?${query.toString()}`);
  }, [id, isConnectionBlocked, mode, resolvedPlayerToken, token]);

  useEffect(() => {
    if (!sseUrl || typeof EventSource === 'undefined') {
      setSseActive(false);
      return undefined;
    }

    let source = null;
    let reconnectAttempt = 0;
    let stopped = false;

    const clearRetry = () => {
      if (gameSseRetryTimeoutRef.current) {
        clearTimeout(gameSseRetryTimeoutRef.current);
        gameSseRetryTimeoutRef.current = null;
      }
    };

    const connect = () => {
      if (stopped) {
        return;
      }
      clearRetry();
      if (source) {
        source.close();
      }
      source = new EventSource(sseUrl);

      const handleOpen = () => {
        if (!gameSseConnectedRef.current && isDev) {
          console.info('[SSE] game events connected', { mode, id });
        }
        gameSseConnectedRef.current = true;
        reconnectAttempt = 0;
        setSseActive(true);
      };
      const handleError = () => {
        if (gameSseConnectedRef.current) {
          console.warn('[SSE] game events disconnected', { mode, id });
        }
        gameSseConnectedRef.current = false;
        setSseActive(false);
        if (stopped) {
          return;
        }
        source?.close();
        const delay = getSseReconnectDelay(reconnectAttempt);
        reconnectAttempt += 1;
        gameSseRetryTimeoutRef.current = setTimeout(connect, delay);
      };
      const handleDiceRolled = (event) => {
        if (!event?.data) {
          return;
        }
        try {
          const payload = JSON.parse(event.data);
          const rollPlayerId = payload?.lastDiceRoll?.playerId ?? payload?.playerId ?? null;
          if (mode === 'player' && playerId && Number(rollPlayerId) !== Number(playerId)) {
            return;
          }
          applyMoveResponse(payload);
        } catch (parseError) {
          console.error(parseError);
        }
      };
      const handleMoveApplied = (event) => {
        if (!event?.data) {
          return;
        }
        try {
          const payload = JSON.parse(event.data);
          if (!payload || !payload.moveId || !Number.isInteger(payload.playerId)) {
            return;
          }
          const fallbackMoveIndex = Date.now();
          const normalizedPath = Array.isArray(payload.pathCells)
            ? payload.pathCells.filter((cell) => Number.isFinite(Number(cell))).map((cell) => Number(cell))
            : [];
          const syntheticMove = {
            id: Number(payload.moveId),
            moveId: String(payload.moveId),
            playerId: Number(payload.playerId),
            fromCell: Number(payload.startCell),
            toCell: Number(payload.finalCell),
            transitions: normalizedPath,
            moveIndex: Number.isFinite(Number(payload.moveIndex)) ? Number(payload.moveIndex) : fallbackMoveIndex,
            isDraft: false,
          };

          movesLoadSourceRef.current = 'live';
          movesInitializedRef.current = true;
          setMovesLoaded(true);
          setMovesError('');
          setMoves((prev) => {
            const existing = Array.isArray(prev) ? prev : [];
            if (existing.some((move) => String(move?.moveId || '') === syntheticMove.moveId)) {
              return existing;
            }
            return [...existing, syntheticMove].sort((left, right) => {
              const leftIndex = Number.isFinite(left?.moveIndex) ? left.moveIndex : 0;
              const rightIndex = Number.isFinite(right?.moveIndex) ? right.moveIndex : 0;
              return leftIndex - rightIndex;
            });
          });
        } catch (parseError) {
          console.error(parseError);
        }
      };
      const handleMovesUpdated = (event) => {
        if (!event?.data) {
          return;
        }
        try {
          const payload = JSON.parse(event.data);
          applyMoveResponse(payload);
          if (hasConductorPrivileges) {
            const lastMove = Array.isArray(payload?.moves) && payload.moves.length
              ? payload.moves[payload.moves.length - 1]
              : null;
            const hasFinishMove = Number(lastMove?.toCell) === FINISH_CELL;
            if (payload?.session?.awaitingFinishDecision || hasFinishMove) {
              syncGameState();
            }
          }
        } catch (parseError) {
          console.error(parseError);
        }
      };
      const handleGameStateUpdated = (event) => {
        if (!event?.data) {
          return;
        }
        try {
          const payload = JSON.parse(event.data);
          applyMoveResponse(payload);
        } catch (parseError) {
          console.error(parseError);
        }
      };

      source.addEventListener('diceRolled', handleDiceRolled);
      source.addEventListener('move_applied', handleMoveApplied);
      source.addEventListener('movesUpdated', handleMovesUpdated);
      source.addEventListener('gameStateUpdated', handleGameStateUpdated);
      source.addEventListener('recording_status', () => {
        recordingController.refresh().catch(() => {});
      });
      source.addEventListener('open', handleOpen);
      source.addEventListener('error', handleError);
      source.onopen = handleOpen;
      source.onerror = handleError;
    };

    connect();

    return () => {
      stopped = true;
      clearRetry();
      source?.close();
      gameSseConnectedRef.current = false;
      setSseActive(false);
    };
  }, [applyMoveResponse, hasConductorPrivileges, id, isDev, mode, playerId, sseUrl, syncGameState, recordingController.refresh]);

  useEffect(() => {
    if (!streamUrl || typeof EventSource === 'undefined') {
      setPresenceSseActive(false);
      return undefined;
    }
    if (isStreamRunningRef.current) {
      if (isDev) {
        console.warn('[SSE] duplicate game stream start prevented', { mode, id });
      }
      return undefined;
    }

    isStreamRunningRef.current = true;
    streamStartCountRef.current += 1;
    if (isDev) {
      console.info('[SSE] game stream start', { mode, id, starts: streamStartCountRef.current });
    }
    streamAbortRef.current = new AbortController();

    let source = null;
    let reconnectAttempt = 0;
    let stopped = false;

    const clearRetry = () => {
      if (streamSseRetryTimeoutRef.current) {
        clearTimeout(streamSseRetryTimeoutRef.current);
        streamSseRetryTimeoutRef.current = null;
      }
    };

    const connect = () => {
      if (stopped || streamAbortRef.current?.signal.aborted) {
        return;
      }
      clearRetry();
      if (source) {
        source.close();
      }
      source = new EventSource(streamUrl);

      const handleOpen = () => {
        if (!streamSseConnectedRef.current && isDev) {
          console.info('[SSE] game stream connected', { mode, id });
        }
        streamSseConnectedRef.current = true;
        reconnectAttempt = 0;
        setPresenceSseActive(true);
        setPresenceIssue(false);
      };
      const handleError = () => {
        if (streamSseConnectedRef.current && isDev) {
          console.warn('[SSE] game stream disconnected', { mode, id });
        }
        streamSseConnectedRef.current = false;
        setPresenceSseActive(false);
        setPresenceIssue(true);
        if (stopped || streamAbortRef.current?.signal.aborted) {
          return;
        }
        source?.close();
        const delay = getSseReconnectDelay(reconnectAttempt);
        reconnectAttempt += 1;
        streamSseRetryTimeoutRef.current = setTimeout(connect, delay);
      };
      const handleTurnState = (event) => {
        if (!event?.data) {
          return;
        }
        try {
          const payload = JSON.parse(event.data);
          if (!payload || !payload.turnState) {
            return;
          }
          setGame((prev) => {
            if (!prev) {
              return prev;
            }
            const nextSession = { ...(prev.session || {}) };
            nextSession.turnState = payload.turnState;
            if (payload.currentPlayerId !== undefined) {
              nextSession.currentPlayerId = payload.currentPlayerId;
            }
            return { ...prev, session: nextSession };
          });
        } catch (parseError) {
          console.error(parseError);
        }
      };
      const handleTurnChanged = (event) => {
        if (!event?.data) {
          return;
        }
        try {
          const payload = JSON.parse(event.data);
          const nextCurrentPlayerId = payload?.currentPlayerId ?? null;
          const applyTurnChanged = () => {
            setGame((prev) => {
              if (!prev) {
                return prev;
              }
              const nextSession = { ...(prev.session || {}) };
              nextSession.currentPlayerId = nextCurrentPlayerId;
              return { ...prev, session: nextSession, currentPlayerId: nextCurrentPlayerId ?? prev.currentPlayerId ?? null };
            });
          };

          if (isMoveAnimationRunningRef.current) {
            queuedTurnStateRef.current = {
              turnState: appliedTurnStateRef.current?.turnState || null,
              currentPlayerId: nextCurrentPlayerId,
            };
            return;
          }

          applyTurnChanged();
        } catch (parseError) {
          console.error(parseError);
        }
      };
      const handlePresence = (event) => {
        if (!event?.data) {
          return;
        }
        try {
          const payload = JSON.parse(event.data);
          if (Array.isArray(payload?.onlinePlayerIds)) {
            lastPresenceSnapshotRef.current = payload;
            setOnlinePlayerIds(payload.onlinePlayerIds);
            setPresenceIssue(false);
            return;
          }
          setPresenceIssue(true);
        } catch (parseError) {
          console.error(parseError);
          setPresenceIssue(true);
        }
      };

      source.addEventListener('turnState', handleTurnState);
      source.addEventListener('turn_changed', handleTurnChanged);
      source.addEventListener('presence', handlePresence);
      source.addEventListener('open', handleOpen);
      source.addEventListener('error', handleError);
      source.onopen = handleOpen;
      source.onerror = handleError;
    };

    connect();

    return () => {
      stopped = true;
      clearRetry();
      streamAbortRef.current?.abort();
      streamAbortRef.current = null;
      source?.close();
      streamSseConnectedRef.current = false;
      isStreamRunningRef.current = false;
      setPresenceSseActive(false);
    };
  }, [apiBaseUrl, id, isDev, mode, streamUrl]);

  useEffect(() => {
    if (!sseUrl || sseActive) {
      return undefined;
    }
    let intervalId = null;

    if (mode === 'player') {
      if (isConnectionBlocked || !roomId || !inviteToken) {
        return undefined;
      }
      intervalId = setInterval(() => {
        fetchPlayerState({ skipLoading: true, ignoreErrors: true });
      }, 2500);
    } else if (mode === 'conductor') {
      if (!token) {
        return undefined;
      }
      intervalId = setInterval(() => {
        syncGameState();
      }, 2500);
    }

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [fetchPlayerState, inviteToken, isConnectionBlocked, mode, roomId, sseActive, sseUrl, syncGameState, token]);

  useEffect(() => {
    const shouldRun = Boolean(
      id
      && !presenceSseActive
      && ((mode === 'conductor' && token) || (mode === 'player' && !isConnectionBlocked && resolvedPlayerToken)),
    );

    if (!shouldRun) {
      if (presenceIntervalRef.current) {
        clearTimeout(presenceIntervalRef.current);
        presenceIntervalRef.current = null;
      }
      presenceAbortRef.current?.abort();
      presenceAbortRef.current = null;
      presenceInFlightRef.current = false;
      isPresenceRunningRef.current = false;
      return undefined;
    }

    if (isPresenceRunningRef.current) {
      if (isDev) {
        console.warn('[Presence] duplicate poller start prevented', { mode, id });
      }
      return undefined;
    }

    isPresenceRunningRef.current = true;
    presenceStartCountRef.current += 1;
    presenceBackoffStepRef.current = 0;
    if (isDev) {
      console.info('[Presence] poller started', { mode, id, starts: presenceStartCountRef.current });
    }

    const baseInterval = 15000;
    const backoffSchedule = [1500, 3000, 6000, 12000, 20000];
    let stopped = false;

    const clearPresenceTimer = () => {
      if (presenceIntervalRef.current) {
        clearTimeout(presenceIntervalRef.current);
        presenceIntervalRef.current = null;
      }
    };

    const withJitter = (delay) => {
      const jitterMs = Math.floor(Math.random() * 800);
      return delay + jitterMs;
    };

    const scheduleNext = (delay) => {
      if (stopped) {
        return;
      }
      clearPresenceTimer();
      presenceIntervalRef.current = setTimeout(pollPresence, Math.max(300, withJitter(delay)));
    };

    async function pollPresence() {
      if (stopped || presenceInFlightRef.current) {
        return;
      }
      presenceInFlightRef.current = true;
      presenceAbortRef.current = new AbortController();

      try {
        const nowMs = Date.now();
        if (presenceSnapshotRateLimitedUntilRef.current > nowMs) {
          scheduleNext(presenceSnapshotRateLimitedUntilRef.current - nowMs);
          return;
        }
        const query = new URLSearchParams();
        if (mode === 'conductor' && token) {
          query.set('token', token);
        } else if (mode === 'player') {
          query.set('participantToken', resolvedPlayerToken);
        }
        const url = query.toString()
          ? apiUrl(`/api/games/${id}/presence?${query.toString()}`)
          : apiUrl(`/api/games/${id}/presence`);
        const response = await fetch(url, { signal: presenceAbortRef.current.signal, cache: 'no-store' });
        if (!response.ok) {
          setPresenceIssue(true);
          if (response.status === 429) {
            let retryDelayMs = 0;
            try {
              const payload = await response.json();
              retryDelayMs = parseRetryAtDelayMs(payload?.retryAt);
            } catch (_error) {
              retryDelayMs = 0;
            }
            if (retryDelayMs > 0) {
              presenceSnapshotRateLimitedUntilRef.current = Date.now() + retryDelayMs;
              scheduleNext(retryDelayMs);
              return;
            }
          }
          const step = Math.min(presenceBackoffStepRef.current, backoffSchedule.length - 1);
          scheduleNext(backoffSchedule[step]);
          presenceBackoffStepRef.current = Math.min(step + 1, backoffSchedule.length - 1);
          return;
        }
        presenceSnapshotRateLimitedUntilRef.current = 0;

        let data = null;
        try {
          data = await response.json();
        } catch (parseError) {
          data = null;
        }

        if (Array.isArray(data?.onlinePlayerIds)) {
          lastPresenceSnapshotRef.current = data;
          setOnlinePlayerIds(data.onlinePlayerIds);
          setPresenceIssue(false);
          presenceBackoffStepRef.current = 0;
          scheduleNext(baseInterval);
          return;
        }

        const previousPresence = lastPresenceSnapshotRef.current;
        setOnlinePlayerIds(Array.isArray(previousPresence?.onlinePlayerIds) ? previousPresence.onlinePlayerIds : []);
        setPresenceIssue(true);
        const step = Math.min(presenceBackoffStepRef.current, backoffSchedule.length - 1);
        scheduleNext(backoffSchedule[step]);
        presenceBackoffStepRef.current = Math.min(step + 1, backoffSchedule.length - 1);
      } catch (presenceError) {
        if (presenceError.name === 'AbortError') {
          return;
        }
        if (isDev) {
          console.warn('[Presence] poll failed', presenceError);
        }
        setPresenceIssue(true);
        const step = Math.min(presenceBackoffStepRef.current, backoffSchedule.length - 1);
        scheduleNext(backoffSchedule[step]);
        presenceBackoffStepRef.current = Math.min(step + 1, backoffSchedule.length - 1);
      } finally {
        presenceInFlightRef.current = false;
      }
    }

    pollPresence();

    return () => {
      stopped = true;
      clearPresenceTimer();
      presenceAbortRef.current?.abort();
      presenceAbortRef.current = null;
      presenceInFlightRef.current = false;
      isPresenceRunningRef.current = false;
      presenceBackoffStepRef.current = 0;
      presenceSnapshotRateLimitedUntilRef.current = 0;
    };
  }, [apiBaseUrl, id, isConnectionBlocked, isDev, mode, presenceSseActive, resolvedPlayerToken, token]);

  useEffect(() => {
    if (mode !== 'player') {
      return undefined;
    }
    if (isConnectionBlocked) {
      return undefined;
    }
    if (!roomId || !inviteToken) {
      return undefined;
    }
    let intervalId = null;
    const pollChat = () => {
      fetchChat({ silent: true });
    };
    pollChat();
    intervalId = setInterval(pollChat, 4000);
    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [fetchChat, inviteToken, isConnectionBlocked, mode, roomId]);

  useEffect(() => {
    if (mode !== 'conductor' || !token) {
      return;
    }
    const loadSettings = async () => {
      try {
        const response = await fetch(apiUrl('/api/game-settings'), {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!response.ok) {
          throw new Error(await parseError(response, t('errors.requestFailed')));
        }
        const data = await response.json();
        setSettings(normalizeSettingsPayload(data));
      } catch (settingsError) {
        console.error(settingsError);
      }
    };
    loadSettings();
  }, [mode, token]);

  useEffect(() => {
    if (mode !== 'conductor') {
      setBoard(buildBoardFromGame(game));
      return;
    }
    if (!token || !game?.boardId) {
      setBoard(null);
      return;
    }
    const loadBoard = async () => {
      try {
        const response = await fetch(apiUrl(`/api/boards/${game.boardId}`), {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!response.ok) {
          throw new Error(await parseError(response, t('errors.requestFailed')));
        }
        const data = await response.json();
        setBoard(data.board);
      } catch (boardError) {
        console.error(boardError);
        setBoard(null);
      }
    };
    loadBoard();
  }, [buildBoardFromGame, game, game?.boardId, mode, token]);

  useEffect(() => {
    if (mode !== 'conductor') {
      return;
    }
    if (!token || !game?.deckId) {
      setCards([]);
      setCardBackgroundUrl('');
      setCardTextColor('');
      return;
    }
    const loadCards = async () => {
      try {
        const response = await fetch(apiUrl(`/api/decks/${game.deckId}/cards`), {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!response.ok) {
          throw new Error(await parseError(response, t('errors.requestFailed')));
        }
        const data = await response.json();
        setCards(Array.isArray(data.cards) ? data.cards : []);
        setCardBackgroundUrl(data.cardBackgroundUrl || data.backgroundUrl || '');
        setCardTextColor(data.textColor || '');
      } catch (cardsError) {
        console.error(cardsError);
        setCards([]);
        setCardBackgroundUrl('');
        setCardTextColor('');
      }
    };
    loadCards();
  }, [game?.deckId, mode, token]);

  const pendingMovesByPlayer = useMemo(() => {
    const pendingPlayers = new Set();

    moves.forEach((move) => {
      const { moveKey, pinnedFromCell } = ensurePinnedFromCellForMove(
        move,
        displayedPositions,
        pinnedFromCellByMoveKeyRef,
      );
      if (!moveKey) {
        return;
      }
      if (queuedMoveKeysRef.current.has(moveKey) || activeMoveAnimationIdRef.current === moveKey) {
        if (move.playerId !== undefined && move.playerId !== null) {
          pendingPlayers.add(move.playerId);
        }
        return;
      }
      const previousPath =
        plannedPathByMoveKeyRef.current.get(moveKey)
        ?? animatedPathByMoveKeyRef.current.get(moveKey)
        ?? [];
      const resolvedPath = resolveMoveAnimationPath(move, pinnedFromCell, transitionMap);
      if (resolvedPath.length <= previousPath.length) {
        return;
      }
      const commonPrefixLength = getCommonPrefixLength(previousPath, resolvedPath);
      if (commonPrefixLength < previousPath.length) {
        return;
      }
      const startIndex = Math.max(0, commonPrefixLength - 1);
      const tailPath = resolvedPath.slice(startIndex);
      if (tailPath.length >= 2 && move.playerId !== undefined && move.playerId !== null) {
        pendingPlayers.add(move.playerId);
      }
    });

    return pendingPlayers;
  }, [displayedPositions, moves, transitionMap]);

  useEffect(() => {
    if (!Array.isArray(game?.players) || isMoveAnimating || isAnimatingMoveRef.current) {
      return;
    }
    if (animationLockRef.current !== ANIMATION_LOCK_NONE) {
      return;
    }

    const updates = {};

    game.players.forEach((player) => {
      const playerId = player?.id;
      if (playerId === null || playerId === undefined) {
        return;
      }
      if (
        pendingMovesByPlayer.has(playerId)
        || pendingAnimationPlayersRef.current.has(playerId)
        || introDropAnimatingPlayersRef.current.has(playerId)
        || shouldHoldPlayerPositionDuringPendingMove(player)
        || hasUnanimatedMovesForPlayer(playerId, moves, lastAnimatedMoveIndexByPlayerRef)
      ) {
        return;
      }

      const nextCell = resolvePlayerDisplayCellForPlayer(
        player,
        introDropPlayedByPlayer,
        introDropPlayedRef.current,
      );
      if (!Number.isFinite(nextCell)) {
        return;
      }
      updates[playerId] = nextCell;
    });

    if (!Object.keys(updates).length) {
      return;
    }

    updateDisplayedPositions((prev) => {
      let changed = false;
      const next = { ...prev };
      Object.entries(updates).forEach(([playerId, cell]) => {
        if (next[playerId] !== cell) {
          next[playerId] = cell;
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [game?.players, introDropPlayedByPlayer, isMoveAnimating, moves, pendingMovesByPlayer]);

  useEffect(() => {
    if (positionsInitializedRef.current) {
      return;
    }

    if (!Array.isArray(game?.players) || !game.players.length || isMoveAnimating || isAnimatingMoveRef.current) {
      return;
    }
    if (animationLockRef.current !== ANIMATION_LOCK_NONE) {
      return;
    }

    const initialPositions = {};

    game.players.forEach((player) => {
      const playerId = player?.id;
      if (playerId === null || playerId === undefined) {
        return;
      }
      if (
        pendingMovesByPlayer.has(playerId)
        || pendingAnimationPlayersRef.current.has(playerId)
        || introDropAnimatingPlayersRef.current.has(playerId)
        || shouldHoldPlayerPositionDuringPendingMove(player)
        || hasUnanimatedMovesForPlayer(playerId, moves, lastAnimatedMoveIndexByPlayerRef)
      ) {
        return;
      }
      const cell = resolvePlayerDisplayCellForPlayer(
        player,
        introDropPlayedByPlayer,
        introDropPlayedRef.current,
      );
      if (Number.isFinite(cell)) {
        initialPositions[playerId] = cell;
      }
    });

    if (!Object.keys(initialPositions).length) {
      return;
    }

    updateDisplayedPositions((prev) => ({ ...prev, ...initialPositions }));
    positionsInitializedRef.current = true;
  }, [game?.players, introDropPlayedByPlayer, isMoveAnimating, moves, pendingMovesByPlayer]);

  const conductorProfile = game?.conductorProfile || user;

  const conductorDisplayName = useMemo(
    () =>
      getLocalizedProfileField(conductorProfile, 'name', i18n.language)
      || game?.conductorName
      || localizedUserName
      || t('chat.conductorFallback'),
    [conductorProfile, game?.conductorName, i18n.language, localizedUserName, t],
  );

  const getPlayerDisplayName = useCallback(
    (player) => {
      if (!player) {
        return '';
      }
      const nameMode = player.nameMode || 'preset';
      const baseName = personalGame ? conductorDisplayName : player.name;
      const fallbackName = Number.isInteger(player?.id)
        ? t('room.names.playerWithIndex', { id: player.id })
        : t('room.names.player');
      if (nameMode === 'playerInput' && !baseName?.trim?.()) {
        return t('room.names.playerInput');
      }
      return baseName?.trim?.() || fallbackName;
    },
    [conductorDisplayName, personalGame, t],
  );

  const chatAuthorName = useMemo(() => {
    if (mode === 'player') {
      const player = (game?.players || []).find((entry) => entry?.id === playerId);
      return getPlayerDisplayName(player) || t('room.names.player');
    }
    return localizedUserName || conductorDisplayName || t('chat.conductorFallback');
  }, [conductorDisplayName, game?.players, getPlayerDisplayName, localizedUserName, mode, playerId, t]);


  useEffect(() => {
    if (!game?.players?.length) {
      setSelectedPlayerId(null);
      selectedPlayerInitializedRef.current = false;
      return;
    }

    setSelectedPlayerId((prev) => {
      if (prev && game.players.some((player) => player.id === prev)) {
        return prev;
      }

      if (hasConductorPrivileges) {
        return prev;
      }

      if (selectedPlayerInitializedRef.current) {
        return prev;
      }

      selectedPlayerInitializedRef.current = true;

      if (requestedPlayerId && game.players.some((player) => player.id === requestedPlayerId)) {
        return requestedPlayerId;
      }
      return game.players[0].id;
    });
  }, [game?.players, hasConductorPrivileges, requestedPlayerId]);


  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    if (!playersList.length) {
      return;
    }
    const updates = {};
    playersList.forEach((player) => {
      const playerId = player?.id;
      if (!Number.isInteger(playerId) || introDropPlayedRef.current.get(playerId)) {
        return;
      }
      const storageKey = getIntroDropStorageKey(playerId);
      if (!storageKey) {
        return;
      }
      const storedValue = localStorage.getItem(storageKey);
      if (storedValue === '1') {
        introDropPlayedRef.current.set(playerId, true);
        updates[playerId] = true;
      }
    });
    if (Object.keys(updates).length) {
      setIntroDropPlayedByPlayer((prev) => ({ ...prev, ...updates }));
    }
  }, [getIntroDropStorageKey, playersList]);

  useEffect(() => {
    if (typeof window === 'undefined' || !stopAlertsStorageKey) {
      return;
    }
    const storedValue = localStorage.getItem(stopAlertsStorageKey);
    setShowStopAlerts(storedValue !== '0');
  }, [stopAlertsStorageKey]);

  useEffect(() => {
    if (typeof window === 'undefined' || !chatNotificationSoundStorageKey) {
      return;
    }
    const storedValue = localStorage.getItem(chatNotificationSoundStorageKey);
    setChatNotificationSoundEnabled(storedValue !== '0');
  }, [chatNotificationSoundStorageKey]);

  useEffect(() => {
    if (typeof window === 'undefined' || !chatAlertsStorageKey) {
      return;
    }
    const storedValue = localStorage.getItem(chatAlertsStorageKey);
    setShowChatAlerts(storedValue !== '0');
  }, [chatAlertsStorageKey]);

  useEffect(() => {
    if (!isPlayerVisualHistoryMode || typeof window === 'undefined' || !playerVisualHistoryStorageKey) {
      return;
    }
    const storedValue = localStorage.getItem(playerVisualHistoryStorageKey);
    setVisualHistoryEnabled(storedValue === '1');
  }, [isPlayerVisualHistoryMode, playerVisualHistoryStorageKey]);

  useEffect(() => {
    if (!canShowNotes || typeof window === 'undefined' || !notesStorageKey) {
      notesLoadedRef.current = false;
      return;
    }
    const storedValue = localStorage.getItem(notesStorageKey) || '';
    setNotesText(storedValue);
    notesLoadedRef.current = true;
  }, [canShowNotes, notesStorageKey]);

  useEffect(() => {
    if (!notesLoadedRef.current || typeof window === 'undefined' || !notesStorageKey) {
      return () => {};
    }
    if (notesSaveTimeoutRef.current) {
      clearTimeout(notesSaveTimeoutRef.current);
    }
    notesSaveTimeoutRef.current = setTimeout(() => {
      localStorage.setItem(notesStorageKey, notesText);
    }, NOTES_SAVE_DELAY_MS);

    return () => {
      if (notesSaveTimeoutRef.current) {
        clearTimeout(notesSaveTimeoutRef.current);
        notesSaveTimeoutRef.current = null;
      }
    };
  }, [notesStorageKey, notesText]);

  const handleNotesOpen = useCallback(() => {
    setActiveRightPanel('notes');
  }, []);

  const handleNotesChange = useCallback((event) => {
    const { value } = event.target;
    const nextValue = value.length > NOTES_MAX_LENGTH ? value.slice(0, NOTES_MAX_LENGTH) : value;
    setNotesText(nextValue);
  }, []);

  useEffect(() => {
    if (showStopAlerts) {
      return;
    }
    pendingStopAlertsByMoveRef.current = new Map();
    stopAlertsQueueRef.current = [];
    if (stopAlertsQueueTimerRef.current) {
      clearTimeout(stopAlertsQueueTimerRef.current);
      stopAlertsQueueTimerRef.current = null;
    }
  }, [showStopAlerts]);

  useEffect(() => {
    cellStopCountsByPlayerRef.current = new Map();
    rowStopCountsByPlayerRef.current = new Map();
    lastStoppedRowByPlayerRef.current = new Map();
    lastProcessedMoveIndexByPlayerRef.current = new Map();
    processedStopAlertKeysRef.current = new Set();
    stopAlertsInitializedRef.current = false;
    pendingStopAlertsByMoveRef.current = new Map();
    stopAlertsQueueRef.current = [];
    if (stopAlertsQueueTimerRef.current) {
      clearTimeout(stopAlertsQueueTimerRef.current);
      stopAlertsQueueTimerRef.current = null;
    }
  }, [id]);

  const colorMap = useMemo(() => {
    const map = new Map();
    (game?.players || []).forEach((player, index) => {
      map.set(player.id, playerColors[index % playerColors.length]);
    });
    return map;
  }, [game?.players]);

  const getPlayerColor = useCallback(
    (playerId, fallbackIndex = 0) => (
      colorMap.get(playerId) || playerColors[fallbackIndex % playerColors.length] || '#0f172a'
    ),
    [colorMap],
  );

  const takenChipKeys = useMemo(() => {
    const set = new Set();
    (game?.players || []).forEach((player) => {
      if (player?.chipKey) {
        set.add(player.chipKey);
      }
    });
    return set;
  }, [game?.players]);

  const playerLookup = useMemo(() => {
    const map = new Map();
    (game?.players || []).forEach((player) => {
      map.set(player.id, player);
    });
    return map;
  }, [game?.players]);

  const playerDisplayNameLookup = useMemo(() => {
    const map = new Map();
    (game?.players || []).forEach((player) => {
      map.set(player.id, getPlayerDisplayName(player));
    });
    return map;
  }, [game?.players, getPlayerDisplayName]);
  const introDropActivePlayersSet = useMemo(() => {
    const ids = Object.keys(introDropPositions)
      .map((key) => Number(key))
      .filter((value) => Number.isInteger(value));
    return new Set(ids);
  }, [introDropPositions]);

  const playersByCell = useMemo(() => {
    const map = {};
    (game?.players || []).forEach((player, index) => {
      if (introDropActivePlayersSet.has(player.id)) {
        return;
      }
      const cell = clampCellNumber(
        displayedPositions[player.id] ?? player.currentCell ?? 1,
      );
      if (!map[cell]) {
        map[cell] = [];
      }
      map[cell].push({
        ...player,
        color: getPlayerColor(player.id, index),
        chipKey: player.chipKey,
        displayName: getPlayerDisplayName(player),
      });
    });
    return map;
  }, [displayedPositions, game?.players, getPlayerColor, getPlayerDisplayName, introDropActivePlayersSet]);

  const introDropEntries = useMemo(
    () => Object.entries(introDropPositions)
      .map(([playerIdKey, position]) => {
        const playerId = Number(playerIdKey);
        if (!Number.isInteger(playerId)) {
          return null;
        }
        const player = playerLookup.get(playerId);
        const chipSrc = player?.chipKey ? getChipSrc(player.chipKey) : null;
        if (!chipSrc || !position) {
          return null;
        }
        return {
          playerId,
          chipSrc,
          position,
          isCurrentPlayer: playerId === highlightPlayerId,
        };
      })
      .filter(Boolean),
    [highlightPlayerId, introDropPositions, playerLookup],
  );

  const completedMoves = useMemo(() => moves.filter((move) => move && !move.isDraft), [moves]);

  const resolveStopCellFromMove = useCallback((move) => {
    if (!move) {
      return null;
    }
    if (Number.isFinite(move?.toCell)) {
      return clampCellNumber(move.toCell);
    }
    const { pinnedFromCell } = ensurePinnedFromCellForMove(
      move,
      displayedPositions,
      pinnedFromCellByMoveKeyRef,
    );
    const resolvedPath = resolveMoveAnimationPath(move, pinnedFromCell, transitionMap);
    if (!resolvedPath.length) {
      return null;
    }
    const lastCell = resolvedPath[resolvedPath.length - 1];
    return Number.isFinite(lastCell) ? clampCellNumber(lastCell) : null;
  }, [displayedPositions, transitionMap]);

  const showRollToast = useCallback((message, variant = 'warning') => {
    setRollToast({ message, visible: true, variant });

    if (rollToastTimerRef.current) {
      clearTimeout(rollToastTimerRef.current);
    }
    if (rollToastExitTimerRef.current) {
      clearTimeout(rollToastExitTimerRef.current);
    }

    rollToastTimerRef.current = setTimeout(() => {
      setRollToast((prev) => (prev.message ? { ...prev, visible: false } : prev));
      rollToastExitTimerRef.current = setTimeout(() => {
        setRollToast({ message: '', visible: false, variant });
      }, 350);
    }, 3000);
  }, []);

  const processStopAlertQueue = useCallback(() => {
    if (stopAlertsQueueTimerRef.current) {
      return;
    }
    const queue = stopAlertsQueueRef.current;
    if (!queue.length) {
      return;
    }
    const nextMessage = queue.shift();
    if (nextMessage) {
      showRollToast(nextMessage, 'warning');
    }
    stopAlertsQueueTimerRef.current = setTimeout(() => {
      stopAlertsQueueTimerRef.current = null;
      processStopAlertQueue();
    }, 3350);
  }, [showRollToast]);

  const queuePendingStopAlert = useCallback((moveKey, alert) => {
    if (!moveKey) {
      return;
    }
    const pendingMap = pendingStopAlertsByMoveRef.current;
    const current = pendingMap.get(moveKey) || [];
    pendingMap.set(moveKey, [...current, alert]);
  }, []);

  const releasePendingStopAlerts = useCallback((moveKey) => {
    if (!moveKey) {
      return;
    }
    const pendingMap = pendingStopAlertsByMoveRef.current;
    const pending = pendingMap.get(moveKey);
    if (!pending?.length) {
      pendingMap.delete(moveKey);
      return;
    }
    if (showStopAlerts && hasConductorPrivileges) {
      stopAlertsQueueRef.current.push(...pending.map((alert) => alert.text));
      processStopAlertQueue();
    }
    pendingMap.delete(moveKey);
  }, [hasConductorPrivileges, processStopAlertQueue, showStopAlerts]);

  const stopSequenceByPlayer = useMemo(() => {
    const sequences = {};
    const earliestMoves = new Map();
    const movesByPlayer = new Map();

    completedMoves.forEach((move) => {
      if (!move) {
        return;
      }
      const playerId = move?.playerId;
      if (playerId === null || playerId === undefined) {
        return;
      }
      const existing = earliestMoves.get(playerId);
      if (!existing || (Number.isInteger(move.moveIndex) && move.moveIndex < existing.moveIndex)) {
        earliestMoves.set(playerId, move);
      }
      const list = movesByPlayer.get(playerId) || [];
      list.push(move);
      movesByPlayer.set(playerId, list);
    });

    movesByPlayer.forEach((list) => {
      list.sort((first, second) => {
        const firstIndex = Number.isInteger(first.moveIndex) ? first.moveIndex : 0;
        const secondIndex = Number.isInteger(second.moveIndex) ? second.moveIndex : 0;
        if (firstIndex !== secondIndex) {
          return firstIndex - secondIndex;
        }
        const firstTime = first?.createdAt ? new Date(first.createdAt).getTime() : 0;
        const secondTime = second?.createdAt ? new Date(second.createdAt).getTime() : 0;
        return firstTime - secondTime;
      });
    });

    (game?.players || []).forEach((player) => {
      const playerId = player?.id;
      if (playerId === null || playerId === undefined) {
        return;
      }
      const playerMoves = movesByPlayer.get(playerId) || [];
      const firstMove = earliestMoves.get(playerId);
      const initialCell = resolveStopCell(firstMove?.fromCell)
        ?? resolveStopCell(player?.currentCell);
      const sequence = [];
      const visitedCells = new Set();
      if (Number.isFinite(initialCell)) {
        sequence.push(initialCell);
        visitedCells.add(initialCell);
      }
      playerMoves.forEach((move) => {
        const { pinnedFromCell } = ensurePinnedFromCellForMove(
          move,
          displayedPositions,
          pinnedFromCellByMoveKeyRef,
        );
        const resolvedPath = resolveMoveAnimationPath(move, pinnedFromCell, transitionMap);
        const jumps = extractMoveJumps(resolvedPath, transitionMap);
        jumps.forEach((jump) => {
          const triggerCell = resolveStopCell(jump.trigger);
          if (!Number.isFinite(triggerCell)) {
            return;
          }
          if (visitedCells.has(triggerCell)) {
            return;
          }
          sequence.push(triggerCell);
          visitedCells.add(triggerCell);
        });
        const stopCell = resolveStopCell(move?.toCell);
        if (!Number.isFinite(stopCell)) {
          return;
        }
        const lastStop = sequence[sequence.length - 1];
        const fromCell = resolveStopCell(move?.fromCell);
        if (Number.isFinite(fromCell) && fromCell === stopCell) {
          return;
        }
        if (lastStop === stopCell) {
          return;
        }
        sequence.push(stopCell);
        visitedCells.add(stopCell);
      });
      sequences[playerId] = sequence;
    });

    return sequences;
  }, [
    completedMoves,
    displayedPositions,
    game?.players,
    pinnedFromCellByMoveKeyRef,
    transitionMap,
  ]);

  useEffect(() => {
    if (mode !== 'conductor' || !hasConductorPrivileges) {
      return;
    }
    const orderedMoves = moves
      .filter((move) => move && !move.isDraft && Number.isFinite(move.moveIndex))
      .slice()
      .sort((first, second) => first.moveIndex - second.moveIndex);
    if (!orderedMoves.length) {
      stopAlertsInitializedRef.current = true;
      return;
    }

    const shouldEnqueue = stopAlertsInitializedRef.current;

    orderedMoves.forEach((move) => {
      const playerId = move?.playerId;
      const moveIndex = move?.moveIndex;
      if (!Number.isInteger(playerId) || !Number.isFinite(moveIndex)) {
        return;
      }
      const lastProcessed = lastProcessedMoveIndexByPlayerRef.current.get(playerId);
      if (Number.isFinite(lastProcessed) && moveIndex <= lastProcessed) {
        return;
      }

      const { pinnedFromCell } = ensurePinnedFromCellForMove(
        move,
        displayedPositions,
        pinnedFromCellByMoveKeyRef,
      );
      const fromCell = Number.isFinite(pinnedFromCell)
        ? resolveStopCell(pinnedFromCell)
        : resolveStopCell(move?.fromCell);
      const stopCell = resolveStopCellFromMove(move);
      if (!Number.isFinite(stopCell)) {
        lastProcessedMoveIndexByPlayerRef.current.set(playerId, moveIndex);
        return;
      }
      if (Number.isFinite(fromCell) && fromCell === stopCell) {
        lastProcessedMoveIndexByPlayerRef.current.set(playerId, moveIndex);
        return;
      }

      const rowIndex = getRowIndex(stopCell);

      const cellCounts = cellStopCountsByPlayerRef.current.get(playerId) || new Map();
      const rowCounts = rowStopCountsByPlayerRef.current.get(playerId) || new Map();
      const lastStoppedRow = lastStoppedRowByPlayerRef.current.get(playerId);

      const nextCellCount = (cellCounts.get(stopCell) || 0) + 1;

      cellCounts.set(stopCell, nextCellCount);
      cellStopCountsByPlayerRef.current.set(playerId, cellCounts);

      let nextRowCount = rowCounts.get(rowIndex) || 0;
      let shouldCountRow = false;

      if (lastStoppedRow === null || lastStoppedRow === undefined) {
        shouldCountRow = true;
        lastStoppedRowByPlayerRef.current.set(playerId, rowIndex);
      } else if (rowIndex !== lastStoppedRow) {
        shouldCountRow = true;
        lastStoppedRowByPlayerRef.current.set(playerId, rowIndex);
      }

      if (shouldCountRow) {
        nextRowCount += 1;
        rowCounts.set(rowIndex, nextRowCount);
        rowStopCountsByPlayerRef.current.set(playerId, rowCounts);
      }

      const moveKey = getMoveKey(move);
      if (shouldEnqueue && moveKey) {
        if (nextCellCount >= 2) {
          const alertKey = `${moveKey}:cell`;
          if (!processedStopAlertKeysRef.current.has(alertKey)) {
            processedStopAlertKeysRef.current.add(alertKey);
            queuePendingStopAlert(moveKey, {
              id: alertKey,
              type: 'cell',
              playerId,
              count: nextCellCount,
              cellNumber: stopCell,
              text: t('room.alerts.cellVisited', { count: nextCellCount }),
            });
          }
        }
        if (shouldCountRow && nextRowCount >= 2) {
          const alertKey = `${moveKey}:row`;
          if (!processedStopAlertKeysRef.current.has(alertKey)) {
            processedStopAlertKeysRef.current.add(alertKey);
            queuePendingStopAlert(moveKey, {
              id: alertKey,
              type: 'row',
              playerId,
              count: nextRowCount,
              rowIndex,
              text: t('room.alerts.rowVisited', { count: nextRowCount }),
            });
          }
        }
      }

      lastProcessedMoveIndexByPlayerRef.current.set(playerId, moveIndex);
    });

    if (!stopAlertsInitializedRef.current) {
      stopAlertsInitializedRef.current = true;
    }
  }, [
    displayedPositions,
    hasConductorPrivileges,
    mode,
    moves,
    queuePendingStopAlert,
    resolveStopCellFromMove,
    t,
  ]);

  const stopCellsByPlayer = useMemo(() => {
    const map = {};
    Object.entries(stopSequenceByPlayer).forEach(([playerId, sequence]) => {
      map[playerId] = new Set(sequence);
    });
    return map;
  }, [stopSequenceByPlayer]);

  const cellCenterByNumber = useMemo(() => {
    const map = new Map();
    for (let cell = 1; cell <= TOTAL_CELLS; cell += 1) {
      map.set(cell, getCellCoordinates(cell));
    }
    return map;
  }, []);

  const effectiveVisualHistoryPlayers = useMemo(() => {
    if (isPlayerVisualHistoryMode) {
      return visualHistoryEnabled && Number.isInteger(playerId) ? [playerId] : [];
    }
    return visualHistoryPlayers;
  }, [isPlayerVisualHistoryMode, playerId, visualHistoryEnabled, visualHistoryPlayers]);
  const visualHistoryPlayersSet = useMemo(
    () => new Set(effectiveVisualHistoryPlayers),
    [effectiveVisualHistoryPlayers],
  );
  const canShowVisualHistory = visualHistoryEnabled && visualHistoryPlayersSet.size > 0;
  const canShowVisitedCells = canShowVisualHistory && settings.highlightVisitedCells;

  const visualHistorySegments = useMemo(() => {
    if (!canShowVisualHistory) {
      return [];
    }
    const segments = [];
    const movesByPlayer = new Map();

    completedMoves.forEach((move) => {
      if (!move) {
        return;
      }
      const playerId = move?.playerId;
      if (playerId === null || playerId === undefined) {
        return;
      }
      if (!visualHistoryPlayersSet.has(playerId)) {
        return;
      }
      const list = movesByPlayer.get(playerId) || [];
      list.push(move);
      movesByPlayer.set(playerId, list);
    });

    movesByPlayer.forEach((list, playerId) => {
      list.sort((first, second) => {
        const firstIndex = Number.isInteger(first.moveIndex) ? first.moveIndex : 0;
        const secondIndex = Number.isInteger(second.moveIndex) ? second.moveIndex : 0;
        if (firstIndex !== secondIndex) {
          return firstIndex - secondIndex;
        }
        const firstTime = first?.createdAt ? new Date(first.createdAt).getTime() : 0;
        const secondTime = second?.createdAt ? new Date(second.createdAt).getTime() : 0;
        return firstTime - secondTime;
      });

      let segmentIndex = 0;
      list.forEach((move) => {
        const { pinnedFromCell } = ensurePinnedFromCellForMove(
          move,
          displayedPositions,
          pinnedFromCellByMoveKeyRef,
        );
        const pathCells = resolveMoveAnimationPath(move, pinnedFromCell, transitionMap);
        const moveSegments = splitPathIntoHistorySegments(pathCells);

        moveSegments.forEach((segment) => {
          segments.push({
            ...segment,
            playerId,
            index: segmentIndex,
          });
          segmentIndex += 1;
        });
      });
    });

    return segments;
  }, [
    canShowVisualHistory,
    completedMoves,
    displayedPositions,
    pinnedFromCellByMoveKeyRef,
    transitionMap,
    visualHistoryPlayersSet,
  ]);

  const visualHistoryStops = useMemo(() => {
    if (!canShowVisualHistory) {
      return [];
    }
    const points = [];
    visualHistoryPlayersSet.forEach((playerId) => {
      const sequence = stopSequenceByPlayer[playerId] || [];
      sequence.forEach((cell, index) => {
        points.push({
          cell,
          playerId,
          index,
        });
      });
    });
    return points;
  }, [canShowVisualHistory, stopSequenceByPlayer, visualHistoryPlayersSet]);

  const stopCellsByCell = useMemo(() => {
    if (!visualHistoryPlayersSet.size) {
      return new Set();
    }
    const cells = new Set();
    visualHistoryPlayersSet.forEach((playerId) => {
      const playerStops = stopCellsByPlayer[playerId];
      if (!playerStops) {
        return;
      }
      playerStops.forEach((cell) => cells.add(cell));
    });
    return cells;
  }, [stopCellsByPlayer, visualHistoryPlayersSet]);

  const selectedPlayer = useMemo(() => {
    if (!selectedPlayerId) {
      return null;
    }
    return (game?.players || []).find((player) => player.id === selectedPlayerId) || null;
  }, [game?.players, selectedPlayerId]);

  useEffect(() => {
    setManualValue('');
    setRollError('');
  }, [selectedPlayerId]);

  useEffect(() => {
    if (selectedPlayer?.diceType !== 'manual') {
      setManualValue('');
    }
  }, [selectedPlayer?.diceType]);

  const chipChoices = useMemo(
    () =>
      chips.map((chip) => ({
        ...chip,
        disabled: takenChipKeys.has(chip.chipKey) && chip.chipKey !== selectedPlayer?.chipKey,
      })),
    [selectedPlayer?.chipKey, takenChipKeys],
  );

  useEffect(() => {
    if (!personalGame || !selectedPlayer || !setupStorageKey) {
      setSetupModalOpen(false);
      return;
    }
    const alreadyConfigured = Boolean(localStorage.getItem(setupStorageKey));
    if (alreadyConfigured && selectedPlayer?.chipKey) {
      setSetupModalOpen(false);
      return;
    }
    const initialName = selectedPlayer.name || localizedUserName || '';
    const initialChipKey = selectedPlayer?.chipKey || '';

    setSetupName(initialName);
    setSetupChipKey(initialChipKey || '');
    setSetupError('');
    setSetupModalOpen(true);
  }, [localizedUserName, selectedPlayer, setupStorageKey]);

  useEffect(() => {
    if (!setupModalOpen || !setupChipKey) {
      return;
    }
    if (setupChipKey === selectedPlayer?.chipKey) {
      return;
    }
    if (takenChipKeys.has(setupChipKey)) {
      setSetupError(t('room.setup.chipTaken'));
      setSetupChipKey('');
    }
  }, [selectedPlayer?.chipKey, setupChipKey, setupModalOpen, t, takenChipKeys]);

  const myPlayerId = useMemo(
    () => (!hasConductorPrivileges ? playerId : null),
    [hasConductorPrivileges, playerId],
  );

  const myPlayerEntry = useMemo(
    () => (myPlayerId ? playerLookup.get(myPlayerId) || null : null),
    [myPlayerId, playerLookup],
  );

  const pendingSeriesMoveId = session?.turnState?.pendingSeries?.moveId || null;
  const movesForLog = useMemo(() => {
    const result = [];
    const seenSeriesIds = new Set();
    const seriesMovesById = new Map();

    completedMoves.forEach((move) => {
      if (!move?.seriesId) {
        return;
      }
      const list = seriesMovesById.get(move.seriesId) || [];
      list.push(move);
      seriesMovesById.set(move.seriesId, list);
    });

    seriesMovesById.forEach((list, seriesId) => {
      const sorted = [...list].sort((first, second) => {
        const firstIndex = Number.isFinite(first?.moveIndex) ? first.moveIndex : 0;
        const secondIndex = Number.isFinite(second?.moveIndex) ? second.moveIndex : 0;
        if (firstIndex !== secondIndex) {
          return firstIndex - secondIndex;
        }
        const firstTime = first?.createdAt ? new Date(first.createdAt).getTime() : 0;
        const secondTime = second?.createdAt ? new Date(second.createdAt).getTime() : 0;
        return firstTime - secondTime;
      });
      seriesMovesById.set(seriesId, sorted);
    });

    for (let index = completedMoves.length - 1; index >= 0; index -= 1) {
      const move = completedMoves[index];
      if (!move) {
        continue;
      }
      if (pendingSeriesMoveId && move.seriesId && move.seriesId === pendingSeriesMoveId) {
        continue;
      }
      if (move.seriesId) {
        if (seenSeriesIds.has(move.seriesId)) {
          continue;
        }
        seenSeriesIds.add(move.seriesId);
        const seriesMoves = seriesMovesById.get(move.seriesId) || [move];
        const aggregatedMove = buildAggregatedSeriesMove(seriesMoves) || move;
        result.push(aggregatedMove);
        continue;
      }
      result.push(move);
    }
    return result;
  }, [completedMoves, pendingSeriesMoveId]);
  const visibleMovesForLog = useMemo(() => {
    if (!hasConductorPrivileges && myPlayerId) {
      return movesForLog.filter((move) => move.playerId === myPlayerId);
    }
    if (!isConductorView || !selectedPlayerId) {
      return movesForLog;
    }
    return movesForLog.filter((move) => move.playerId === selectedPlayerId);
  }, [hasConductorPrivileges, isConductorView, movesForLog, myPlayerId, selectedPlayerId]);
  const lastMoveIdByPlayer = useMemo(() => {
    const latestByPlayer = new Map();
    movesForLog.forEach((move) => {
      if (!move || latestByPlayer.has(move.playerId)) {
        return;
      }
      latestByPlayer.set(move.playerId, move.id);
    });
    return latestByPlayer;
  }, [movesForLog]);
  const getUndoMetaForMove = useCallback((candidate) => {
    if (!candidate) {
      return {
        seriesMoves: [],
        startMove: null,
        barrierMove: null,
      };
    }

    const seriesMoves = candidate.seriesId
      ? completedMoves.filter(
        (move) => move.seriesId === candidate.seriesId && move.playerId === candidate.playerId,
      )
      : [candidate];

    const startMove = seriesMoves.reduce(
      (acc, move) => (!acc || move.moveIndex < acc.moveIndex ? move : acc),
      null,
    );

    const barrierMove = completedMoves.reduce((acc, move) => {
      if (move.playerId !== candidate.playerId || move.comment !== 'continueAfterFinish') {
        return acc;
      }
      if (!acc || move.moveIndex > acc.moveIndex) {
        return move;
      }
      return acc;
    }, null);

    return {
      seriesMoves,
      startMove,
      barrierMove,
    };
  }, [completedMoves]);
  const isUndoBarrierBlockedForMove = useCallback(
    (candidate) => {
      if (!candidate) {
        return false;
      }
      const undoMeta = getUndoMetaForMove(candidate);
      const undoRollbackStart = undoMeta.startMove?.fromCell ?? candidate?.fromCell ?? null;
      return Boolean(
        undoMeta.barrierMove
          && (
            candidate?.moveIndex <= undoMeta.barrierMove.moveIndex
            || (Number.isFinite(undoRollbackStart) && undoRollbackStart < REENTRY_CELL)
          ),
      );
    },
    [getUndoMetaForMove],
  );

  const latestMove = useMemo(
    () => {
      if (moves.length) {
        return moves[moves.length - 1];
      }
      if (completedMoves.length) {
        return completedMoves[completedMoves.length - 1];
      }
      return null;
    },
    [completedMoves, moves],
  );

  const startDiceAnimation = useCallback((playerId) => {
    if (diceAnimationRef.current.inFlight) {
      return;
    }
    let resolve = null;
    const promise = new Promise((resolvePromise) => {
      resolve = resolvePromise;
    });
    diceAnimationRef.current = {
      inFlight: true,
      promise,
      resolve,
      playerId: Number.isInteger(playerId) ? playerId : null,
    };
    setIsDiceAnimating(true);
  }, []);

  const endDiceAnimation = useCallback(() => {
    if (!diceAnimationRef.current.inFlight) {
      return;
    }
    const { resolve } = diceAnimationRef.current;
    diceAnimationRef.current = {
      inFlight: false,
      promise: null,
      resolve: null,
      playerId: null,
    };
    if (resolve) {
      resolve();
    }
    setIsDiceAnimating(false);
  }, []);

  const waitForDiceAnimation = useCallback(async (move) => {
    const { inFlight, promise, playerId } = diceAnimationRef.current;
    if (!inFlight || !promise) {
      return;
    }
    if (Number.isInteger(playerId) && move?.playerId !== playerId) {
      return;
    }
    await promise;
  }, []);

  useEffect(() => {
    const playerIds = playersList.map((player) => player.id);
    setVisualHistoryPlayers((prev) => {
      if (!playerIds.length) {
        return [];
      }
      const prevSet = new Set(prev);
      let changed = false;
      playerIds.forEach((id) => {
        if (!prevSet.has(id)) {
          prevSet.add(id);
          changed = true;
        }
      });
      const filtered = Array.from(prevSet).filter((id) => playerIds.includes(id));
      if (changed || filtered.length !== prev.length) {
        return filtered;
      }
      return prev;
    });
  }, [playersList]);

  useEffect(() => {
    if (!movesLoaded || movesInitializedRef.current) {
      return;
    }
    if (animationLockRef.current !== ANIMATION_LOCK_NONE) {
      return;
    }

    if (movesLoadSourceRef.current === 'live') {
      movesInitializedRef.current = true;
      return;
    }

    if (userInitiatedMoveRef.current) {
      const latestMoveByPlayerId = new Map();
      const currentPositions = new Map();
      const nextPositions = {};

      playersList.forEach((player) => {
        const playerId = player?.id;
        const currentCell = clampCellNumber(player?.currentCell);
        if (playerId === null || playerId === undefined) {
          return;
        }
        currentPositions.set(playerId, currentCell);
        nextPositions[playerId] = currentCell;
      });

      moves.forEach((move) => {
        const playerId = move?.playerId;
        const moveIndex = move?.moveIndex;
        if (playerId === null || playerId === undefined || !Number.isFinite(moveIndex)) {
          return;
        }
        const current = latestMoveByPlayerId.get(playerId);
        if (!current || moveIndex > current.moveIndex) {
          latestMoveByPlayerId.set(playerId, move);
        }
      });

      latestMoveByPlayerId.forEach((move, playerId) => {
        const moveKey = getMoveKey(move);
        const fallbackCell = currentPositions.get(playerId) ?? 1;
        let startCell = null;

        if (Number.isFinite(move?.fromCell)) {
          startCell = clampCellNumber(move.fromCell);
        } else {
          const stepValue = resolveMoveStepValue(move);
          if (Number.isFinite(move?.toCell) && Number.isFinite(stepValue)) {
            startCell = clampCellNumber(move.toCell - stepValue);
          } else {
            startCell = fallbackCell;
          }
        }

        if (moveKey && Number.isFinite(startCell)) {
          pinnedFromCellByMoveKeyRef.current.set(moveKey, startCell);
        }

        if (Number.isFinite(move?.moveIndex)) {
          lastAnimatedMoveIndexByPlayerRef.current.set(playerId, move.moveIndex - 1);
        }

        if (Number.isFinite(startCell)) {
          nextPositions[playerId] = startCell;
        }
      });

      if (!positionsInitializedRef.current && Object.keys(nextPositions).length) {
        updateDisplayedPositions((prev) => ({ ...prev, ...nextPositions }));
      }

      positionsInitializedRef.current = true;
      movesInitializedRef.current = true;
      return;
    }

    const nextPositions = {};

    moves.forEach((move) => {
      const { moveKey, pinnedFromCell } = ensurePinnedFromCellForMove(
        move,
        displayedPositions,
        pinnedFromCellByMoveKeyRef,
      );
      if (!moveKey) {
        return;
      }
      const path = resolveMoveAnimationPath(move, pinnedFromCell, transitionMap);
      animatedPathByMoveKeyRef.current.set(moveKey, path);

      if (positionsInitializedRef.current) {
        return;
      }

      const lastCell = path[path.length - 1]
        ?? clampCellNumber(move.toCell)
        ?? clampCellNumber(move.fromCell);

      if (Number.isFinite(lastCell) && move?.playerId !== undefined && move?.playerId !== null) {
        nextPositions[move.playerId] = lastCell;
      }
    });

    if (!positionsInitializedRef.current && Object.keys(nextPositions).length) {
      updateDisplayedPositions((prev) => ({ ...prev, ...nextPositions }));
      positionsInitializedRef.current = true;
    }

    movesInitializedRef.current = true;
  }, [displayedPositions, moves, movesLoaded, playersList, transitionMap]);

  const playMoveAnimation = useCallback(
    async ({
      move,
      path: providedPath,
      resolvedPath = [],
      moveKey = null,
    }) => {
      const path = providedPath?.length ? providedPath : resolvedPath;
      if (!path.length) {
        return;
      }

      const duration = getStepDurationMs(settings.movementSpeed);
      const sessionId = animationSessionRef.current;

      trailPathCellsRef.current = path;

      try {
        if (animationSessionRef.current !== sessionId) {
          return;
        }

        updateDisplayedPositions((prev) => {
          if (animationSessionRef.current !== sessionId) {
            return prev;
          }
          const current = prev[move.playerId];
          if (current === path[0]) {
            return prev;
          }
          return { ...prev, [move.playerId]: path[0] };
        }, 'move-animation-start');

        for (let index = 1; index < path.length; index += 1) {
          await wait(duration);
          if (animationSessionRef.current !== sessionId) {
            return;
          }
          updateDisplayedPositions((prev) => {
            if (animationSessionRef.current !== sessionId) {
              return prev;
            }
            return { ...prev, [move.playerId]: path[index] };
          }, 'move-animation-step');
        }

        if (animationSessionRef.current === sessionId) {
          if (moveKey && resolvedPath.length) {
            animatedPathByMoveKeyRef.current.set(moveKey, resolvedPath);
          }
          const moveIndex = move?.moveIndex;
          const playerId = move?.playerId;
          if (
            !move?.isDraft
            && Number.isFinite(moveIndex)
            && playerId !== null
            && playerId !== undefined
          ) {
            const previous = lastAnimatedMoveIndexByPlayerRef.current.get(playerId);
            if (!Number.isFinite(previous) || moveIndex > previous) {
              lastAnimatedMoveIndexByPlayerRef.current.set(playerId, moveIndex);
            }
          }
          if (!move?.isDraft && move?.moveId) {
            lastAnimatedMoveIdRef.current = move.moveId;
          }
        }
      } finally {
        trailPathCellsRef.current = [];
      }
    },
    [settings.movementSpeed],
  );

  const playDropAnimation = useCallback(
    (playerId, targetCell, { markIntroDrop = false } = {}) => new Promise((resolve) => {
      if (!Number.isInteger(playerId)) {
        resolve();
        return;
      }
      const player = playerLookup.get(playerId);
      if (!player || !Number.isFinite(targetCell)) {
        resolve();
        return;
      }
      if (typeof window === 'undefined' || typeof requestAnimationFrame === 'undefined') {
        updateDisplayedPositions((prev) => ({ ...prev, [playerId]: targetCell }), 'drop-animation-no-raf');
        if (markIntroDrop) {
          markIntroDropPlayed(playerId);
        }
        resolve();
        return;
      }

      const sessionId = animationSessionRef.current;
      introDropAnimationIdRef.current += 1;
      const animationId = introDropAnimationIdRef.current;
      const start = getCellCenter(FINISH_CELL, cellCenterByNumber);
      const end = getCellCenter(targetCell, cellCenterByNumber);
      const startTime = performance.now();
      const deltaX = end.x - start.x;
      const deltaY = end.y - start.y;

      introDropAnimatingPlayersRef.current.add(playerId);
      updateDisplayedPositions((prev) => ({ ...prev, [playerId]: FINISH_CELL }), 'drop-animation-start');
      setIntroDropPositions((prev) => ({ ...prev, [playerId]: start }));

      const updateTokenPosition = (x, y) => {
        const element = introDropTokenRefByPlayerId.current.get(playerId);
        if (!element) {
          return;
        }
        element.style.left = `${(x / BOARD_COLS) * 100}%`;
        element.style.top = `${(y / BOARD_ROWS) * 100}%`;
      };

      const cleanup = (completed) => {
        introDropAnimatingPlayersRef.current.delete(playerId);
        setIntroDropPositions((prev) => {
          if (!prev[playerId]) {
            return prev;
          }
          const next = { ...prev };
          delete next[playerId];
          return next;
        });
        if (completed) {
          updateDisplayedPositions((prev) => ({ ...prev, [playerId]: targetCell }), 'drop-animation-complete');
          if (markIntroDrop) {
            markIntroDropPlayed(playerId);
          }
        }
        resolve();
      };

      const step = (timestamp) => {
        if (animationSessionRef.current !== sessionId || introDropAnimationIdRef.current !== animationId) {
          cleanup(false);
          return;
        }
        const elapsed = timestamp - startTime;
        const progress = Math.min(1, elapsed / INTRO_DROP_DURATION_MS);
        const x = start.x + deltaX * progress;
        const y = start.y + deltaY * progress;
        updateTokenPosition(x, y);
        if (progress < 1) {
          requestAnimationFrame(step);
        } else {
          cleanup(true);
        }
      };

      requestAnimationFrame(step);
    }),
    [cellCenterByNumber, markIntroDropPlayed, playerLookup],
  );

  const playSegmentedMoveAnimation = useCallback(
    async ({ move, moveKey }) => {
      const playerId = move?.playerId;
      if (playerId === null || playerId === undefined) {
        return;
      }
      const segments = getMoveSegments(move);
      if (!segments.length) {
        return;
      }
      const dropTarget = resolveSegmentedDropTarget(move);
      const stepsPath = resolveSegmentedStepsPath(move);

      if (Number.isFinite(dropTarget)) {
        await playDropAnimation(playerId, dropTarget, { markIntroDrop: true });
      }

      if (stepsPath.length) {
        await playMoveAnimation({
          move,
          moveKey,
          path: stepsPath,
          resolvedPath: stepsPath,
        });
      }
    },
    [playDropAnimation, playMoveAnimation],
  );

  const shouldPlayIntroDropForMove = useCallback(
    (move) => {
      if (getMoveSegments(move).length) {
        return false;
      }
      const playerId = move?.playerId;
      const moveIndex = move?.moveIndex;
      if (!Number.isInteger(playerId) || !Number.isFinite(moveIndex) || move?.isDraft) {
        return false;
      }
      if (introDropPlayedRef.current.get(playerId)) {
        return false;
      }
      const player = playerLookup.get(playerId);
      if (!player || Number.isInteger(player?.startCellNumber)) {
        return false;
      }
      const firstMoveIndex = firstMoveIndexByPlayer.get(playerId);
      if (!Number.isFinite(firstMoveIndex) || firstMoveIndex !== moveIndex) {
        return false;
      }
      return true;
    },
    [firstMoveIndexByPlayer, playerLookup],
  );

  const playIntroDropAnimation = useCallback(
    (move) => {
      const playerId = move?.playerId;
      if (!Number.isInteger(playerId)) {
        return Promise.resolve();
      }
      const player = playerLookup.get(playerId);
      const targetCell = resolveIntroDropTargetCellForMove(player, move);
      return playDropAnimation(playerId, targetCell, { markIntroDrop: true });
    },
    [playDropAnimation, playerLookup],
  );

  const playReentryDropAnimation = useCallback(
    (move) => {
      const playerId = move?.playerId;
      const targetCell = Number.isFinite(move?.toCell) ? clampCellNumber(move.toCell) : null;
      return playDropAnimation(playerId, targetCell, { markIntroDrop: true });
    },
    [playDropAnimation],
  );

  const markMoveProcessed = useCallback((moveKey) => {
    if (!moveKey) {
      return;
    }
    processedMoveKeysRef.current.add(moveKey);
  }, []);

  const finalizeSkippedStep = useCallback(({ move, moveKey, resolvedPath = [] }) => {
    if (!move) {
      return;
    }

    const playerId = move?.playerId;
    const moveIndex = move?.moveIndex;
    const fallbackCell = clampCellNumber(move?.toCell) ?? clampCellNumber(move?.fromCell);
    const lastCell = resolvedPath.length
      ? resolvedPath[resolvedPath.length - 1]
      : fallbackCell;

    if (moveKey) {
      const storedPath = resolvedPath.length ? resolvedPath : lastCell ? [lastCell] : [];
      animatedPathByMoveKeyRef.current.set(moveKey, storedPath);
      plannedPathByMoveKeyRef.current.delete(moveKey);
    }

    if (Number.isFinite(lastCell) && playerId !== null && playerId !== undefined) {
      updateDisplayedPositions((prev) => {
        if (prev[playerId] === lastCell) {
          return prev;
        }
        return { ...prev, [playerId]: lastCell };
      });
    }

    if (
      !move?.isDraft
      && Number.isFinite(moveIndex)
      && playerId !== null
      && playerId !== undefined
    ) {
      const previous = lastAnimatedMoveIndexByPlayerRef.current.get(playerId);
      if (!Number.isFinite(previous) || moveIndex > previous) {
        lastAnimatedMoveIndexByPlayerRef.current.set(playerId, moveIndex);
      }
    }
    if (!move?.isDraft && move?.moveId) {
      lastAnimatedMoveIdRef.current = move.moveId;
    }
    if (moveKey) {
      releasePendingStopAlerts(moveKey);
    }
    markMoveProcessed(moveKey);
  }, [markMoveProcessed, releasePendingStopAlerts]);

  const clearAnimationWatchdog = useCallback(() => {
    if (animationWatchdogRef.current) {
      clearTimeout(animationWatchdogRef.current);
      animationWatchdogRef.current = null;
    }
  }, []);

  const finalizeMoveAnimationLifecycle = useCallback((lifecycleId) => {
    if (!Number.isInteger(lifecycleId)) {
      return false;
    }

    if (moveAnimationLifecycleRef.current.id !== lifecycleId || moveAnimationLifecycleRef.current.finalized) {
      return false;
    }

    moveAnimationLifecycleRef.current.finalized = true;
    isMoveAnimationRunningRef.current = false;
    setIsMoveAnimationRunning(false);
    animationLockRef.current = ANIMATION_LOCK_NONE;

    if (queuedTurnStateRef.current) {
      appliedTurnStateRef.current = queuedTurnStateRef.current;
      queuedTurnStateRef.current = null;
      setTurnStateSyncTick((prev) => prev + 1);
    }

    return true;
  }, []);

  const startMoveAnimationLifecycle = useCallback(() => {
    const nextId = moveAnimationLifecycleRef.current.id + 1;
    moveAnimationLifecycleRef.current = { id: nextId, finalized: false };
    isMoveAnimationRunningRef.current = true;
    setIsMoveAnimationRunning(true);
    return nextId;
  }, []);

  const forceResetAnimations = useCallback(async () => {
    animationSessionRef.current += 1;
    clearAnimationWatchdog();
    isAnimatingRef.current = false;
    isAnimatingMoveRef.current = false;
    finalizeMoveAnimationLifecycle(moveAnimationLifecycleRef.current.id);
    setHighlightPlayerId(null);
    activeMoveAnimationIdRef.current = null;
    animationQueueRef.current = [];
    queuedMoveKeysRef.current = new Set();
    plannedPathByMoveKeyRef.current = new Map();
    pendingAnimationPlayersRef.current = new Map();
    setIntroDropPositions({});

    if (mode === 'conductor') {
      await syncGameState();
    } else {
      await fetchPlayerState({ skipLoading: true, ignoreErrors: true });
    }
  }, [clearAnimationWatchdog, fetchPlayerState, finalizeMoveAnimationLifecycle, mode, syncGameState]);

  useEffect(() => () => {
    clearAnimationWatchdog();
  }, [clearAnimationWatchdog]);

  useEffect(() => {
    if (isMoveAnimating || isAnimatingRef.current || isAnimatingMoveRef.current) {
      return;
    }
    if (!isTurnAnimationInProgress) {
      return;
    }
    setIsTurnAnimationInProgress(false);
  }, [isMoveAnimating, isTurnAnimationInProgress]);

  useEffect(() => {
    if (!hasConductorPrivileges || !token || !id) {
      return;
    }
    if (isTurnAnimationInProgress || isMoveAnimating || isAnimatingRef.current || isAnimatingMoveRef.current) {
      return;
    }
    const turnId = pendingAutoHandover?.turnId;
    const playerId = pendingAutoHandover?.fromPlayerId;
    if (!turnId || !playerId) {
      return;
    }
    if (turnAnimationAckInFlightRef.current === turnId) {
      return;
    }

    turnAnimationAckInFlightRef.current = turnId;
    fetch(apiUrl(`/api/games/${id}/turn-animation-complete`), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ turnId, playerId }),
    })
      .then(async (response) => {
        if (!response.ok) {
          const errorData = await parseResponseError(response, t('errors.requestFailed'));
          throw new Error(errorData.message);
        }
        return response.json();
      })
      .then((payload) => {
        if (payload?.session) {
          applyMoveResponse(payload);
        }
        setPendingAutoHandover(null);
      })
      .catch((error) => {
        console.error('[TURN_ANIMATION_COMPLETE_ERROR]', error);
      })
      .finally(() => {
        if (turnAnimationAckInFlightRef.current === turnId) {
          turnAnimationAckInFlightRef.current = '';
        }
      });
  }, [
    applyMoveResponse,
    hasConductorPrivileges,
    id,
    isMoveAnimating,
    isTurnAnimationInProgress,
    pendingAutoHandover,
    t,
    token,
  ]);

  const processAnimationQueue = useCallback(() => {
    if (isAnimatingRef.current) {
      return;
    }

    const nextMove = animationQueueRef.current.shift();
    if (!nextMove) {
      return;
    }

    const moveKey = nextMove?.moveKey ?? getMoveKey(nextMove?.move);
    activeMoveAnimationIdRef.current = moveKey;
    isAnimatingRef.current = true;
    isAnimatingMoveRef.current = true;
    const lifecycleId = startMoveAnimationLifecycle();
    if (!nextMove?.move?.isDraft && nextMove?.move?.moveId) {
      lastProcessedMoveIdRef.current = nextMove.move.moveId;
    }
    if (nextMove?.move?.playerId !== undefined && nextMove?.move?.playerId !== null) {
      setHighlightPlayerId(nextMove.move.playerId);
    }
    const runAnimation = async () => {
      await waitForDiceAnimation(nextMove?.move);
      if (getMoveSegments(nextMove?.move).length) {
        animationLockRef.current = ANIMATION_LOCK_ENTRY;
        await playSegmentedMoveAnimation({ move: nextMove?.move, moveKey });
        return;
      }
      if (shouldPlayIntroDropForMove(nextMove?.move)) {
        animationLockRef.current = ANIMATION_LOCK_ENTRY;
        await playIntroDropAnimation(nextMove?.move);
      }
      if (nextMove?.move?.kind === 'burn_reentry_drop') {
        animationLockRef.current = ANIMATION_LOCK_ENTRY;
        await playReentryDropAnimation(nextMove?.move);
        const moveIndex = nextMove?.move?.moveIndex;
        const playerId = nextMove?.move?.playerId;
        if (
          !nextMove?.move?.isDraft
          && Number.isFinite(moveIndex)
          && playerId !== null
          && playerId !== undefined
        ) {
          const previous = lastAnimatedMoveIndexByPlayerRef.current.get(playerId);
          if (!Number.isFinite(previous) || moveIndex > previous) {
            lastAnimatedMoveIndexByPlayerRef.current.set(playerId, moveIndex);
          }
        }
        if (!nextMove?.move?.isDraft && nextMove?.move?.moveId) {
          lastAnimatedMoveIdRef.current = nextMove.move.moveId;
        }
        return;
      }
      animationLockRef.current = ANIMATION_LOCK_MOVE;
      await playMoveAnimation(nextMove);
    };

    clearAnimationWatchdog();
    animationWatchdogRef.current = setTimeout(() => {
      if (activeMoveAnimationIdRef.current === moveKey) {
        forceResetAnimations();
      }
    }, ANIMATION_WATCHDOG_MS);
    runAnimation()
      .catch((error) => {
        console.error(t('errors.playMoveAnimation'), error);
      })
      .finally(() => {
        clearAnimationWatchdog();
        markMoveProcessed(moveKey);
        if (moveKey) {
          queuedMoveKeysRef.current.delete(moveKey);
          plannedPathByMoveKeyRef.current.delete(moveKey);
          releasePendingStopAlerts(moveKey);
        }
        unmarkPendingAnimationPlayer(nextMove?.move?.playerId);
        if (activeMoveAnimationIdRef.current === moveKey || !activeMoveAnimationIdRef.current) {
          isAnimatingRef.current = false;
          isAnimatingMoveRef.current = false;
          activeMoveAnimationIdRef.current = null;
        }
        finalizeMoveAnimationLifecycle(lifecycleId);
        processAnimationQueue();
      });
  }, [
    clearAnimationWatchdog,
    forceResetAnimations,
    markMoveProcessed,
    playIntroDropAnimation,
    playMoveAnimation,
    playSegmentedMoveAnimation,
    playReentryDropAnimation,
    releasePendingStopAlerts,
    finalizeMoveAnimationLifecycle,
    shouldPlayIntroDropForMove,
    startMoveAnimationLifecycle,
    t,
    unmarkPendingAnimationPlayer,
    waitForDiceAnimation,
  ]);

  useEffect(() => {
    if (!movesLoaded) {
      return;
    }

    if (!movesInitializedRef.current && movesLoadSourceRef.current !== 'live') {
      return;
    }

    const pendingAnimations = [];

    moves.forEach((move) => {
      if (!move) {
        return;
      }
      if (move?.moveId && lastAnimatedMoveIdRef.current === move.moveId) {
        return;
      }
      if (move?.moveId && lastProcessedMoveIdRef.current === move.moveId) {
        return;
      }

      const { moveKey, pinnedFromCell } = ensurePinnedFromCellForMove(
        move,
        displayedPositions,
        pinnedFromCellByMoveKeyRef,
      );
      if (!moveKey) {
        return;
      }
      if (processedMoveKeysRef.current.has(moveKey)) {
        return;
      }
      if (queuedMoveKeysRef.current.has(moveKey) || activeMoveAnimationIdRef.current === moveKey) {
        return;
      }
      const previousPath =
        plannedPathByMoveKeyRef.current.get(moveKey)
        ?? animatedPathByMoveKeyRef.current.get(moveKey)
        ?? [];
      const resolvedPath = resolveMoveAnimationPath(move, pinnedFromCell, transitionMap);

      if (resolvedPath.length <= 1 && !previousPath.length) {
        finalizeSkippedStep({ move, moveKey, resolvedPath });
        return;
      }

      if (resolvedPath.length <= previousPath.length) {
        return;
      }

      const commonPrefixLength = getCommonPrefixLength(previousPath, resolvedPath);
      if (commonPrefixLength < previousPath.length) {
        return;
      }

      const startIndex = Math.max(0, commonPrefixLength - 1);
      const tailPath = resolvedPath.slice(startIndex);
      if (tailPath.length >= 2) {
        plannedPathByMoveKeyRef.current.set(moveKey, resolvedPath);
        pendingAnimations.push({
          move,
          moveKey,
          path: tailPath,
          resolvedPath,
        });
      }
    });

    if (!pendingAnimations.length) {
      return;
    }

    const orderedMoves = [...pendingAnimations].sort((a, b) => a.move.moveIndex - b.move.moveIndex);
    orderedMoves.forEach((entry) => {
      markPendingAnimationPlayer(entry.move?.playerId);
      if (entry.moveKey) {
        queuedMoveKeysRef.current.add(entry.moveKey);
      }
    });
    animationQueueRef.current.push(...orderedMoves);
    processAnimationQueue();
  }, [
    displayedPositions,
    pinnedFromCellByMoveKeyRef,
    finalizeSkippedStep,
    markPendingAnimationPlayer,
    moves,
    movesLoaded,
    processAnimationQueue,
    transitionMap,
  ]);

  const sessionTurnState = useMemo(() => session?.turnState || null, [session?.turnState]);

  const effectiveTurnStateSync = useMemo(() => {
    const fallbackState = {
      turnState: sessionTurnState,
      currentPlayerId: session?.currentPlayerId ?? null,
    };

    if (isMoveAnimationRunning) {
      return appliedTurnStateRef.current?.turnState || appliedTurnStateRef.current?.currentPlayerId !== undefined
        ? appliedTurnStateRef.current
        : fallbackState;
    }

    return appliedTurnStateRef.current || fallbackState;
  }, [isMoveAnimationRunning, session?.currentPlayerId, sessionTurnState, turnStateSyncTick]);

  const effectiveSessionTurnState = effectiveTurnStateSync?.turnState || null;
  const effectiveSessionCurrentPlayerId = effectiveTurnStateSync?.currentPlayerId ?? null;

const currentTurnPlayerId = useMemo(() => {
  const soleSessionPlayerId = playersList?.length === 1 ? playersList[0]?.id ?? null : null;

  return (
    effectiveSessionTurnState?.currentPlayerId ??
    effectiveSessionCurrentPlayerId ??
    (isSoloOnline ? (soleOnlinePlayerId ?? null) : null) ??
    soleSessionPlayerId ??
    null
  );
}, [
  effectiveSessionCurrentPlayerId,
  effectiveSessionTurnState?.currentPlayerId,
  isSoloOnline,
  soleOnlinePlayerId,
  playersList,
]);

  useEffect(() => {
    if (didAutoPromptTokenPickerRef.current) {
      return;
    }

    if (!isConductorRole || !personalGame || !session || setupModalOpen) {
      return;
    }

    const targetPlayer = currentTurnPlayerId
      ? playersList.find((player) => player.id === currentTurnPlayerId) || null
      : playersList[0] || null;

    if (!targetPlayer || targetPlayer?.chipKey) {
      return;
    }

    didAutoPromptTokenPickerRef.current = true;
    if (selectedPlayerId !== targetPlayer.id) {
      setSelectedPlayerId(targetPlayer.id);
    }
  }, [
    currentTurnPlayerId,
    isConductorRole,
    personalGame,
    playersList,
    selectedPlayerId,
    session,
    setupModalOpen,
  ]);

  useEffect(() => {
    if (!isConductorView) {
      previousConductorTurnPlayerIdRef.current = currentTurnPlayerId || null;
      if (manualViewLock) {
        setManualViewLock(false);
      }
      return;
    }

    if (!playersList.length) {
      previousConductorTurnPlayerIdRef.current = currentTurnPlayerId || null;
      if (selectedPlayerId !== null) {
        setSelectedPlayerId(null);
      }
      if (manualViewLock) {
        setManualViewLock(false);
      }
      return;
    }

    if (isMoveAnimationRunning) {
      return;
    }

    const previousTurnPlayerId = previousConductorTurnPlayerIdRef.current;
    const turnChanged = previousTurnPlayerId !== currentTurnPlayerId;

    if (turnChanged && suppressConductorAutoSelectRef.current) {
      suppressConductorAutoSelectRef.current = false;
      previousConductorTurnPlayerIdRef.current = currentTurnPlayerId || null;
      return;
    }

    const selectedPlayerExists = selectedPlayerId
      ? playersList.some((player) => player.id === selectedPlayerId)
      : false;

    if (selectedPlayerId && !selectedPlayerExists) {
      if (manualViewLock) {
        setManualViewLock(false);
      }
      if (currentTurnPlayerId && playersList.some((player) => player.id === currentTurnPlayerId)) {
        setSelectedPlayerId(currentTurnPlayerId);
        previousConductorTurnPlayerIdRef.current = currentTurnPlayerId;
        return;
      }
      const fallbackPlayerId = playersList[0]?.id ?? null;
      if (fallbackPlayerId !== selectedPlayerId) {
        setSelectedPlayerId(fallbackPlayerId);
      }
      previousConductorTurnPlayerIdRef.current = currentTurnPlayerId || null;
      return;
    }

    if (turnChanged) {
      if (manualViewLock) {
        setManualViewLock(false);
      }
      if (currentTurnPlayerId && selectedPlayerId !== currentTurnPlayerId) {
        setSelectedPlayerId(currentTurnPlayerId);
      }
      previousConductorTurnPlayerIdRef.current = currentTurnPlayerId || null;
      return;
    }

    if (!selectedPlayerId) {
      if (currentTurnPlayerId && playersList.some((player) => player.id === currentTurnPlayerId)) {
        setSelectedPlayerId(currentTurnPlayerId);
      } else if (playersList[0]?.id) {
        setSelectedPlayerId(playersList[0].id);
      }
      previousConductorTurnPlayerIdRef.current = currentTurnPlayerId || null;
      return;
    }

    if (!manualViewLock && currentTurnPlayerId && selectedPlayerId !== currentTurnPlayerId) {
      setSelectedPlayerId(currentTurnPlayerId);
    }

    previousConductorTurnPlayerIdRef.current = currentTurnPlayerId || null;
  }, [
    currentTurnPlayerId,
    isConductorView,
    manualViewLock,
    selectedPlayerId,
    playersList,
    isMoveAnimationRunning,
  ]);

  const currentTurnPlayerEntry = useMemo(() => {
    if (!currentTurnPlayerId) {
      return null;
    }
    return playersList.find((player) => player.id === currentTurnPlayerId) || null;
  }, [currentTurnPlayerId, playersList]);

  useEffect(() => {
    if (!personalGame || selectedPlayerId || !playersList.length) {
      return;
    }

    const fallbackPlayerId = currentTurnPlayerId && playersList.some((player) => player.id === currentTurnPlayerId)
      ? currentTurnPlayerId
      : playersList[0]?.id ?? null;

    if (fallbackPlayerId) {
      setSelectedPlayerId(fallbackPlayerId);
    }
  }, [currentTurnPlayerId, personalGame, playersList, selectedPlayerId]);

  const isAutoSelectMode = sessionType === 'personal' || sessionType === 'individual';
  const getAutoSelectedActor = useCallback(() => {
    if (!playersList.length) {
      return null;
    }

    if (sessionType === 'personal') {
      if (currentTurnPlayerId) {
        const currentTurnPlayer = playersList.find((player) => player.id === currentTurnPlayerId);
        if (currentTurnPlayer) {
          return currentTurnPlayer;
        }
      }
      if (Number.isInteger(myPlayerId)) {
        const myPlayer = playersList.find((player) => player.id === myPlayerId);
        if (myPlayer) {
          return myPlayer;
        }
      }
      return playersList[0] || null;
    }

    if (sessionType === 'individual') {
      if (playersList.length === 1) {
        return playersList[0] || null;
      }
      if (Number.isInteger(myPlayerId)) {
        const myPlayer = playersList.find((player) => player.id === myPlayerId);
        if (myPlayer) {
          return myPlayer;
        }
      }
      if (currentTurnPlayerId) {
        return playersList.find((player) => player.id === currentTurnPlayerId) || null;
      }
      return playersList[0] || null;
    }

    return null;
  }, [currentTurnPlayerId, myPlayerId, playersList, sessionType]);
  const autoSelectedActor = useMemo(
    () => (isAutoSelectMode ? getAutoSelectedActor() : null),
    [getAutoSelectedActor, isAutoSelectMode],
  );
  const activeActor = isAutoSelectMode ? autoSelectedActor : selectedPlayer;
  const activeActorId = activeActor?.id ?? null;

  const allParticipantsConnected = useMemo(
    () => {
      const plannedPlayerIds = playersList
        .map((player) => player?.id)
        .filter((playerId) => Number.isInteger(playerId));
      const plannedCount = plannedPlayerIds.length;

      if (!plannedCount) {
        return false;
      }

      const plannedIdsSet = new Set(plannedPlayerIds);
      const connectedPlannedIds = new Set();

      sessionParticipants.forEach((participant) => {
        const playerId = participant?.playerId;
        if (!participant?.isOnline) {
          return;
        }
        if (!Number.isInteger(playerId)) {
          return;
        }
        if (!plannedIdsSet.has(playerId)) {
          return;
        }
        connectedPlannedIds.add(playerId);
      });

      return connectedPlannedIds.size === plannedCount;
    },
    [playersList, sessionParticipants],
  );
  const hasConnectedPlayerWithSelectedChip = useMemo(
    () => {
      if (!playersList.length) {
        return false;
      }

      const onlineParticipantPlayerIds = new Set(
        sessionParticipants
          .filter((participant) => participant?.isOnline && Number.isInteger(participant?.playerId))
          .map((participant) => participant.playerId),
      );

      return playersList.some((player) => {
        if (!player?.chipKey || !Number.isInteger(player?.id)) {
          return false;
        }

        if (hasPresenceSnapshot) {
          return onlinePlayersSet?.has(player.id);
        }

        return onlineParticipantPlayerIds.has(player.id);
      });
    },
    [hasPresenceSnapshot, onlinePlayersSet, playersList, sessionParticipants],
  );

  const currentTurnRequestText = currentTurnPlayerEntry?.playerRequest?.trim() || '';
  const playerRequestText = activeActor?.playerRequest?.trim() || '';
  const personalRequestText = game?.requestText?.trim() || '';
  const canEditRequest = forcePlayerMode || (personalGame && hasConductorPrivileges);
  const viewedRequestText = isConductorView ? playerRequestText : currentTurnRequestText;
  const requestFallbackText = t('room.request.empty');
  const sanitizeRequestText = useCallback(
    (rawText, fallback = '') => {
      const normalizedText = typeof rawText === 'string' ? rawText.trim() : '';
      if (!normalizedText) {
        return fallback;
      }

      if (isRawSystemText(normalizedText)) {
        if (RAW_I18N_KEY_PATTERN.test(normalizedText)) {
          const translated = t(normalizedText);
          if (translated !== normalizedText && !isRawSystemText(translated)) {
            return translated;
          }
        }
        return fallback;
      }

      return normalizedText;
    },
    [t],
  );
  const requestDisplayText = forcePlayerMode
    ? sanitizeRequestText(playerRequestText, t('room.request.add'))
    : personalGame
      ? sanitizeRequestText(personalRequestText, requestFallbackText)
      : sanitizeRequestText(viewedRequestText, requestFallbackText);
  const requestPlayerName = isConductorView && activeActor
    ? getPlayerDisplayName(activeActor)
    : currentTurnPlayerEntry
      ? getPlayerDisplayName(currentTurnPlayerEntry)
      : t('log.playerFallback');
  const showRequestMore = !forcePlayerMode
    && !personalGame
    && Boolean(viewedRequestText)
    && requestTextOverflowing;

  useEffect(() => {
    if (isMoveAnimating) {
      return;
    }
    setHighlightPlayerId(currentTurnPlayerId);
  }, [currentTurnPlayerId, isMoveAnimating]);


  const isCurrentPlayersTurn = useMemo(() => {
    if (!activeActorId) {
      return false;
    }
    if (currentTurnPlayerId && currentTurnPlayerId === activeActorId) {
      return true;
    }
    if (isSoloOnline && isPlayerOnline(activeActorId)) {
      return true;
    }
    return false;
  }, [activeActorId, currentTurnPlayerId, isPlayerOnline, isSoloOnline]);

  const isOtherPlayersTurn = useMemo(
    () => Boolean(currentTurnPlayerId && activeActorId && !isCurrentPlayersTurn),
    [activeActorId, currentTurnPlayerId, isCurrentPlayersTurn],
  );

  const canStartSession = useMemo(
    () =>
      !isRestrictedPlayer
      && session
      && session.status !== 'running'
      && session.status !== 'finished'
      && !startingSession,
    [isRestrictedPlayer, session, session?.status, startingSession],
  );
  const isWaitingPlayersToStartSession = !personalGame && !hasConnectedPlayerWithSelectedChip;

  const canFinishSession = useMemo(
    () => hasConductorPrivileges && session?.status === 'running',
    [hasConductorPrivileges, session?.status],
  );
  const canShowSupportInGame = useMemo(
    () => hasConductorPrivileges && Boolean(token),
    [hasConductorPrivileges, token],
  );

  const fallbackTurnPlayer = useMemo(
    () => (!manualTurnRequired && activeActor ? activeActor : null),
    [activeActor, manualTurnRequired],
  );

  const turnState = useMemo(() => {
    if (effectiveSessionTurnState && (effectiveSessionTurnState.currentPlayerId || manualTurnRequired || effectiveSessionTurnState.isSeriesReadyToMove)) {
      if (isSoloOnline && soleOnlinePlayerId) {
        return { ...effectiveSessionTurnState, currentPlayerId: soleOnlinePlayerId };
      }
      // When currentPlayerId is null (session start or series ready), resolve from fallback
      if (!effectiveSessionTurnState.currentPlayerId) {
const soleSessionPlayerId = playersList?.length === 1 ? playersList[0]?.id ?? null : null;
const soloResolvedId = (isSoloOnline ? (soleOnlinePlayerId ?? null) : null) ?? soleSessionPlayerId;

const resolvedPlayerId =
  soloResolvedId ??
  currentTurnPlayerId ??
  fallbackTurnPlayer?.id ??
  null;

return { ...effectiveSessionTurnState, currentPlayerId: resolvedPlayerId };
      }
      return effectiveSessionTurnState;
    }

    const resolvedTurnPlayer = currentTurnPlayerEntry || fallbackTurnPlayer;
    const resolvedTurnPlayerId = currentTurnPlayerId ?? fallbackTurnPlayer?.id ?? null;
    const pendingDiceSeries = resolvedTurnPlayer?.pendingDiceSeries || [];
    const pendingAppliedSeries = resolvedTurnPlayer?.pendingAppliedSeries || [];
    const awaitingAction = Boolean(
      pendingAppliedSeries.length > 0
        || (pendingDiceSeries.length > 0 && getLastDiceSeriesValue(pendingDiceSeries) === 6)
        || resolvedTurnPlayer?.pendingMoveId,
    );

    return {
      currentPlayerId: resolvedTurnPlayerId,
      pendingSixSum: resolvedTurnPlayer?.pendingSixSum || 0,
      pendingDiceSeries,
      pendingAppliedSeries,
      hasPendingSeries: Boolean(pendingDiceSeries.length || pendingAppliedSeries.length),
      awaitingAction,
      isSeriesReadyToMove: pendingAppliedSeries.length > 0,
      lastDiceRoll: resolveLastDiceRoll(session, session?.turnState),
      diceLock: resolveDiceLock(session, session?.turnState),
    };
  }, [
    currentTurnPlayerEntry,
    currentTurnPlayerId,
    fallbackTurnPlayer,
    isSoloOnline,
    manualTurnRequired,
    session,
    session?.turnState,
    effectiveSessionTurnState,
    soleOnlinePlayerId,
  ]);

  const activeSeriesValues = useMemo(() => {
    const pendingDiceSeries = normalizeDiceSeries(
      resolvePendingSeriesValue(
        'pendingDiceSeries',
        turnState,
        session,
        session?.turnState,
      ),
    );
    if (pendingDiceSeries.length) {
      return pendingDiceSeries;
    }

    const pendingAppliedSeries = normalizeDiceSeries(
      resolvePendingSeriesValue(
        'pendingAppliedSeries',
        turnState,
        session,
        session?.turnState,
      ),
    );
    if (pendingAppliedSeries.length) {
      return pendingAppliedSeries;
    }

    return [];
  }, [session, session?.turnState, turnState]);

  const resolvedDiceLock = useMemo(
    () => resolveDiceLock(diceLockState, turnState, session, session?.turnState),
    [diceLockState, session, session?.turnState, turnState],
  );

  useEffect(() => {
    if (!resolvedDiceLock?.lockedUntil) {
      return undefined;
    }
    const timeout = Math.max(0, resolvedDiceLock.lockedUntil - Date.now());
    if (!timeout) {
      return undefined;
    }
    const timer = setTimeout(() => {
      setDiceLockTick((prev) => prev + 1);
    }, timeout);
    return () => clearTimeout(timer);
  }, [resolvedDiceLock?.lockedUntil]);

  const lastDiceRollForCurrent = useMemo(() => {
    if (!currentTurnPlayerId) {
      return null;
    }
    return lastDiceRolls[currentTurnPlayerId] || null;
  }, [currentTurnPlayerId, lastDiceRolls]);

  const latestCompletedRoll = useMemo(
    () => (completedMoves.length ? completedMoves[completedMoves.length - 1]?.diceValue || null : null),
    [completedMoves],
  );

  useEffect(() => {
    if (activeSeriesValues.length) {
      setLastRoll(activeSeriesValues[activeSeriesValues.length - 1]);
      return;
    }

    if (lastDiceRollForCurrent?.value !== null && lastDiceRollForCurrent?.value !== undefined) {
      setLastRoll(lastDiceRollForCurrent.value);
      return;
    }

    setLastRoll((prev) => (prev !== null && prev !== undefined ? prev : latestCompletedRoll));
  }, [activeSeriesValues, completedMoves, lastDiceRollForCurrent?.value, latestCompletedRoll]);

  const playerUiStateById = useMemo(() => {
    const latestMoveByPlayerId = new Map();
    [...completedMoves, ...moves].forEach((move) => {
      if (!Number.isInteger(move?.playerId)) {
        return;
      }
      latestMoveByPlayerId.set(move.playerId, move);
    });

    const nextState = {};
    playersList.forEach((player) => {
      if (!Number.isInteger(player?.id)) {
        return;
      }
      const playerId = player.id;
      const pendingDiceSeries = normalizeDiceSeriesItems(player?.pendingDiceSeries);
      const pendingAppliedSeries = normalizeDiceSeriesItems(player?.pendingAppliedSeries);
      const currentSeries = pendingAppliedSeries.length ? pendingAppliedSeries : pendingDiceSeries;
      const latestPlayerMove = latestMoveByPlayerId.get(playerId) || null;
      const lastRollValueFromSeries = currentSeries.length ? currentSeries[currentSeries.length - 1]?.value : null;
      const lastRollValue =
        lastDiceRolls[playerId]?.value
        ?? lastRollValueFromSeries
        ?? latestPlayerMove?.diceValue
        ?? null;

      nextState[playerId] = {
        lastRoll: lastRollValue,
        series: currentSeries,
        pending: Boolean(player?.pendingMoveId || pendingAppliedSeries.length),
        fromCell: latestPlayerMove?.fromCell ?? null,
        toCell: latestPlayerMove?.toCell ?? null,
        needContinue: pendingAppliedSeries.length > 0,
      };
    });

    return nextState;
  }, [completedMoves, lastDiceRolls, moves, playersList]);

  const playerUiState = useMemo(() => {
    if (hasConductorPrivileges || !Number.isInteger(myPlayerId)) {
      return playerUiStateById;
    }

    const nextState = {};
    Object.entries(playerUiStateById).forEach(([rawPlayerId, value]) => {
      const playerId = Number(rawPlayerId);
      if (playerId === myPlayerId) {
        nextState[playerId] = value;
        return;
      }
      nextState[playerId] = {
        lastRoll: null,
        series: [],
        pending: false,
        fromCell: null,
        toCell: null,
        needContinue: false,
      };
    });

    return nextState;
  }, [hasConductorPrivileges, myPlayerId, playerUiStateById]);

  const viewedPlayerId = isConductorView ? activeActorId : (myPlayerId ?? activeActorId);
  const viewedPlayerUiState = viewedPlayerId ? playerUiState[viewedPlayerId] || null : null;
  const playerCanViewOwnRollState = hasConductorPrivileges
    || Boolean(
      Number.isInteger(myPlayerId)
      && viewedPlayerId === myPlayerId
      && (currentTurnPlayerId === myPlayerId || viewedPlayerUiState?.needContinue || viewedPlayerUiState?.pending),
    );

  const viewedRoll = useMemo(() => {
    if (!viewedPlayerUiState || !playerCanViewOwnRollState) {
      return null;
    }
    return viewedPlayerUiState.lastRoll ?? null;
  }, [playerCanViewOwnRollState, viewedPlayerUiState]);
  const hasViewedRoll = viewedRoll !== null && viewedRoll !== undefined;

  const shouldShowWaitingState = !hasConductorPrivileges && !playerCanViewOwnRollState;

  const awaitingConfirmation = Boolean(
    game?.awaitingConfirmation
      || session?.awaitingConfirmation
      || session?.awaitingFinishDecision
      || turnState?.awaitingConfirmation
      || game?.pendingAction
      || game?.waitingForTransition
      || game?.pendingCardId,
  );

  const isGameInactive = Boolean(
    game?.status && ['finished', 'cancelled', 'archived'].includes(game.status),
  );
  const sessionFinished = sessionStatus === 'finished';
  const isGameFinished = game?.status === 'finished' || sessionFinished;
  const isViewerPlayerFinished = Boolean(
    !hasConductorPrivileges
      && myPlayerEntry
      && isFinishedStatus(myPlayerEntry.status),
  );
  const isGameFinishedForViewer = Boolean(
    isGameFinished || isGameInactive || isViewerPlayerFinished,
  );

  const turnStateStatus = useMemo(() => {
    if (!manualTurnRequired || !isSessionRunning) {
      return null;
    }
    if (sessionTurnState?.status) {
      return sessionTurnState.status;
    }
    return currentTurnPlayerId ? 'ACTIVE' : 'AWAITING_PASS';
  }, [currentTurnPlayerId, isSessionRunning, manualTurnRequired, sessionTurnState?.status]);

  const isAwaitingPass = manualTurnRequired && turnStateStatus === 'AWAITING_PASS';
  const manualTurnHandoverRequired = false;

  const isMyTurn = Boolean(
    manualTurnRequired && Number.isInteger(myPlayerId) && currentTurnPlayerId === myPlayerId,
  );

  useEffect(() => {
    const previousTurnPlayerId = previousTurnPlayerIdRef.current;
    const normalizedCurrentTurnPlayerId = Number.isInteger(currentTurnPlayerId) ? currentTurnPlayerId : null;
    const isManualDiceActor = activeActor?.diceType === 'manual' && Number.isInteger(activeActorId);

    if (
      isManualDiceActor
      && normalizedCurrentTurnPlayerId === activeActorId
      && previousTurnPlayerId !== activeActorId
    ) {
      setManualValue('');
    }

    previousTurnPlayerIdRef.current = normalizedCurrentTurnPlayerId;
  }, [activeActor?.diceType, activeActorId, currentTurnPlayerId]);

  const finishDecisionPlayerId = Number.isInteger(session?.finishDecisionPlayerId)
    ? session.finishDecisionPlayerId
    : null;
  const awaitingFinishDecision = Boolean(session?.awaitingFinishDecision);
  const finishDecisionPlayer = Number.isInteger(finishDecisionPlayerId)
    ? players.find((entry) => entry?.id === finishDecisionPlayerId) || null
    : null;
  const finishDecisionPlayerAttempt = Number.isInteger(finishDecisionPlayer?.finishAttempt)
    ? finishDecisionPlayer.finishAttempt
    : 0;
  const finishDecisionPlayerWasContinued = Boolean(finishDecisionPlayer?.wasContinuedAfterFinish);
  const finishDecisionPlayerName = sanitizeRequestText(
    finishDecisionPlayer?.name,
    t('room.names.player'),
  );
  const finishDecisionPlayerChipSrc = finishDecisionPlayer?.chipKey ? getChipSrc(finishDecisionPlayer.chipKey) : null;
  const endReason = typeof session?.endReason === 'string' ? session.endReason : null;
  const currentEndAttempt = Number.isInteger(session?.endAttempt) ? session.endAttempt : 0;
  const finishThanksEventId = typeof session?.finishThanksEventId === 'string'
    ? session.finishThanksEventId
    : '';
  const finishThanksPlayerId = Number.isInteger(session?.finishThanksPlayerId)
    ? session.finishThanksPlayerId
    : null;
  const isFinishThanksEventForMe = Boolean(
    !hasConductorPrivileges
      && myPlayerEntry
      && finishThanksEventId
      && finishThanksPlayerId === myPlayerEntry.id,
  );
  const activePlayerEndPopupTriggerKey = finishThanksEventId || String(currentEndAttempt || 0);
  const playerEndPopupStorageKey = useMemo(
    () => (id && myPlayerEntry?.id && activePlayerEndPopupTriggerKey
      ? `player_finish_popup_dismissed:${id}:${myPlayerEntry.id}:${activePlayerEndPopupTriggerKey}`
      : ''),
    [activePlayerEndPopupTriggerKey, id, myPlayerEntry?.id],
  );
  const playerEndPopupSeenStorageKey = useMemo(
    () => (id && myPlayerEntry?.id && activePlayerEndPopupTriggerKey
      ? `player_finish_popup_seen:${id}:${myPlayerEntry.id}:${activePlayerEndPopupTriggerKey}`
      : ''),
    [activePlayerEndPopupTriggerKey, id, myPlayerEntry?.id],
  );
  const dismissedEndAttempt = Number.isInteger(myPlayerEntry?.dismissedEndAttempt)
    ? myPlayerEntry.dismissedEndAttempt
    : null;
  const [isEndPopupDismissedLocally, setIsEndPopupDismissedLocally] = useState(false);
  const [isEndPopupSeenLocally, setIsEndPopupSeenLocally] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined' || !playerEndPopupStorageKey) {
      setIsEndPopupDismissedLocally(false);
      return;
    }
    setIsEndPopupDismissedLocally(localStorage.getItem(playerEndPopupStorageKey) === '1');
  }, [playerEndPopupStorageKey]);

  useEffect(() => {
    if (typeof window === 'undefined' || !playerEndPopupSeenStorageKey) {
      setIsEndPopupSeenLocally(false);
      return;
    }
    setIsEndPopupSeenLocally(localStorage.getItem(playerEndPopupSeenStorageKey) === '1');
  }, [playerEndPopupSeenStorageKey]);

  const isFinishDecisionPlayer = Boolean(
    !hasConductorPrivileges
      && myPlayerEntry
      && finishDecisionPlayerId === myPlayerEntry.id,
  );
  const shouldOpenEndPopupFromServer = Boolean(
    endReason === 'PLAYER_REACHED_68'
      && isFinishDecisionPlayer
      && awaitingFinishDecision,
  );
  const shouldOpenEndPopupFromFinishThanks = isFinishThanksEventForMe;
  const isDismissedForCurrentEndAttempt = Boolean(
    dismissedEndAttempt === currentEndAttempt || isEndPopupDismissedLocally,
  );
  const shouldTriggerPlayerEndPopup = Boolean(
    !hasConductorPrivileges
      && currentEndAttempt > 0
      && !isDismissedForCurrentEndAttempt
      && (
        (endReason === 'PLAYER_REACHED_68' && shouldOpenEndPopupFromServer)
        || (shouldOpenEndPopupFromFinishThanks && !tipsOfferOpen)
      ),
  );
  const finishSecondModalKey = useMemo(
    () => (finishDecisionPlayer
      ? `${finishDecisionPlayer.id}:${finishDecisionPlayerAttempt}`
      : ''),
    [finishDecisionPlayer, finishDecisionPlayerAttempt],
  );
  const [dismissedFinishSecondModalKey, setDismissedFinishSecondModalKey] = useState('');
  const finishSecondTimeOpen = Boolean(
    awaitingFinishDecision
      && hasConductorPrivileges
      && !isMoveAnimating
      && finishDecisionPlayer
      && finishDecisionPlayerAttempt >= 2
      && finishDecisionPlayerWasContinued
      && finishSecondModalKey
      && dismissedFinishSecondModalKey !== finishSecondModalKey,
  );
  const finishChoiceOpen = Boolean(
    awaitingFinishDecision
      && hasConductorPrivileges
      && !isMoveAnimating
      && !finishSecondTimeOpen,
  );

  const showGenericUiError = useCallback((error) => {
    console.error('[GamePage] ui action failed', error);
    setGlobalErrorModalOpen(true);
  }, []);

  useEffect(() => {
    if (!finishSecondTimeOpen) {
      return;
    }
    setConductorFinishedModalOpen(true);
  }, [finishSecondTimeOpen]);


  const handlePayTips = useCallback(async () => {
    if (tipsPaymentLoading) {
      return;
    }

    const amountEur = Number.isFinite(tipsSelectedAmount) && tipsSelectedAmount > 0
      ? tipsSelectedAmount
      : 5;

    if (!amountEur || amountEur <= 0) {
      setTipsPaymentError(t('room.tips.amountError'));
      return;
    }

    const conductorId = Number.isInteger(game?.createdById) ? game.createdById : conductorProfile?.id || null;
    const gameSessionId = Number.isInteger(session?.id) ? session.id : null;

    if (!conductorId || !gameSessionId) {
      setTipsPaymentError(t('room.tips.requestError'));
      return;
    }

    setTipsPaymentLoading(true);
    setTipsPaymentError('');

    try {
      const response = await fetch(apiUrl('/api/payments/wayforpay/tips/purchase'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          gameId: id,
          playerId: myPlayerEntry?.id || playerId || null,
          amountEur,
          language: i18n.language,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || t('room.tips.requestError'));
      }

      const data = await response.json();
      const form = document.createElement('form');
      form.method = 'POST';
      form.acceptCharset = 'utf-8';
      form.action = data.actionUrl;
      form.target = '_blank';

      Object.entries(data.fields || {}).forEach(([name, value]) => {
        const items = Array.isArray(value) ? value : [value];
        items.forEach((item) => {
          if (item === undefined || item === null) {
            return;
          }
          const input = document.createElement('input');
          input.type = 'hidden';
          input.name = name;
          input.value = String(item);
          form.appendChild(input);
        });
      });

      document.body.appendChild(form);
      form.submit();
      setTipsPaymentLoading(false);
    } catch (error) {
      showGenericUiError(error);
      setTipsPaymentError(`${t('error.genericBody', { ns: 'common' })} ${t('error.supportHint', { ns: 'common' })}`);
      setTipsPaymentLoading(false);
    }
  }, [
    conductorDisplayName,
    conductorProfile?.id,
    game?.createdById,
    i18n.language,
    myPlayerEntry?.id,
    playerId,
    session?.id,
    t,
    tipsPaymentLoading,
    tipsSelectedAmount,
    token,
    id,
    showGenericUiError,
  ]);

  useEffect(() => {
    if (finishChoiceOpen) {
      return;
    }
    if (finishChoiceLoading) {
      setFinishChoiceLoading(false);
    }
    if (finishChoiceError) {
      setFinishChoiceError('');
    }
  }, [finishChoiceError, finishChoiceLoading, finishChoiceOpen]);

  useEffect(() => {
    if (hasConductorPrivileges) {
      if (tipsOfferOpen) {
        setTipsOfferOpen(false);
      }
      return;
    }

    if (!shouldTriggerPlayerEndPopup || !activePlayerEndPopupTriggerKey) {
      return;
    }

    const alreadyOpenedForCurrentTrigger = openedPlayerEndPopupTriggerRef.current === activePlayerEndPopupTriggerKey;
    if (alreadyOpenedForCurrentTrigger) {
      if (!tipsOfferOpen) {
        setTipsOfferOpen(true);
      }
      return;
    }

    if (isEndPopupSeenLocally) {
      return;
    }

    if (playerEndPopupSeenStorageKey && typeof window !== 'undefined') {
      localStorage.setItem(playerEndPopupSeenStorageKey, '1');
      setIsEndPopupSeenLocally(true);
    }
    openedPlayerEndPopupTriggerRef.current = activePlayerEndPopupTriggerKey;

    if (!tipsOfferOpen) {
      setTipsOfferOpen(true);
    }
  }, [
    activePlayerEndPopupTriggerKey,
    endReason,
    hasConductorPrivileges,
    isEndPopupSeenLocally,
    playerEndPopupSeenStorageKey,
    shouldOpenEndPopupFromFinishThanks,
    shouldOpenEndPopupFromServer,
    shouldTriggerPlayerEndPopup,
    tipsOfferOpen,
  ]);

  const awaitingDecision = session?.awaitingDecision || turnState?.awaitingDecision || null;
  const isCell68DecisionPendingForMe = Boolean(
    awaitingDecision?.type === 'CELL_68'
      && Number.isInteger(myPlayerId)
      && awaitingDecision?.playerId === myPlayerId,
  );

  const shouldShowWaitTurn = Boolean(
    manualTurnRequired
      && !hasConductorPrivileges
      && Number.isInteger(myPlayerId)
      && isSessionRunning
      && !isGameFinishedForViewer
      && !isCell68DecisionPendingForMe
      && (isAwaitingPass || currentTurnPlayerId !== myPlayerId),
  );

  const shouldShowManualTurnHandoverRequired = false;
  const shouldShowTurnHandoverPending = false;

  const showManualInput = useMemo(
    () => activeActor?.diceType === 'manual'
      && isSessionRunning
      && isCurrentPlayersTurn
      && !isGameFinishedForViewer,
    [
      isCurrentPlayersTurn,
      isGameFinishedForViewer,
      isSessionRunning,
      activeActor?.diceType,
    ],
  );

  const rollGuardReason = useMemo(() => {
    if (isGameFinishedForViewer && !hasConductorPrivileges) {
      return 'finishedPlayer';
    }
    if (!activeActor) {
      return isAutoSelectMode ? '' : 'selectPlayer';
    }
    if (isFinishedStatus(activeActor.status)) {
      return 'finishedPlayer';
    }
    if (sessionFinished) {
      return 'sessionFinished';
    }
    if (isGameInactive) {
      return 'inactive';
    }
    if (!isSessionRunning) {
      return 'sessionNotRunning';
    }
    if (manualTurnRequired) {
      if (manualTurnHandoverRequired) {
        return 'noCurrentPlayer';
      }
      if (!currentTurnPlayerId) {
        return 'noCurrentPlayer';
      }
      if (isOtherPlayersTurn) {
        return 'otherTurn';
      }
    }
    if (turnState?.isSeriesReadyToMove) {
      return 'seriesReady';
    }
    if (awaitingConfirmation) {
      return 'awaitingConfirmation';
    }
    return '';
  }, [
    awaitingConfirmation,
    currentTurnPlayerId,
    hasConductorPrivileges,
    isGameInactive,
    isGameFinishedForViewer,
    isOtherPlayersTurn,
    isSessionRunning,
    manualTurnRequired,
    manualTurnHandoverRequired,
    activeActor,
    isAutoSelectMode,
    sessionFinished,
    turnState?.isSeriesReadyToMove,
  ]);

  const rollDisabledReason = rollGuardReason ? t(`room.roll.reasons.${rollGuardReason}`) : '';
  const currentRollHasError = Boolean(activeActor && (rollError || continueError));
  const currentRollBorderColor = currentRollHasError ? '#ef4444' : '#cbd5f5';
  const livekitBusy = livekitStatus === 'fetching' || livekitStatus === 'connecting';
  const livekitConnected = livekitStatus === 'connected';
  const livekitButtonDisabled = livekitBusy || livekitDisabledByCompletedGame;
  const livekitTooltipText = livekitDisabledByCompletedGame
    ? t('room.tooltips.videoExpiredCompletedGame')
    : t('room.tooltips.video');

  const continueGuardReason = useMemo(() => {
    if (!hasConductorPrivileges) {
      return '';
    }
    if (sessionFinished) {
      return 'sessionFinished';
    }
    if (!isSessionRunning) {
      return 'sessionNotRunning';
    }
    if (isGameInactive) {
      return 'inactive';
    }
    if (!turnState?.isSeriesReadyToMove) {
      return 'noSeries';
    }
    if (!turnState?.currentPlayerId || !currentTurnPlayerId) {
      return 'noCurrentPlayer';
    }
    if (turnState.currentPlayerId !== currentTurnPlayerId) {
      return 'otherTurn';
    }
    if (awaitingConfirmation) {
      return 'awaitingConfirmation';
    }
    return '';
  }, [
    awaitingConfirmation,
    currentTurnPlayerId,
    hasConductorPrivileges,
    isGameInactive,
    isSessionRunning,
    sessionFinished,
    turnState?.currentPlayerId,
    turnState?.isSeriesReadyToMove,
  ]);

  const continueDisabled = continuingMove || Boolean(continueGuardReason);

  const continueDisabledReason = continueGuardReason
    ? t(`room.roll.continueReasons.${continueGuardReason}`)
    : '';

  const pendingDiceSeriesSource = useMemo(
    () => resolvePendingSeriesValue(
      'pendingDiceSeries',
      turnState,
      session,
      session?.turnState,
    ),
    [session, session?.turnState, turnState],
  );

  const pendingAppliedSeriesSource = useMemo(
    () => resolvePendingSeriesValue(
      'pendingAppliedSeries',
      turnState,
      session,
      session?.turnState,
    ),
    [session, session?.turnState, turnState],
  );

  const currentTurnPendingDice = useMemo(
    () => normalizeDiceSeries(pendingDiceSeriesSource),
    [pendingDiceSeriesSource],
  );

  const currentTurnPendingApplied = useMemo(
    () => normalizeDiceSeries(pendingAppliedSeriesSource),
    [pendingAppliedSeriesSource],
  );

  const pendingSeriesInfo = useMemo(
    () => getPendingSeriesInfo(currentTurnPendingDice, currentTurnPendingApplied),
    [currentTurnPendingApplied, currentTurnPendingDice],
  );

  const viewedSeries = useMemo(() => {
    if (!viewedPlayerUiState || !playerCanViewOwnRollState) {
      return [];
    }
    return normalizeDiceSeriesItems(viewedPlayerUiState.series);
  }, [playerCanViewOwnRollState, viewedPlayerUiState]);

  const isSeriesInProgress = useMemo(
    () => pendingSeriesInfo.series.length > 1,
    [pendingSeriesInfo.series.length],
  );

  const turnBusy = diceRolling || isMoveAnimating || isSeriesInProgress || isDiceAnimating;

  const isDiceLockActive = useMemo(() => {
    if (!resolvedDiceLock || !Number.isFinite(resolvedDiceLock.lockedUntil)) {
      return false;
    }
    if (Number.isInteger(resolvedDiceLock.playerId) && activeActorId) {
      if (resolvedDiceLock.playerId !== activeActorId) {
        return false;
      }
    }
    return Date.now() < resolvedDiceLock.lockedUntil;
  }, [activeActorId, diceLockTick, resolvedDiceLock]);

  const rollDisabled = rolling || turnBusy || Boolean(rollGuardReason) || isDiceLockActive;

  const hasSeriesContext = useMemo(
    () => viewedSeries.length >= 2,
    [viewedSeries.length],
  );

  const seriesText = useMemo(() => renderDiceSeries(viewedSeries, 'current-roll-series'), [viewedSeries]);

  const isSeriesReadyToMove = turnState?.isSeriesReadyToMove === true;
  const isSeriesFirstStepPending = useMemo(() => {
    if (!isSeriesReadyToMove) {
      return false;
    }
    if (pendingSeriesInfo.series.length < 2) {
      return false;
    }
    if (!currentTurnPendingApplied.length) {
      return false;
    }
    return currentTurnPendingApplied.length === pendingSeriesInfo.series.length;
  }, [
    currentTurnPendingApplied.length,
    isSeriesReadyToMove,
    pendingSeriesInfo.series.length,
  ]);
  const isTurnFinished = useMemo(
    () => !isMoveAnimating && !turnState?.awaitingAction,
    [isMoveAnimating, turnState?.awaitingAction],
  );

  const seriesSignature = useMemo(
    () => (pendingSeriesInfo.series.length ? pendingSeriesInfo.series.join('+') : ''),
    [pendingSeriesInfo.series],
  );

  const turnBusyState = diceRolling || isMoveAnimating || isSeriesReadyToMove || isDiceAnimating;

  const rollDisabledState = rolling || turnBusyState || Boolean(rollGuardReason) || isDiceLockActive;

  const undoLocked = undoingMove || turnBusyState || awaitingConfirmation;
  const canUndoMoveForCandidate = useCallback(
    (candidate) => Boolean(
      hasConductorPrivileges
        && candidate
        && session?.id
        && !isUndoBarrierBlockedForMove(candidate),
    ),
    [hasConductorPrivileges, isUndoBarrierBlockedForMove, session?.id],
  );

  const shouldShowContinue = useMemo(
    () => hasConductorPrivileges
      && isSeriesReadyToMove
      && activeActorId === currentTurnPlayerId
      && pendingSeriesInfo.series.length > 1,
    [
      currentTurnPlayerId,
      hasConductorPrivileges,
      isSeriesReadyToMove,
      pendingSeriesInfo.series.length,
      activeActorId,
    ],
  );

  const notifyRecordingCardEvent = useCallback((action, cellNumber) => {
    // No client-side actor identity or timestamp is trusted by the recording API.
    if (!game?.id || sessionStatus !== 'running' || !Number.isInteger(cellNumber)) return;
    const requestId = typeof crypto !== 'undefined' && crypto.randomUUID
      ? crypto.randomUUID()
      : `${Date.now()}_${Math.random().toString(36).slice(2, 12)}`;
    const body = { action, cellNumber, requestId };
    const headers = { 'Content-Type': 'application/json' };
    if (hasConductorPrivileges && token) {
      headers.Authorization = `Bearer ${token}`;
    } else if (resolvedPlayerToken) {
      body.participantToken = resolvedPlayerToken;
      body.connectionId = getActiveConnectionId();
    } else {
      return;
    }
    fetch(apiUrl(`/api/games/${game.id}/recording/card`), {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
    }).then((response) => {
      if (!response.ok) console.warn('[Recording] card event rejected', response.status);
    }).catch((error) => console.warn('[Recording] card event request failed', error));
  }, [game?.id, sessionStatus, hasConductorPrivileges, token, resolvedPlayerToken, getActiveConnectionId]);

  const handleCellClick = (cellNumber) => {
    const card = cards.find((entry) => entry.cellNumber === cellNumber) || null;
    setCardModal({
      open: true,
      cellNumber,
      card,
      error: card ? '' : t('errors.cardNotFound'),
    });
    notifyRecordingCardEvent('opened', cellNumber);
  };

  const handleCloseCardModal = () => {
    if (cardModal.open && Number.isInteger(cardModal.cellNumber)) {
      notifyRecordingCardEvent('closed', cardModal.cellNumber);
    }
    setCardModal({ open: false, cellNumber: null, card: null, error: '' });
  };

  const handlePlayerCardClick = async (player, event) => {
    if (!player) {
      return;
    }

    if (!hasConductorPrivileges) {
      return;
    }

    if (event?.target?.closest?.(PAUSE_BUTTON_SELECTOR)) {
      return;
    }

    setSelectedPlayerId(player.id);
    setManualViewLock(true);
    setTurnChangeError('');
    setPlayersPanelError('');
    const shouldAttemptPass = armedPassPlayerId === player.id;
    setArmedPassPlayerId(player.id);

    if (!shouldAttemptPass) {
      return;
    }

    if (!manualTurnRequired) {
      return;
    }

    if (!session || !isSessionRunning) {
      return;
    }

    if (isFinishedStatus(player.status)) {
      return;
    }

    if (player?.isPaused) {
      return;
    }

    if (currentTurnPlayerId === player.id && !manualTurnHandoverRequired) {
      return;
    }

    if (!token) {
      setTurnChangeError(t('errors.requestFailed'));
      return;
    }

    setSwitchingPlayerId(player.id);
    try {
      const response = await fetch(apiUrl(`/api/games/${id}/pass-turn`), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ targetPlayerId: player.id }),
      });

      if (!response.ok) {
        const errorData = await parseResponseError(response, t('errors.requestFailed'));
        if (errorData.code === 'TURN_NOT_FINISHED') {
          throw new Error('Дочекайтесь завершення ходу');
        }
        throw new Error(errorData.message);
      }

      const data = await response.json();

      setGame((prev) => {
        if (!prev) {
          return prev;
        }

        const updatedSession = data?.session ? { ...(prev.session || {}), ...data.session } : prev.session;

        return {
          ...prev,
          players: Array.isArray(data?.players) ? data.players : prev.players,
          session: updatedSession,
        };
      });

      await syncGameState();
      setArmedPassPlayerId(null);
    } catch (switchError) {
      const safeMessage = normalizeUiErrorMessage(switchError, t('errors.requestFailed'));
      setTurnChangeError(safeMessage);
      if (!safeMessage && isDev) {
        console.error('[TURN_CHANGE_SYSTEM_ERROR]', switchError);
      }
    } finally {
      setSwitchingPlayerId(null);
    }
  };

  const handlePauseTogglePointerDown = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setArmedPassPlayerId(null);
  };

  const handlePauseToggleClick = async (event, player) => {
    event.preventDefault();
    event.stopPropagation();

    setArmedPassPlayerId(null);
    setTurnChangeError('');
    setPlayersPanelError('');

    if (personalGame) {
      return;
    }

    if (!hasConductorPrivileges || !player || !session?.id || !token) {
      return;
    }

    setSwitchingPlayerId(player.id);
    try {
      const response = await fetch(apiUrl(`/api/games/${id}/toggle-pause`), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ playerId: player.id }),
      });

      if (!response.ok) {
        const errorData = await parseResponseError(response, t('errors.requestFailed'));
        throw new Error(errorData.message);
      }

      const data = await response.json();
      setGame((prev) => {
        if (!prev) {
          return prev;
        }
        return {
          ...prev,
          players: Array.isArray(data?.players) ? data.players : prev.players,
          session: data?.session ? { ...(prev.session || {}), ...data.session } : prev.session,
        };
      });
      await syncGameState();
    } catch (pauseError) {
      setPlayersPanelError(normalizeUiErrorMessage(pauseError, t('errors.requestFailed')));
    } finally {
      setSwitchingPlayerId(null);
    }
  };

  const handleDiceTypeChange = async (player, nextDiceType) => {
    if (!player || !nextDiceType || player.diceType === nextDiceType) {
      return;
    }

    if (!['auto', 'manual'].includes(nextDiceType)) {
      return;
    }

    if (!canEditDiceType || !token) {
      setPlayersPanelError(t('errors.requestFailed'));
      return;
    }

    setPlayersPanelError('');
    setDiceTypeUpdatingId(player.id);
    try {
      const response = await fetch(apiUrl(`/api/games/${id}/players/${player.id}`), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ diceType: nextDiceType }),
      });

      if (!response.ok) {
        throw new Error(await parseError(response, t('errors.requestFailed')));
      }

      const data = await response.json();
      const updatedPlayer = data?.player;

      if (updatedPlayer?.id) {
        setGame((prev) => {
          if (!prev) {
            return prev;
          }
          return {
            ...prev,
            players: (prev.players || []).map((item) => (item.id === updatedPlayer.id ? { ...item, ...updatedPlayer } : item)),
          };
        });
      }
    } catch (diceTypeError) {
      const rawMessage = normalizeUiErrorMessage(diceTypeError, t('errors.requestFailed'));
      const safeMessage = localizeDiceTypeErrorMessage(rawMessage, t);
      setPlayersPanelError(safeMessage);
      if (!safeMessage && isDev) {
        console.error('[DICE_TYPE_SYSTEM_ERROR]', diceTypeError);
      }
    } finally {
      setDiceTypeUpdatingId(null);
    }
  };


  const handleOpenSuperScenarioModal = useCallback(() => {
    setSuperScenarioModalOpen(true);
    setSuperScenarioModalMode('menu');
    setSuperScenarioTargetCell('');
    setSuperScenarioFutureMoves('');
    setSuperScenarioError('');
  }, []);

  const handleApplySuperScenario = useCallback(async () => {
    if (!token || !id) {
      return;
    }
    const targetPlayerId = activeActorId || game?.session?.currentPlayerId || null;
    if (!targetPlayerId) {
      setSuperScenarioError(t('room.superScenario.errors.playerRequired'));
      return;
    }

    const trimmedFutureMoves = String(superScenarioFutureMoves || '').trim();
    const trimmedTargetCell = String(superScenarioTargetCell || '').trim();

    let payload = null;
    if (superScenarioModalMode === 'futureMoves') {
      const values = trimmedFutureMoves
        .split(/[^0-9]+/)
        .map((item) => Number(item))
        .filter((item) => Number.isInteger(item));

      const invalid = values.length < 2 || values.some((item) => item < 1 || item > 6);
      if (invalid) {
        setSuperScenarioError(t('room.superScenario.errors.invalidValues'));
        return;
      }
      payload = { values };
    } else {
      const targetCell = Number(trimmedTargetCell);
      if (!Number.isInteger(targetCell) || targetCell < 1 || targetCell > 72) {
        setSuperScenarioError(t('room.superScenario.errors.invalidTargetCell'));
        return;
      }
      payload = { targetCell };
    }

    setSuperScenarioApplying(true);
    setSuperScenarioError('');
    try {
      const response = await fetch(apiUrl(`/api/games/${id}/players/${targetPlayerId}/future-dice-scenario`), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(await parseError(response, t('errors.requestFailed')));
      }

      const data = await response.json();
      const updatedPlayer = data?.player;
      if (updatedPlayer?.id) {
        setGame((prev) => {
          if (!prev) {
            return prev;
          }
          return {
            ...prev,
            players: (prev.players || []).map((item) => (item.id === updatedPlayer.id ? { ...item, ...updatedPlayer } : item)),
          };
        });
      }
      setSuperScenarioModalOpen(false);
      setSuperScenarioModalMode('menu');
      setSuperScenarioTargetCell('');
      setSuperScenarioFutureMoves('');
    } catch (applyError) {
      setSuperScenarioError(normalizeUiErrorMessage(applyError, t('errors.requestFailed')));
    } finally {
      setSuperScenarioApplying(false);
    }
  }, [
    activeActorId,
    game?.session?.currentPlayerId,
    id,
    superScenarioFutureMoves,
    superScenarioModalMode,
    superScenarioTargetCell,
    t,
    token,
  ]);

  const handleSavePlayerSetup = async () => {
    if (!token || !activeActor) {
      return;
    }

    const nameIsRequired = game?.type === 'personal';
    const trimmedName = setupName.trim();

    if (nameIsRequired && !trimmedName) {
      setSetupError(t('errors.nameRequired'));
      return;
    }

    if (!setupChipKey) {
      setSetupError(t('errors.tokenRequired'));
      return;
    }

    setSetupSaving(true);
    setSetupError('');
    try {
      const payload = { chipKey: setupChipKey };
      if (trimmedName) {
        payload.name = trimmedName;
      }

      const response = await fetch(apiUrl(`/api/games/${id}/players/${activeActor.id}`), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        if (errorData?.error === 'CHIP_TAKEN') {
          setSetupError(t('room.setup.chipTaken'));
          setSetupChipKey('');
          return;
        }
        throw new Error(errorData?.error || t('errors.requestFailed'));
      }

      const data = await response.json();
      const updatedPlayer = data?.player;
      if (updatedPlayer?.id) {
        setGame((prev) => {
          if (!prev) {
            return prev;
          }
          return {
            ...prev,
            players: prev.players.map((player) => (player.id === updatedPlayer.id ? { ...player, ...updatedPlayer } : player)),
          };
        });
      }

      if (setupStorageKey) {
        localStorage.setItem(setupStorageKey, '1');
      }

      setSetupModalOpen(false);
    } catch (setupErr) {
      setSetupError(setupErr.message);
    } finally {
      setSetupSaving(false);
    }
  };

  const handleStartSession = async () => {
    if (!token || !canStartSession) {
      return;
    }
    setStartingSession(true);
    setStartError('');
    try {
      const response = await fetchWithIdempotencyRetry(apiUrl(`/api/games/${id}/session/start`), {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) {
        throw new Error(await parseError(response, t('errors.requestFailed')));
      }
      const data = await response.json();
      setGame((prev) => {
        if (!prev) {
          return prev;
        }
        return {
          ...prev,
          status: prev.status === 'planned' ? 'active' : prev.status,
          session: data.session || prev.session || null,
        };
      });
    } catch (startErr) {
      showGenericUiError(startErr);
      setStartError(`${t('error.genericBody', { ns: 'common' })} ${t('error.supportHint', { ns: 'common' })}`);
    } finally {
      setStartingSession(false);
    }
  };

  const handleFinishSession = async () => {
    if (!token || !game?.session || !canFinishSession) {
      return;
    }
    setFinishingSession(true);
    setFinishError('');
    try {
      const response = await fetch(apiUrl(`/api/games/${id}/session/finish`), {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) {
        throw new Error(await parseError(response, t('errors.requestFailed')));
      }
      const data = await response.json();
      setGame((prev) => {
        if (!prev) {
          return prev;
        }
        return {
          ...prev,
          status: prev.status === 'finished' ? prev.status : 'finished',
          session: data.session || prev.session || null,
        };
      });
    } catch (finishErr) {
      showGenericUiError(finishErr);
      setFinishError(`${t('error.genericBody', { ns: 'common' })} ${t('error.supportHint', { ns: 'common' })}`);
    } finally {
      setFinishingSession(false);
    }
  };

  const handleFinishSessionRequest = () => {
    if (finishingSession || !canFinishSession) {
      return;
    }
    setFinishSessionConfirmOpen(true);
  };

  const handleFinishSessionConfirm = () => {
    setFinishSessionConfirmOpen(false);
    handleFinishSession();
  };

  const requestContinueMove = useCallback(
    async (playerId, { silent = false } = {}) => {
      if (!token || !playerId || !turnState?.isSeriesReadyToMove || !hasConductorPrivileges) {
        return false;
      }

      if (continueGuardReason) {
        if (!silent) {
          setContinueError(continueDisabledReason || t('errors.requestFailed'));
        }
        return false;
      }

      if (continuingMove || continueMoveInFlightRef.current) {
        return false;
      }

      setContinuingMove(true);
      if (!silent) {
        setContinueError('');
      }
      userInitiatedMoveRef.current = true;
      const seriesKey = pendingSeriesInfo.series.length ? pendingSeriesInfo.series.join('+') : 'no-series';
      continueMoveKeyRef.current = `${id}:${playerId}:${seriesKey}`;
      continueMoveInFlightRef.current = true;

      try {
        const response = await fetch(apiUrl(`/api/games/${id}/continue-move`), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ playerId }),
        });

        if (response.status === 409) {
          await syncGameState();
          return false;
        }

        if (!response.ok) {
          throw new Error(await parseError(response, t('errors.requestFailed')));
        }

        const data = await response.json();
        if (data?.shouldResetTo68 && Number.isInteger(playerId)) {
          updateDisplayedPositions((prev) => ({ ...prev, [playerId]: 68 }), 'continue-reset-to-68');
        }
        applyMoveResponse(data);
        const fromCell = Number(data?.fromCell);
        const diceValue = Number(data?.diceValue);
        const toCell = Number(data?.toCell);
        const isOvershoot =
          Number.isFinite(fromCell)
          && Number.isFinite(diceValue)
          && fromCell + diceValue > TOTAL_CELLS
          && Number.isFinite(toCell)
          && toCell === fromCell;
        if (isOvershoot) {
          const overshootAlertKey = diceValue === 6
            ? 'room.alerts.overshootRollAgain'
            : 'room.alerts.overshootWrongThoughtRollAgain';
          showRollToast(t(overshootAlertKey), 'warning');
        }
        if (!Array.isArray(data?.moves)) {
          await syncGameState();
        }
        return true;
      } catch (continueErr) {
        if (!silent) {
          setContinueError(continueErr.message);
        }
        return false;
      } finally {
        setContinuingMove(false);
        continueMoveKeyRef.current = '';
        continueMoveInFlightRef.current = false;
      }
    },
    [
      applyMoveResponse,
      continueDisabledReason,
      continueGuardReason,
      continuingMove,
      hasConductorPrivileges,
      id,
      pendingSeriesInfo.series,
      showRollToast,
      syncGameState,
      t,
      token,
      turnState?.isSeriesReadyToMove,
      updateDisplayedPositions,
    ],
  );

const handleContinueMove = async () => {
  const continueMovePlayerId = turnState?.currentPlayerId ?? currentTurnPlayerId ?? null;
  if (!continueMovePlayerId) return;

  await requestContinueMove(continueMovePlayerId);
};

  const handleUndoLastMove = async (candidate) => {
    if (!candidate || !session?.id || !token || !hasConductorPrivileges) {
      return;
    }

    if (undoingMove) {
      return;
    }

    if (isGameFinished) {
      const message = t('errors.undoFinished');
      showRollToast(message, 'warning');
      setUndoError(message);
      return;
    }

    setUndoingMove(true);
    setUndoError('');
    userInitiatedMoveRef.current = true;

    try {
      const response = await fetch(
        apiUrl(`/api/game-sessions/${session.id}/players/${candidate.playerId}/undo-last-action`),
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (!response.ok) {
        const errorData = await parseResponseError(response, t('errors.requestFailed'));
        if (errorData.code === 'GAME_FINISHED') {
          const message = t('errors.undoFinished');
          showRollToast(message, 'warning');
          throw new Error(message);
        }
        throw new Error(errorData.message);
      }

      const data = await response.json();
      applyMoveResponse(data);
    } catch (undoRequestError) {
      setUndoError(undoRequestError.message);
    } finally {
      setUndoingMove(false);
    }
  };

  const showTurnToast = useCallback((message, variant = 'success') => {
    setTurnToast({ message, visible: true, variant });

    if (turnToastTimerRef.current) {
      clearTimeout(turnToastTimerRef.current);
    }
    if (turnToastExitTimerRef.current) {
      clearTimeout(turnToastExitTimerRef.current);
    }

    turnToastTimerRef.current = setTimeout(() => {
      setTurnToast((prev) => (prev.message ? { ...prev, visible: false } : prev));
      turnToastExitTimerRef.current = setTimeout(() => {
        setTurnToast({ message: '', visible: false, variant });
      }, 350);
    }, 3000);
  }, []);

  useEffect(() => {
    if (typeof document === 'undefined') {
      return undefined;
    }

    if (tipPaymentState !== 'waiting') {
      return undefined;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [tipPaymentState]);

  const clearTipOrderReferenceFromUrl = useCallback(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const nextUrl = new URL(window.location.href);
    nextUrl.searchParams.delete('tipOrderReference');
    window.history.replaceState({}, '', `${nextUrl.pathname}${nextUrl.search}${nextUrl.hash}`);
  }, []);

  const startTipsWaitingFlow = useCallback((orderReference, startedAt = Date.now(), persistToSession = false) => {
    const normalizedOrderReference = String(orderReference || '').trim();
    if (!normalizedOrderReference || consumedTipOrderRef.current === normalizedOrderReference) {
      return;
    }

    if (persistToSession && typeof window !== 'undefined') {
      sessionStorage.setItem(
        WFP_TIP_FLOW_KEY,
        JSON.stringify({
          orderReference: normalizedOrderReference,
          startedAt,
          gameId: id,
        }),
      );
    }

    tipsFinalizedRef.current = false;
    tipsSyncRequestedAtRef.current = 0;
    setTipPaymentResult(null);
    setTipPaymentAmount(0);
    setTipPaymentCurrency('EUR');
    setTipsOfferOpen(false);
    setReturnOfferAfterFail(false);
    setTipPaymentOrderRef(normalizedOrderReference);
    setTipsFlowStartedAt(startedAt);
    setTipPaymentState('waiting');
  }, [id]);

  const finalizeTipsFlow = useCallback(({ variant, amount = 0, currency = 'EUR', orderReference = '' }) => {
    if (tipsFinalizedRef.current) {
      return;
    }

    const normalizedOrderReference = String(orderReference || tipPaymentOrderRef || '').trim();
    tipsFinalizedRef.current = true;
    if (tipsPollingTimerRef.current) {
      window.clearTimeout(tipsPollingTimerRef.current);
      tipsPollingTimerRef.current = null;
    }
    if (tipsPollAbortControllerRef.current) {
      tipsPollAbortControllerRef.current.abort();
      tipsPollAbortControllerRef.current = null;
    }

    tipsInFlightRef.current = false;
    tipsSyncRequestedAtRef.current = 0;
    if (normalizedOrderReference) {
      consumedTipOrderRef.current = normalizedOrderReference;
    }

    setTipPaymentState('result');
    setTipPaymentOrderRef(null);
    setTipsFlowStartedAt(0);
    if (typeof window !== 'undefined') {
      sessionStorage.removeItem(WFP_TIP_FLOW_KEY);
      sessionStorage.removeItem(WFP_TIP_STARTED_KEY);
      if (variant === 'success' && tipOfferDismissedStorageKey) {
        localStorage.setItem(tipOfferDismissedStorageKey, '1');
      }
    }
    clearTipOrderReferenceFromUrl();
    setTipPaymentResult(variant === 'success' ? 'approved' : 'failed');
    setTipPaymentAmount(amount);
    setTipPaymentCurrency(currency);
    setReturnOfferAfterFail(variant !== 'success');
  }, [clearTipOrderReferenceFromUrl, tipOfferDismissedStorageKey, tipPaymentOrderRef]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    if (tipPaymentState === 'waiting') {
      return;
    }

    if (tipOrderReference && consumedTipOrderRef.current !== tipOrderReference) {
      startTipsWaitingFlow(tipOrderReference, Date.now(), true);
      return;
    }

    const rawValue = sessionStorage.getItem(WFP_TIP_FLOW_KEY);
    if (rawValue) {
      try {
        const parsed = JSON.parse(rawValue);
        if (parsed?.gameId && String(parsed.gameId) !== String(id)) {
          sessionStorage.removeItem(WFP_TIP_FLOW_KEY);
          sessionStorage.removeItem(WFP_TIP_STARTED_KEY);
          return;
        }
        if (parsed?.orderReference && consumedTipOrderRef.current !== parsed.orderReference) {
          startTipsWaitingFlow(parsed.orderReference, Number(parsed.startedAt) || Date.now(), false);
          return;
        }
      } catch (error) {
        sessionStorage.removeItem(WFP_TIP_FLOW_KEY);
      }
    }

    const startedRaw = sessionStorage.getItem(WFP_TIP_STARTED_KEY);
    if (!startedRaw) {
      return;
    }

    try {
      const started = JSON.parse(startedRaw);
      if (started?.gameId && String(started.gameId) !== String(id)) {
        sessionStorage.removeItem(WFP_TIP_STARTED_KEY);
        return;
      }
      const contextRaw = sessionStorage.getItem(TIPS_CONTEXT_KEY);
      const context = contextRaw ? JSON.parse(contextRaw) : null;
      const contextOrderReference = String(context?.orderReference || '').trim();
      if (contextOrderReference && consumedTipOrderRef.current !== contextOrderReference) {
        startTipsWaitingFlow(contextOrderReference, Number(started?.startedAt) || Date.now(), true);
      }
    } catch (error) {
      sessionStorage.removeItem(WFP_TIP_STARTED_KEY);
    }
  }, [id, startTipsWaitingFlow, tipOrderReference, tipPaymentState]);

  useEffect(() => {
    if (tipPaymentState !== 'waiting' || !tipPaymentOrderRef) {
      return undefined;
    }

    let cancelled = false;

    const scheduleNextPoll = () => {
      if (cancelled || tipsFinalizedRef.current || tipPaymentState !== 'waiting') {
        return;
      }
      tipsPollingTimerRef.current = window.setTimeout(runPollingCycle, TIP_POLL_FAST_INTERVAL);
    };

    const resolveFinalStatus = (status) => {
      const normalized = String(status || '').trim().toLowerCase();
      if (normalized === 'approved') {
        return 'success';
      }
      if (normalized && normalized !== 'pending') {
        return 'fail';
      }
      return 'pending';
    };

    const handleStatusResponse = async (response) => {
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload.error || 'Failed to fetch tips payment status');
      }

      const finalStatus = resolveFinalStatus(payload?.status);
      if (finalStatus !== 'pending') {
        const amount = Number(payload?.amount);
        finalizeTipsFlow({
          variant: finalStatus,
          amount: Number.isFinite(amount) ? amount : 0,
          currency: typeof payload?.currency === 'string' ? payload.currency : 'EUR',
          orderReference: tipPaymentOrderRef,
        });
        return true;
      }

      return false;
    };

    const runPollingCycle = async () => {
      if (cancelled || tipsFinalizedRef.current || tipsInFlightRef.current || tipPaymentState !== 'waiting') {
        return;
      }

      const elapsed = Date.now() - tipsFlowStartedAt;
      if (elapsed >= TIP_POLL_TIMEOUT_MS) {
        finalizeTipsFlow({ variant: 'fail', orderReference: tipPaymentOrderRef });
        return;
      }

      tipsInFlightRef.current = true;
      const controller = new AbortController();
      tipsPollAbortControllerRef.current = controller;

      try {
        const statusResponse = await fetch(
          apiUrl(`/api/payments/tips/${encodeURIComponent(tipPaymentOrderRef)}`),
          {
            method: 'GET',
            signal: controller.signal,
          },
        );
        const finalizedByStatus = await handleStatusResponse(statusResponse);
        if (finalizedByStatus) {
          return;
        }

        const now = Date.now();
        if (now - tipsSyncRequestedAtRef.current >= TIP_POLL_SYNC_INTERVAL_MS || tipsSyncRequestedAtRef.current === 0) {
          tipsSyncRequestedAtRef.current = now;
          const syncResponse = await fetch(
            apiUrl(`/api/payments/wayforpay/tips/${encodeURIComponent(tipPaymentOrderRef)}/sync`),
            {
              method: 'POST',
              signal: controller.signal,
            },
          );
          const finalizedBySync = await handleStatusResponse(syncResponse);
          if (finalizedBySync) {
            return;
          }
        }

        scheduleNextPoll();
      } catch (error) {
        if (!cancelled && error.name !== 'AbortError') {
          scheduleNextPoll();
        }
      } finally {
        tipsInFlightRef.current = false;
        tipsPollAbortControllerRef.current = null;
      }
    };

    runPollingCycle();

    return () => {
      cancelled = true;
      if (tipsPollingTimerRef.current) {
        window.clearTimeout(tipsPollingTimerRef.current);
        tipsPollingTimerRef.current = null;
      }
      if (tipsPollAbortControllerRef.current) {
        tipsPollAbortControllerRef.current.abort();
        tipsPollAbortControllerRef.current = null;
      }
      tipsInFlightRef.current = false;
    };
  }, [finalizeTipsFlow, tipPaymentOrderRef, tipsFlowStartedAt, tipPaymentState]);

  useEffect(
    () => () => {
      if (rollToastTimerRef.current) {
        clearTimeout(rollToastTimerRef.current);
      }
      if (rollToastExitTimerRef.current) {
        clearTimeout(rollToastExitTimerRef.current);
      }
      if (stopAlertsQueueTimerRef.current) {
        clearTimeout(stopAlertsQueueTimerRef.current);
      }
    },
    [],
  );

  const handleDismissEndPopup = useCallback(async () => {
    if (!id || !Number.isInteger(currentEndAttempt)) {
      return;
    }

    setTipsOfferOpen(false);

    try {
      if (typeof window !== 'undefined' && playerEndPopupStorageKey) {
        localStorage.setItem(playerEndPopupStorageKey, '1');
      }
      setIsEndPopupDismissedLocally(true);
      const payload = {
        endAttempt: currentEndAttempt,
        roomId,
        token: inviteToken,
        participantToken: resolvedPlayerToken,
      };

      const response = await fetch(apiUrl(`/api/games/${id}/end-popup-dismiss`), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(await parseError(response, t('errors.requestFailed')));
      }

      setGame((prev) => {
        if (!prev) {
          return prev;
        }
        const players = Array.isArray(prev.players) ? prev.players.map((item) => {
          if (item?.id !== myPlayerEntry?.id) {
            return item;
          }
          return { ...item, dismissedEndAttempt: currentEndAttempt };
        }) : prev.players;
        return { ...prev, players };
      });
    } catch (dismissError) {
      showGenericUiError(dismissError);
    }
  }, [
    currentEndAttempt,
    id,
    inviteToken,
    myPlayerEntry?.id,
    playerEndPopupStorageKey,
    resolvedPlayerToken,
    roomId,
    token,
    showGenericUiError,
  ]);

  const handleFinishDecision = useCallback(async (action) => {
    if (!token || !id) {
      setFinishChoiceError(t('errors.requestFailed'));
      return;
    }

    if (!['finish', 'continue'].includes(action)) {
      return;
    }

    setFinishChoiceLoading(true);
    setFinishChoiceError('');
    try {
      const response = await fetch(apiUrl(`/api/games/${id}/finish-decision`), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ action }),
      });

      if (!response.ok) {
        throw new Error(await parseError(response, t('errors.requestFailed')));
      }

      const data = await response.json();
      applyMoveResponse(data);
    } catch (finishError) {
      showGenericUiError(finishError);
      setFinishChoiceError(`${t('error.genericBody', { ns: 'common' })} ${t('error.supportHint', { ns: 'common' })}`);
    } finally {
      setFinishChoiceLoading(false);
    }
  }, [applyMoveResponse, id, t, token, showGenericUiError]);

  useEffect(
    () => () => {
      if (turnToastTimerRef.current) {
        clearTimeout(turnToastTimerRef.current);
      }
      if (turnToastExitTimerRef.current) {
        clearTimeout(turnToastExitTimerRef.current);
      }
    },
    [],
  );

  useEffect(
    () => () => {
      if (chatToastTimerRef.current) {
        clearTimeout(chatToastTimerRef.current);
      }
      if (chatToastExitTimerRef.current) {
        clearTimeout(chatToastExitTimerRef.current);
      }
    },
    [],
  );

  useEffect(() => {
    if (!manualTurnRequired || hasConductorPrivileges || !Number.isInteger(myPlayerId)) {
      wasMyTurnRef.current = isMyTurn;
      return;
    }

    if (isMyTurn && !wasMyTurnRef.current) {
      showTurnToast(t('your_turn'), 'success');
    }

    wasMyTurnRef.current = isMyTurn;
  }, [hasConductorPrivileges, isMyTurn, manualTurnRequired, myPlayerId, showTurnToast, t]);

  const resolveRollErrorMessage = useCallback(
    (errorData) => {
      if (!errorData) {
        return t('errors.requestFailed');
      }
      if (errorData.code === 'SERIES_READY') {
        return t('room.roll.reasons.seriesReady');
      }
      const message = typeof errorData.message === 'string' ? errorData.message : '';
      if (manualTurnRequired && isAwaitingPass) {
        const normalizedMessage = message.toLowerCase();
        if (normalizedMessage.includes('передача хода')) {
          return '';
        }
      }
      return localizeApiErrorMessage(errorData, t);
    },
    [isAwaitingPass, manualTurnRequired, t],
  );

  const handleRoll = async () => {
    if (!activeActor) {
      return null;
    }

    if (rollGuardReason) {
      setRollError(rollDisabledReason || t('errors.requestFailed'));
      return null;
    }

    if (forcePlayerMode && (!roomId || !inviteToken)) {
      setRollError(t('errors.requestFailed'));
      return null;
    }

    if (!forcePlayerMode && !token) {
      setRollError(t('errors.requestFailed'));
      return null;
    }

    const mode = activeActor.diceType === 'manual' ? 'manual' : 'auto';
    let normalizedManualValue = null;

    if (mode === 'manual') {
      const parsed = Number(manualValue);
      if (!manualValue || Number.isNaN(parsed) || parsed < 1 || parsed > 6) {
        setRollError(t('room.roll.errors.manualValueRange'));
        return null;
      }
      normalizedManualValue = Math.max(1, Math.min(6, Math.floor(parsed)));
    }

    setRolling(true);
    setRollError('');
    setLastRoll(null);
    setPendingRollResult(null);
    userInitiatedMoveRef.current = true;
    try {
      const payload = {
        playerId: activeActor.id,
        mode,
        manualValue: mode === 'manual' ? normalizedManualValue : null,
      };

      if (forcePlayerMode) {
        const resolvedConnectionId = getActiveConnectionId();
        const response = await fetch(apiUrl(`/api/game-sessions/${roomId}/roll`), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            ...payload,
            token: inviteToken,
            participantToken,
            connectionId: resolvedConnectionId || undefined,
          }),
        });

        if (!response.ok) {
          const errorData = await parseResponseError(response, t('errors.requestFailed'));
          if (CONNECTION_ERROR_CODES.has(errorData.code)) {
            openConnectionIssue('singleConnection');
            return null;
          }
          if (response.status === 409) {
            const normalizedMessage = (errorData.message || '').toLowerCase();
            if (normalizedMessage.includes('ход другого игрока') || TURN_CONFLICT_ERROR_KEYS.has(errorData.message)) {
              suppressConductorAutoSelectRef.current = true;
              await syncGameState();
              throw new Error(localizeApiErrorMessage(errorData, t));
            }
          }
          throw new Error(resolveRollErrorMessage(errorData));
        }

        const data = await response.json();
        const rollValue = getRollValueFromResponse(data);
        setPendingRollResult({
          data,
          rollValue,
          mode,
          normalizedManualValue,
          forcePlayerMode: true,
          needsEntrySix: data?.reason === 'NEED_SIX_TO_START',
        });
        return rollValue;
      }

      const response = await fetch(apiUrl(`/api/games/${id}/roll`), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        const errorData = await parseResponseError(response, t('errors.requestFailed'));
        if (response.status === 409) {
          const normalizedMessage = (errorData.message || '').toLowerCase();
          if (normalizedMessage.includes('ход другого игрока') || TURN_CONFLICT_ERROR_KEYS.has(errorData.message)) {
            suppressConductorAutoSelectRef.current = true;
            await syncGameState();
            throw new Error(localizeApiErrorMessage(errorData, t));
          }
        }
        throw new Error(resolveRollErrorMessage(errorData));
      }
      const data = await response.json();
      const rollValue = getRollValueFromResponse(data);
      setPendingRollResult({
        data,
        rollValue,
        mode,
        normalizedManualValue,
        forcePlayerMode: false,
        needsEntrySix: data?.reason === 'NEED_SIX_TO_START',
      });
      return rollValue;
    } catch (rollErr) {
      setRollError(rollErr.message);
      setPendingRollResult(null);
    } finally {
      setRolling(false);
    }

    return null;
  };

  const handleRollAnimationStart = useCallback(
    () => {
      startDiceAnimation(activeActorId);
    },
    [activeActorId, startDiceAnimation],
  );

  const handleRollAnimationEnd = useCallback(() => {
    endDiceAnimation();
  }, [endDiceAnimation]);

  const handleDiceRoll = useCallback(async () => {
    if (turnBusyState) {
      return null;
    }

    if (rollDisabledState || diceRolling) {
      return null;
    }

    setDiceRolling(true);
    const rollValue = await handleRoll();
    if (rollValue === null || rollValue === undefined) {
      setDiceRolling(false);
      return null;
    }

    return rollValue;
  }, [diceRolling, handleRoll, rollDisabledState, turnBusyState]);

  const handleDiceSettled = useCallback(async () => {
    const pendingRoll = pendingRollResult;
    setPendingRollResult(null);

    if (!pendingRoll) {
      setDiceRolling(false);
      return;
    }

    const {
      data,
      rollValue,
      mode,
      normalizedManualValue,
      forcePlayerMode: wasForcePlayerMode,
      needsEntrySix,
    } = pendingRoll;

    if (mode === 'manual' && normalizedManualValue !== null) {
      setManualValue(String(normalizedManualValue));
    }

    const hasOvershootAlert = Boolean(data?.overshootIgnored);

    if (hasOvershootAlert) {
      const overshootDiceValue = Number.isFinite(Number(data?.diceValue))
        ? Number(data.diceValue)
        : rollValue;
      const overshootAlertKey = overshootDiceValue === 6
        ? 'room.alerts.overshootRollAgain'
        : 'room.alerts.overshootWrongThoughtRollAgain';
      showRollToast(t(overshootAlertKey), 'warning');
    } else if (rollValue !== null && rollValue !== undefined && needsEntrySix) {
      const lastDiceRollFromResponse = resolveLastDiceRoll(
        data?.turnState,
        data?.session,
        data,
      );
      const diceLockFromResponse = resolveDiceLock(
        data?.turnState,
        data?.session,
        data,
      );
      applyLastDiceRollUpdateRef(lastDiceRollFromResponse, { preferForDisplay: true });
      setDiceLockState(diceLockFromResponse || null);
      setLastRoll(rollValue);
      setRollError('');
      showRollToast(t('room.roll.errors.needSixToStart'));
      setDiceRolling(false);
      return;
    }

    try {
      if (wasForcePlayerMode) {
        applyMoveResponse(data);
        if (!Array.isArray(data?.moves)) {
          await fetchPlayerState({ skipLoading: true });
        }
      } else {
        applyMoveResponse(data);
      }
    } finally {
      setDiceRolling(false);
    }
  }, [
    applyLastDiceRollUpdateRef,
    applyMoveResponse,
    fetchPlayerState,
    pendingRollResult,
    showRollToast,
    t,
  ]);

  useEffect(() => {
    if (isSeriesReadyToMove && pendingSeriesInfo.series.length) {
      return;
    }
    continueMoveKeyRef.current = '';
    lastAutoContinueKeyRef.current = '';
    autoSeriesFirstStepKeyRef.current = '';
    autoSeriesFirstStepTriggeredRef.current = false;
  }, [isSeriesReadyToMove, pendingSeriesInfo.series.length, turnState?.currentPlayerId]);

  useEffect(() => {
    if (!hasConductorPrivileges || !isSeriesFirstStepPending) {
      return;
    }

    if (!turnState?.currentPlayerId) {
      return;
    }

    if (!token) {
      return;
    }

    if (continuingMove || continueMoveInFlightRef.current) {
      return;
    }

    if (isMoveAnimating || isAnimatingRef.current) {
      return;
    }

    const nextKey = `${id}:${turnState.currentPlayerId}:${seriesSignature}`;
    if (autoSeriesFirstStepKeyRef.current !== nextKey) {
      autoSeriesFirstStepKeyRef.current = nextKey;
      autoSeriesFirstStepTriggeredRef.current = false;
    }

    if (autoSeriesFirstStepTriggeredRef.current) {
      return;
    }

    autoSeriesFirstStepTriggeredRef.current = true;
    const continueMovePlayerId = turnState?.currentPlayerId ?? currentTurnPlayerId ?? null;
if (!continueMovePlayerId) return;

requestContinueMove(continueMovePlayerId, { silent: true });
  }, [
    continuingMove,
    hasConductorPrivileges,
    id,
    isMoveAnimating,
    isSeriesFirstStepPending,
    requestContinueMove,
    seriesSignature,
    token,
    turnState?.currentPlayerId,
  ]);

  useEffect(() => {
    if (!hasConductorPrivileges || !isSeriesReadyToMove) {
      return;
    }

    if (!turnState?.currentPlayerId) {
      return;
    }

    if (!token) {
      return;
    }

    if (pendingSeriesInfo.series.length > 1) {
      return;
    }

    if (continuingMove || continueMoveInFlightRef.current || isMoveAnimating) {
      return;
    }

    const seriesKey = pendingSeriesInfo.series.join('+');
    const nextKey = `${id}:${turnState.currentPlayerId}:${seriesKey}:${currentTurnPendingApplied.length}:${moves.length}`;
    if (lastAutoContinueKeyRef.current === nextKey) {
      return;
    }

    lastAutoContinueKeyRef.current = nextKey;
    const continueMovePlayerId = turnState?.currentPlayerId ?? currentTurnPlayerId ?? null;
if (!continueMovePlayerId) return;

requestContinueMove(continueMovePlayerId, { silent: true });
  }, [
    continuingMove,
    hasConductorPrivileges,
    id,
    isMoveAnimating,
    isSeriesReadyToMove,
    currentTurnPendingApplied.length,
    moves.length,
    pendingSeriesInfo.series,
    requestContinueMove,
    token,
    turnState?.currentPlayerId,
  ]);

  const handleOpenRequestModal = () => {
    if (isConductorView) {
      if (!activeActor) {
        return;
      }
      setRequestDraft(activeActor?.playerRequest || '');
      setRequestError('');
      setRequestModalOpen(true);
      return;
    }
    const draftText = forcePlayerMode ? activeActor?.playerRequest || '' : game?.requestText || '';
    setRequestDraft(draftText);
    setRequestError('');
    setRequestModalOpen(true);
  };

  const handleSaveRequest = async () => {
    if (forcePlayerMode) {
      if (!activeActor || !roomId || !inviteToken) {
        setRequestError(t('errors.requestFailed'));
        return;
      }
      setSavingRequest(true);
      setRequestError('');
      try {
        const resolvedConnectionId = getActiveConnectionId();
        const response = await fetch(apiUrl(`/api/game-sessions/${roomId}/player-request`), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            token: inviteToken,
            participantToken,
            connectionId: resolvedConnectionId || undefined,
            playerId: activeActor.id,
            requestText: requestDraft,
          }),
        });
        if (!response.ok) {
          const errorData = await parseResponseError(response, t('errors.requestFailed'));
          if (CONNECTION_ERROR_CODES.has(errorData.code)) {
            openConnectionIssue('singleConnection');
            return;
          }
          throw new Error(errorData.message);
        }
        const data = await response.json();
        if (data?.player) {
          setGame((prev) => {
            if (!prev) {
              return prev;
            }
            const updatedPlayers = (prev.players || []).map((player) =>
              player.id === data.player.id ? { ...player, ...data.player } : player,
            );
            return { ...prev, players: updatedPlayers };
          });
        }
        setRequestModalOpen(false);
      } catch (requestErr) {
        setRequestError(requestErr.message);
      } finally {
        setSavingRequest(false);
      }
      return;
    }

    if (isConductorView) {
      if (!token || !activeActor) {
        setRequestError(t('errors.requestFailed'));
        return;
      }
      setSavingRequest(true);
      setRequestError('');
      try {
        const response = await fetch(apiUrl(`/api/games/${id}/players/${activeActor.id}`), {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ requestText: requestDraft }),
        });
        if (!response.ok) {
          throw new Error(await parseError(response, t('errors.requestFailed')));
        }
        const data = await response.json();
        if (data?.player) {
          setGame((prev) => {
            if (!prev) {
              return prev;
            }
            return {
              ...prev,
              players: (prev.players || []).map((player) => (player.id === data.player.id ? data.player : player)),
            };
          });
        }
        setRequestModalOpen(false);
      } catch (requestErr) {
        setRequestError(requestErr.message);
      } finally {
        setSavingRequest(false);
      }
      return;
    }

    if (!token) {
      return;
    }
    setSavingRequest(true);
    setRequestError('');
    try {
      const response = await fetch(apiUrl(`/api/games/${id}/request`), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ requestText: requestDraft }),
      });
      if (!response.ok) {
        throw new Error(await parseError(response, t('errors.requestFailed')));
      }
      const data = await response.json();
      setGame((prev) => (prev ? { ...prev, requestText: data.requestText } : prev));
      setRequestModalOpen(false);
    } catch (requestErr) {
      setRequestError(requestErr.message);
    } finally {
      setSavingRequest(false);
    }
  };

  const handleCloseRequestModal = () => {
    setRequestModalOpen(false);
  };

  const fetchLivekitToken = useCallback(async ({
    gameId,
    mode,
    token: authToken,
    playerToken,
    signal,
  }) => {
    const headers = { 'Content-Type': 'application/json' };
    const payload = { gameId };

    if (mode === 'player') {
      payload.playerToken = playerToken;
    } else if (authToken) {
      headers.Authorization = `Bearer ${authToken}`;
    }

    const response = await fetch(apiUrl('/api/livekit/token'), {
      method: 'POST',
      headers,
      body: JSON.stringify(payload),
      signal,
    });

    if (response.status === 401 || response.status === 403) {
      const accessError = new Error('Livekit access denied');
      accessError.code = 'access';
      throw accessError;
    }

    if (!response.ok) {
      throw new Error('Livekit token request failed');
    }

    const data = await response.json();
    return data?.livekit;
  }, []);

  const kickLivekitParticipant = useCallback(async ({ gameId, targetIdentity }) => {
    if (!token) {
      return false;
    }

    const response = await fetch(apiUrl('/api/livekit/kick'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ gameId, targetIdentity }),
    });

    return response.ok;
  }, [token]);

  const handleLivekitFallbackClose = useCallback(() => {
    setLivekitEnabled(false);
    setLivekitStatus('idle');
    setLivekitBoundaryKey((prev) => prev + 1);
  }, []);

  const livekitFallback = useMemo(() => (
    <div className="livekit-status">
      <p className="form-error">Видео временно недоступно</p>
      <div className="modal-actions">
        <button type="button" className="ghost-button" onClick={handleLivekitFallbackClose}>
          {t('room.livekit.close')}
        </button>
      </div>
    </div>
  ), [handleLivekitFallbackClose, t]);

  const handleEnableRecordingAudio = useCallback(() => {
    if (livekitDisabledByCompletedGame) return;
    setLivekitEnabled(true);
    setRecordingAudioRequestId((old) => old + 1);
  }, [livekitDisabledByCompletedGame]);

  const handleLivekitButton = useCallback(() => {
    if (livekitDisabledByCompletedGame) return;
    if (livekitStatus === 'connected') {
      // Reopen an existing minimized LiveKit window without reconnecting
      // or requesting microphone/camera permissions a second time.
      setLivekitOpenRequestId((prev) => prev + 1);
      return;
    }
    if (livekitStatus !== 'idle') return;
    setLivekitEnabled(true);
    setLivekitOpenRequestId((prev) => prev + 1);
  }, [livekitDisabledByCompletedGame, livekitStatus]);

  useEffect(() => {
    if (!livekitDisabledByCompletedGame || !livekitEnabled) {
      return;
    }
    setLivekitEnabled(false);
    setLivekitStatus('idle');
    setLivekitBoundaryKey((prev) => prev + 1);
  }, [livekitDisabledByCompletedGame, livekitEnabled]);

  const handleLivekitStatusChange = useCallback((nextState) => {
    setLivekitStatus(nextState);
  }, []);

  const handleLivekitClose = useCallback(() => {
    setLivekitEnabled(false);
    setLivekitStatus('idle');
    setRecordingAudioRequestId(0);
  }, []);

  const handleLayoutModeChange = useCallback((mode) => {
    if (mode !== 'players-left' && mode !== 'classic') {
      return;
    }
    setLayoutMode(mode);
  }, []);

  const handleStopAlertsToggle = useCallback((value) => {
    setShowStopAlerts(value);
    if (typeof window === 'undefined' || !stopAlertsStorageKey) {
      return;
    }
    localStorage.setItem(stopAlertsStorageKey, value ? '1' : '0');
  }, [stopAlertsStorageKey]);

  const handleChatNotificationSoundToggle = useCallback((value) => {
    setChatNotificationSoundEnabled(value);
    if (typeof window === 'undefined' || !chatNotificationSoundStorageKey) {
      return;
    }
    localStorage.setItem(chatNotificationSoundStorageKey, value ? '1' : '0');
  }, [chatNotificationSoundStorageKey]);

  const handleChatAlertsToggle = useCallback((value) => {
    setShowChatAlerts(value);
    if (!value) {
      setChatToast({ message: '', visible: false, variant: 'neutral' });
    }
    if (typeof window === 'undefined' || !chatAlertsStorageKey) {
      return;
    }
    localStorage.setItem(chatAlertsStorageKey, value ? '1' : '0');
  }, [chatAlertsStorageKey]);

  const isChatPanelVisible = isPlayersLeftLayout || activeRightPanel === 'chat';

  const showChatToast = useCallback((message) => {
    if (!message) {
      return;
    }

    setChatToast({ message, visible: true, variant: 'neutral' });

    if (chatToastTimerRef.current) {
      clearTimeout(chatToastTimerRef.current);
    }
    if (chatToastExitTimerRef.current) {
      clearTimeout(chatToastExitTimerRef.current);
    }

    chatToastTimerRef.current = setTimeout(() => {
      setChatToast((prev) => (prev.message ? { ...prev, visible: false } : prev));
      chatToastExitTimerRef.current = setTimeout(() => {
        setChatToast({ message: '', visible: false, variant: 'neutral' });
      }, 350);
    }, 3000);
  }, []);

  const playIncomingChatSound = useCallback(() => {
    if (!chatNotificationSoundEnabled || typeof Audio === 'undefined') {
      return;
    }

    if (!chatNotificationAudioRef.current) {
      chatNotificationAudioRef.current = new Audio(CHAT_NOTIFICATION_SOUND_URL);
      chatNotificationAudioRef.current.preload = 'auto';
      chatNotificationAudioRef.current.volume = CHAT_NOTIFICATION_BASE_VOLUME;
    }

    if (
      typeof window !== 'undefined'
      && typeof window.AudioContext !== 'undefined'
      && !chatNotificationGainNodeRef.current
    ) {
      const audioContext = new window.AudioContext();
      const source = audioContext.createMediaElementSource(chatNotificationAudioRef.current);
      const gainNode = audioContext.createGain();
      gainNode.gain.value = CHAT_NOTIFICATION_VOLUME_MULTIPLIER;
      source.connect(gainNode);
      gainNode.connect(audioContext.destination);
      chatNotificationAudioContextRef.current = audioContext;
      chatNotificationSourceNodeRef.current = source;
      chatNotificationGainNodeRef.current = gainNode;
    }

    const audio = chatNotificationAudioRef.current;
    chatNotificationAudioContextRef.current?.resume().catch(() => {});
    audio.currentTime = 0;
    audio.play().catch(() => {});
  }, [chatNotificationSoundEnabled]);

  const handleSendChat = async (event) => {
    event.preventDefault();
    const trimmedText = chatInput.trim();
    if (!trimmedText) {
      setChatFormError(t('errors.chatMessageRequired'));
      return;
    }
    if (!isTextWithinLimit(trimmedText, TEXT_LIMITS.chatMessage)) {
      setChatFormError(t('errors.chatMessageTooLong', { count: TEXT_LIMITS.chatMessage }));
      return;
    }
    setChatFormError('');
    setChatSending(true);
    try {
      let response;
      if (mode === 'conductor') {
        if (!token) {
          setChatFormError(t('errors.requestFailed'));
          if (isDev) {
            console.warn('[Chat] send skipped, missing token', { role: 'conductor', gameId: id });
          }
          return;
        }
        const payload = {
          authorType: 'conductor',
          authorName: chatAuthorName,
          text: trimmedText,
        };
        response = await fetchWithIdempotencyRetry(apiUrl(`/api/games/${id}/chat`), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(payload),
        });
      } else {
        if (!roomId || !inviteToken || !resolvedPlayerToken) {
          setChatFormError(t('errors.requestFailed'));
          if (isDev) {
            console.warn('[Chat] send skipped, missing session data', {
              role: 'player',
              playerId,
              sessionId: roomId,
              gameId: id,
            });
          }
          return;
        }
        const resolvedConnectionId = getActiveConnectionId();
        const payload = {
          token: inviteToken,
          participantToken: resolvedPlayerToken,
          connectionId: resolvedConnectionId || undefined,
          authorName: chatAuthorName,
          text: trimmedText,
        };
        response = await fetchWithIdempotencyRetry(apiUrl(`/api/game-sessions/${roomId}/chat`), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
      }
      if (!response.ok) {
        const errorData = await parseResponseError(response, t('errors.requestFailed'));
        if (mode === 'player' && CONNECTION_ERROR_CODES.has(errorData.code)) {
          openConnectionIssue('singleConnection');
          return;
        }
        if (errorData.code === 'FIELD_TOO_LONG' && errorData?.details?.field === 'text') {
          throw new Error(t('errors.chatMessageTooLong', { count: TEXT_LIMITS.chatMessage }));
        }
        throw new Error(errorData.message);
      }
      const data = await response.json();
      if (data?.message) {
        if (data.message.id !== undefined && data.message.id !== null) {
          knownChatMessageIdsRef.current.add(String(data.message.id));
        }
        setChatMessages((prev) => [...prev, data.message]);
      }
      setChatLoaded(true);
      setChatFetchError('');
      setChatInput('');
      if (isDev) {
        console.debug('[Chat] sent', {
          role: mode === 'conductor' ? 'conductor' : 'player',
          playerId,
          sessionId: roomId,
          gameId: id,
        });
      }
    } catch (chatErr) {
      setChatFormError(chatErr.message);
      if (isDev) {
        console.warn('[Chat] send failed', {
          role: mode === 'conductor' ? 'conductor' : 'player',
          playerId,
          sessionId: roomId,
          gameId: id,
          error: chatErr.message,
        });
      }
    } finally {
      setChatSending(false);
    }
  };

  useEffect(() => {
    if (chatPanelVisible && chatUnreadCount !== 0) {
      setChatUnreadCount(0);
    }
  }, [chatPanelVisible, chatUnreadCount]);

  const handleVisualHistoryPlayerToggle = (playerId) => {
    setVisualHistoryPlayers((prev) => {
      const set = new Set(prev);
      if (set.has(playerId)) {
        set.delete(playerId);
      } else {
        set.add(playerId);
      }
      return Array.from(set);
    });
  };

  const handleVisualHistoryToggle = useCallback(
    (value) => {
      setVisualHistoryEnabled(value);
      if (isPlayerVisualHistoryMode && typeof window !== 'undefined' && playerVisualHistoryStorageKey) {
        localStorage.setItem(playerVisualHistoryStorageKey, value ? '1' : '0');
      }
    },
    [isPlayerVisualHistoryMode, playerVisualHistoryStorageKey],
  );

  const handleHighlightVisitedChange = (value) => {
    setSettings((prev) => ({ ...prev, highlightVisitedCells: value }));
  };

  const handleMovementSpeedChange = useCallback(
    async (event) => {
      const nextSpeed = clampSpeedLevel(event.target.value);
      setSettings((prev) => ({ ...prev, movementSpeed: nextSpeed }));
      setSpeedSaveError('');

      if (!token) {
        return;
      }

      setSpeedSaving(true);
      try {
        const response = await fetch(apiUrl('/api/game-settings'), {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ movementSpeed: nextSpeed }),
        });
        if (!response.ok) {
          throw new Error(await parseError(response, t('errors.requestFailed')));
        }
        const data = await response.json();
        setSettings(normalizeSettingsPayload(data));
      } catch (error) {
        setSpeedSaveError(error.message);
      } finally {
        setSpeedSaving(false);
      }
    },
    [t, token],
  );

  const resolvedCardBackgroundUrl = useMemo(
    () => (cardBackgroundUrl ? assetUrl(cardBackgroundUrl) : ''),
    [cardBackgroundUrl],
  );

  const cellTitles = useMemo(() => {
    const titlesMap = {};

    if (Array.isArray(game?.fieldConfig?.cells)) {
      game.fieldConfig.cells.forEach((cell) => {
        if (Number.isInteger(cell?.number)) {
          titlesMap[cell.number] = cell.title || '';
        }
      });
      return titlesMap;
    }

    const fallbackTitles =
      game?.fieldConfig?.boardConfig?.cellTitles
      || board?.configJson?.cellTitles
      || (Array.isArray(board?.cells)
        ? board.cells.reduce((acc, cell) => {
          if (Number.isInteger(cell?.number) && cell?.title) {
            acc[cell.number] = cell.title;
          }
          return acc;
        }, {})
        : {});

    Object.entries(fallbackTitles).forEach(([key, value]) => {
      const numericKey = Number(key);
      if (Number.isInteger(numericKey)) {
        titlesMap[numericKey] = value;
      }
    });

    return titlesMap;
  }, [
    board?.cells,
    board?.configJson?.cellTitles,
    game?.fieldConfig?.boardConfig?.cellTitles,
    game?.fieldConfig?.cells,
  ]);

  useLayoutEffect(() => {
    if (forcePlayerMode) {
      setRequestTextOverflowing(false);
      return undefined;
    }
    const element = requestTextRef.current;
    if (!element) {
      setRequestTextOverflowing(false);
      return undefined;
    }
    const updateOverflow = () => {
      const isOverflowing = element.scrollHeight > element.clientHeight + 1;
      setRequestTextOverflowing(isOverflowing);
    };
    updateOverflow();
    if (typeof window !== 'undefined') {
      window.addEventListener('resize', updateOverflow);
    }
    return () => {
      if (typeof window !== 'undefined') {
        window.removeEventListener('resize', updateOverflow);
      }
    };
  }, [forcePlayerMode, isPlayersLeftLayout, viewedRequestText]);

  const getMoveLogSegments = useCallback(
    (move) => {
      const { pinnedFromCell } = ensurePinnedFromCellForMove(
        move,
        displayedPositions,
        pinnedFromCellByMoveKeyRef,
      );
      return buildFullLogRouteSegments({
        move,
        pinnedFromCell,
        transitionMap,
        cellTitles,
      });
    },
    [cellTitles, displayedPositions, transitionMap],
  );

  const renderLogRouteSegments = useCallback((segments) => {
    const routeTokens = [];
    segments.forEach((segment, index) => {
      if (index > 0) {
        routeTokens.push(
          <span key={`route-arrow-${index}`} className="log-route__arrow">
            →
          </span>,
        );
      }

      if (segment.type === 'cell') {
        routeTokens.push(
          <span key={`route-cell-${segment.value}-${index}`} className="log-route__cell">
            {segment.value}
          </span>,
        );
        return;
      }

      const directionClass = segment.direction === 'up' ? 'up' : 'down';
      routeTokens.push(
        <span
          key={`route-jump-${segment.from}-${segment.to}-${index}`}
          className={`log-route__jump log-route__jump--${directionClass}`}
        >
          {segment.from}
          <span className={`log-route__jump-arrow jump-arrow-${directionClass}`}>
            {segment.direction === 'up' ? '↑' : '↓'}
          </span>
          {segment.fromTitle ? (
            <span className="log-route__title">
              {' '}
              ({segment.fromTitle})
            </span>
          ) : null}
          {' '}
          →
          {' '}
          {segment.to}
        </span>,
      );
    });
    return routeTokens;
  }, []);

  const moveLogItems = visibleMovesForLog.map((move) => {
    const playerName = playerDisplayNameLookup.get(move.playerId) || t('log.playerFallback');
    const playerColor = getPlayerColor(move.playerId);
    const playerData = playerLookup.get(move.playerId);
    const playerChipSrc = playerData?.chipKey ? getChipSrc(playerData.chipKey) : null;
    const titlePart = cellTitles[move.toCell] ? ` ${cellTitles[move.toCell]}` : '';
    const diceSeries = Array.isArray(move.diceSeries) && move.diceSeries.length
      ? renderDiceSeries(move.diceSeries, 'move-dice-series')
      : Array.isArray(move.series) && move.series.length
        ? renderDiceSeries(move.series, 'move-dice-series')
        : move.diceValue;
    const moveComment = (move.kind === 'burn_reentry' || move.kind === 'burn_reentry_drop')
      ? null
      : move.comment === 'continueAfterFinish'
        ? t('log.continueAfterFinish', { from: move.fromCell ?? '—', to: move.toCell ?? '—' })
        : move.comment;
    const diceDisplay = moveComment ? null : diceSeries;
    const canRenderUndo = hasConductorPrivileges && lastMoveIdByPlayer.get(move.playerId) === move.id;
    const undoDisabled = !canUndoMoveForCandidate(move) || undoLocked || isGameFinished;
    const logSegments = getMoveLogSegments(move);
    return (
      <li key={`move-${move.id}`} className="move-entry">
        <div className="move-header">
          <div className="move-player">
            {playerChipSrc ? (
              <img className="player-chip player-chip--log" src={playerChipSrc} alt="" aria-hidden="true" />
            ) : (
              <span className="player-chip player-chip--log player-chip--fallback" aria-hidden="true" />
            )}
            <strong className="move-player-name" style={{ color: playerColor }}>
              {playerName}
            </strong>
          </div>
          <div className="move-actions">
            {diceDisplay !== null && diceDisplay !== undefined && (
              <span className="move-dice">
                <span aria-hidden="true" role="img">
                  🎲
                </span>
                {diceDisplay}
              </span>
            )}
            {canRenderUndo && (
              <button
                type="button"
                className="move-undo-button"
                onClick={() => handleUndoLastMove(move)}
                disabled={undoDisabled}
                title={t('log.undo')}
                aria-label={t('log.undo')}
              >
                <svg
                  className="move-undo-icon"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                  focusable="false"
                >
                  <path d="M9 3h6a1 1 0 0 1 1 1v1h4v2h-2v12a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2V7H4V5h4V4a1 1 0 0 1 1-1Zm2 2v1h2V5h-2Zm-3 4v10h8V9H8Zm2 2h2v6h-2v-6Zm4 0h2v6h-2v-6Z" />
                </svg>
              </button>
            )}
          </div>
        </div>
        <div className="move-cell">
          <div className="log-route">
            {renderLogRouteSegments(logSegments)}
          </div>
        </div>
        {moveComment && <p className="move-comment">{moveComment}</p>}
        <div className="move-target-row">
          <p className="move-target">{move.toCell}{titlePart}</p>
          <span className="move-meta move-meta--inline">
            {formatMoveTimestamp(move.createdAt, locale)}
          </span>
        </div>
      </li>
    );
  });

  const boardImageUrl = toAbsoluteImageUrl(board?.background?.imageUrl || board?.backgroundImageUrl);
  const boardBackground = boardImageUrl ? { backgroundImage: `url(${boardImageUrl})` } : {};
  const boardStyle = {
    ...boardBackground,
    '--board-ratio': `${BOARD_COLS}/${BOARD_ROWS}`,
  };
  const isCompactBoardLabels = viewportWidth < 600;
  const uiScale = useMemo(() => {
    if (viewportWidth < 1400) {
      return 1;
    }
    if (viewportWidth < 2000) {
      const normalized = (viewportWidth - 1400) / 600;
      return Number((1 + normalized * 0.2).toFixed(3));
    }
    const normalized = Math.min((viewportWidth - 2000) / 800, 1);
    return Number((1.2 + normalized * 0.1).toFixed(3));
  }, [viewportWidth]);
  const cellLayouts = useMemo(
    () => Array.from({ length: TOTAL_CELLS }, (_, index) => {
      const cellNumber = index + 1;
      const rect = getCellRect(cellNumber);
      return {
        cellNumber,
        style: {
          width: `${rect.width}%`,
          height: `${rect.height}%`,
          left: `${rect.left}%`,
          top: `${rect.top}%`,
        },
      };
    }),
    [],
  );
  const startSessionButtonLabel = startingSession
    ? t('room.session.starting')
    : isWaitingPlayersToStartSession
      ? t('room.session.waitingPlayers')
      : personalGame || allParticipantsConnected
      ? t('room.session.start')
      : t('room.session.startPartial');

  const canConfigureVisualHistory = hasConductorPrivileges || isRestrictedPlayer;
  const showPlayerVisualHistoryOnly = isRestrictedPlayer && !hasConductorPrivileges;
  const activePlayerTabId = activeActorId || currentTurnPlayerId;
  const viewedLatestMove = viewedPlayerUiState && playerCanViewOwnRollState
    ? {
      fromCell: viewedPlayerUiState.fromCell,
      toCell: viewedPlayerUiState.toCell,
    }
    : null;
  const playerTabsBar = !isAutoSelectMode && playersList.length > 0 ? (
    <div className="players-tabs-bar notranslate" translate="no">
      <button type="button" className="players-tabs-bar__scroll-button" onClick={() => playersTabsRef.current?.scrollBy({ left: -220, behavior: 'smooth' })} aria-label={t('room.players.scrollLeft')}>
        <span className="players-tabs-bar__scroll-icon" aria-hidden="true">‹</span>
      </button>
      <div className="players-tabs" ref={playersTabsRef} role="tablist" aria-label={t('room.players.title')}>
        {playersList.map((player) => {
          const isActive = activePlayerTabId === player.id;
          const isCurrentTurn = currentTurnPlayerId === player.id;
          const isConductorInfoViewed = isConductorView && selectedPlayerId === player.id;
          const isConductorTurn = isConductorView && isCurrentTurn;
          const shouldShowActiveFill = isActive && !isConductorView;
          const isPaused = Boolean(player?.isPaused);
          const isFinished = isFinishedStatus(player?.status);
          const isOnline = isPlayerOnline(player.id);
          const playerChipSrc = player?.chipKey ? getChipSrc(player.chipKey) : null;
          const pauseTooltip = isPaused
            ? t('room.players.resume')
            : t('room.players.pause');
          const showPauseControl = hasConductorPrivileges && !isFinished && !personalGame;

          return (
            <div key={`player-tab-${player.id}`} className="player-tab-item">
              <InfoTooltip
                content={t('room.tooltips.playerTab')}
                enabled={hasConductorPrivileges && manualTurn}
                placement="top"
                fallbackPlacements={['bottom']}
                autoFlip
              >
                <button
                  type="button"
                  className={`player-tab${shouldShowActiveFill ? ' active' : ''}${isCurrentTurn ? ' player-tab--turn' : ''}${isConductorInfoViewed ? ' player-tab--conductor-info' : ''}${isConductorTurn ? ' player-tab--conductor-turn' : ''}${isPaused ? ' player-tab--paused' : ''}${showPauseControl ? ' player-tab--with-pause-control' : ''}`}
                  onClick={(event) => handlePlayerCardClick(player, event)}
                  disabled={Boolean(switchingPlayerId) || !hasConductorPrivileges || !isOnline}
                  role="tab"
                  aria-selected={isActive}
                >
                  {playerChipSrc ? (
                    <img
                      className="player-chip player-chip--tab"
                      src={playerChipSrc}
                      alt=""
                      aria-hidden="true"
                    />
                  ) : (
                    <span className="player-chip player-chip--tab player-chip--fallback" />
                  )}
                  <span className="player-tab__name">{getPlayerDisplayName(player)}</span>
                </button>
              </InfoTooltip>
              {showPauseControl && (
                <button
                  type="button"
                  className="player-tab__pause-toggle"
                  data-player-pause-button="true"
                  onPointerDown={handlePauseTogglePointerDown}
                  onClick={(event) => handlePauseToggleClick(event, player)}
                  aria-label={pauseTooltip}
                  title={pauseTooltip}
                  disabled={Boolean(switchingPlayerId)}
                >
                  {isPaused ? (
                    <svg className="player-tab__pause-icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                      <path d="M8 6v12l10-6-10-6Z" fill="currentColor" />
                    </svg>
                  ) : (
                    <svg className="player-tab__pause-icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                      <rect x="6" y="5" width="4" height="14" rx="1" fill="currentColor" />
                      <rect x="14" y="5" width="4" height="14" rx="1" fill="currentColor" />
                    </svg>
                  )}
                </button>
              )}
            </div>
          );
        })}
      </div>
      <button type="button" className="players-tabs-bar__scroll-button" onClick={() => playersTabsRef.current?.scrollBy({ left: 220, behavior: 'smooth' })} aria-label={t('room.players.scrollRight')}>
        <span className="players-tabs-bar__scroll-icon" aria-hidden="true">›</span>
      </button>
      {hasConductorPrivileges && turnChangeError && <p className="form-error">{turnChangeError}</p>}
      {hasConductorPrivileges && playersPanelError && <p className="form-error">{playersPanelError}</p>}
    </div>
  ) : null;

  const moveListItems = useMemo(
    () => visibleMovesForLog.map((move) => {
      const playerName = playerDisplayNameLookup.get(move.playerId) || t('log.playerFallback');
      const playerColor = getPlayerColor(move.playerId);
      const playerData = playerLookup.get(move.playerId);
      const playerChipSrc = playerData?.chipKey ? getChipSrc(playerData.chipKey) : null;
      const titlePart = cellTitles[move.toCell] ? ` ${cellTitles[move.toCell]}` : '';
      const diceSeries = Array.isArray(move.diceSeries) && move.diceSeries.length
        ? renderDiceSeries(move.diceSeries, 'move-dice-series')
        : Array.isArray(move.series) && move.series.length
          ? renderDiceSeries(move.series, 'move-dice-series')
          : move.diceValue;
      const moveComment = (move.kind === 'burn_reentry' || move.kind === 'burn_reentry_drop')
        ? null
        : move.comment === 'continueAfterFinish'
          ? t('log.continueAfterFinish', { from: move.fromCell ?? '—', to: move.toCell ?? '—' })
          : move.comment;
      const diceDisplay = moveComment ? null : diceSeries;
      const logSegments = getMoveLogSegments(move);
      return (
        <li key={`move-${move.id}`} className="move-entry">
          <div className="move-header">
            <div className="move-player">
              {playerChipSrc ? (
                <img className="player-chip player-chip--log" src={playerChipSrc} alt="" aria-hidden="true" />
              ) : (
                <span className="player-chip player-chip--log player-chip--fallback" aria-hidden="true" />
              )}
              <strong className="move-player-name" style={{ color: playerColor }}>
                {playerName}
              </strong>
            </div>
            {diceDisplay !== null && diceDisplay !== undefined && (
              <span className="move-dice">
                <span aria-hidden="true" role="img">
                  🎲
                </span>
                {diceDisplay}
              </span>
            )}
          </div>
          <div className="move-cell">
            <div className="log-route">
              {renderLogRouteSegments(logSegments)}
            </div>
          </div>
          {moveComment && <p className="move-comment">{moveComment}</p>}
          <div className="move-target-row">
            <p className="move-target">{move.toCell}{titlePart}</p>
            <span className="move-meta move-meta--inline">
              {formatMoveTimestamp(move.createdAt, locale)}
            </span>
          </div>
        </li>
      );
    }),
    [
      cellTitles,
      formatMoveTimestamp,
      getMoveLogSegments,
      getPlayerColor,
      locale,
      visibleMovesForLog,
      playerDisplayNameLookup,
      playerLookup,
      renderLogRouteSegments,
      t,
    ],
  );

  const movesTabContent = (
    <div className="moves-tab">
      {movesError && <p className="form-error">{movesError}</p>}
      {undoError && <p className="form-error">{undoError}</p>}
      {!movesLoaded && !movesError && <p className="muted">{t('loading.moves')}</p>}
      {visibleMovesForLog.length === 0 && movesLoaded && !movesError && (
        <p className="muted">{t('log.noMoves')}</p>
      )}
      <ul className="moves-list">
        {moveLogItems}
      </ul>
    </div>
  );

  const chatTabContent = (
    <div className="chat-tab">
      {isPlayersLeftLayout && <h2>{t('chat.title')}</h2>}
      {chatFetchError && <p className="form-error">{chatFetchError}</p>}
      {!chatLoaded && !chatFetchError && <p className="muted">{t('loading.chat')}</p>}
      {chatMessages.length === 0 && chatLoaded && !chatFetchError && (
        <p className="muted">{t('chat.noMessages')}</p>
      )}
      <div
        className="chat-messages"
        ref={chatMessagesContainerRef}
        onScroll={(event) => {
          const node = event.currentTarget;
          if (node.scrollTop <= 24) {
            loadOlderChat();
          }
        }}
      >
        {chatLoadingOlder && <p className="muted">Загрузка истории…</p>}
        {chatMessages.map((message) => (
          <div
            key={`chat-${message.id}`}
            className="chat-message"
            lang={message.language === 'ua' ? 'uk' : message.language === 'ru' || message.language === 'en' ? message.language : undefined}
          >
            <div className="chat-message-header">
              <strong>{message.authorName}</strong>
              <span>{formatMessageTime(message.createdAt, locale)}</span>
            </div>
            {message.text && <p>{message.text}</p>}
          </div>
        ))}
      </div>
      <form className="chat-form" onSubmit={handleSendChat}>
        <input
          type="text"
          placeholder={t('chat.placeholder')}
          value={chatInput}
          maxLength={TEXT_LIMITS.chatMessage}
          onChange={(event) => setChatInput(event.target.value)}
        />
        {chatFormError && <p className="form-error">{chatFormError}</p>}
        <button type="submit" className="primary-button" disabled={chatSending}>
          {chatSending ? t('buttons.sending') : t('buttons.send')}
        </button>
      </form>
    </div>
  );

  const chatTabLabelClassic = canViewLog ? t('room.tabs.chat') : t('room.tabs.chatOnly');

  const canToggleDiceType = Boolean(
    activeActor
      && canEditDiceType
      && !isRestrictedPlayer
      && !isFinishedStatus(activeActor.status),
  );
  const showDiceTypeToggle = canToggleDiceType && !rollDisabledState;
  const diceTypeLabel = activeActor?.diceType === 'manual' ? t('room.dice.manual') : t('room.dice.auto');
  const diceTypeTitle = `${t('room.players.diceTypeLabel')}: ${diceTypeLabel}`;
  const isDiceTypeUpdating = activeActor && diceTypeUpdatingId === activeActor.id;
  const nextDiceType = activeActor?.diceType === 'manual' ? 'auto' : 'manual';

  const layoutSwitchControl = (
    <div className="setting-row setting-row--stacked">
      <div className="setting-row__text">
        <p className="setting-row__title">{t('room.layout.title')}</p>
      </div>
      <div className="setting-row__control setting-row__control--full">
        <div className="segmented-control">
          <button
            type="button"
            className={isPlayersLeftLayout ? 'segmented-control__button active' : 'segmented-control__button'}
            onClick={() => handleLayoutModeChange('players-left')}
          >
            {t('room.layout.playersLeft')}
          </button>
          <button
            type="button"
            className={!isPlayersLeftLayout ? 'segmented-control__button active' : 'segmented-control__button'}
            onClick={() => handleLayoutModeChange('classic')}
          >
            {t('room.layout.classic')}
          </button>
        </div>
      </div>
    </div>
  );

  const renderPageContent = () => {
    if (forcePlayerMode && connectionIssue) {
      const connectionMessage = connectionIssue === 'singleConnection'
        ? t('room.connection.singleConnectionBody')
        : t('room.connection.loadFailedBody');
      return (
        <div className="modal-backdrop notranslate" translate="no" role="dialog" aria-modal="true">
          <div className="modal">
            <div className="modal-header">
              <h2>{t('room.connection.title')}</h2>
            </div>
            <p className="modal-description">{connectionMessage}</p>
            <div className="modal-actions">
              <button
                className="primary-button"
                type="button"
                onClick={() => handleConnectionRetry(true)}
                disabled={connectionActionLoading}
              >
                {t('room.connection.actions.reconnect')}
              </button>
              <button
                className="secondary-button"
                type="button"
                onClick={() => handleConnectionRetry(false)}
                disabled={connectionActionLoading}
              >
                {t('room.connection.actions.retry')}
              </button>
            </div>
          </div>
        </div>
      );
    }

    if (loading) {
      return (
        <div className="page-loader">
          <div className="spinner" />
          <p>{t('loading.gameData')}</p>
        </div>
      );
    }

    if (error && !forcePlayerMode) {
      const fallbackPath = mode === 'player' ? '/dashboard/create-game' : '/dashboard/games/group';
      const isInvalidInviteError = error === t('room.errors.invalidInvite');
      const handleErrorRedirect = () => {
        if (isInvalidInviteError) {
          window.location.href = 'https://dev.leelaspace.com/';
          return;
        }
        navigate(fallbackPath);
      };
      const errorButtonLabel = isInvalidInviteError ? t('buttons.goToHome') : t('buttons.backToList');
      return (
        <div className="page-loader">
          <p className="form-error">{error}</p>
          <button className="primary-button" type="button" onClick={handleErrorRedirect}>
            {errorButtonLabel}
          </button>
        </div>
      );
    }

    if (!game || !session || !board || !Array.isArray(players)) {
      return (
        <div className="page-loader">
          <div className="spinner" />
          <p>{t('loading.gameData')}</p>
        </div>
      );
    }

    return (
      <>
        <ToastNotification
          message={isGameFinished ? t('game.finishedAlert') : ''}
          visible={isGameFinished}
          variant="warning"
        />
        <ToastNotification
          message={turnToast.message}
          visible={turnToast.visible}
          variant={turnToast.variant}
        />
        <ToastNotification
          message={shouldShowManualTurnHandoverRequired ? t('turnHandoverRequired') : ''}
          visible={shouldShowManualTurnHandoverRequired}
          variant="warning"
        />
        <ToastNotification
          message={shouldShowTurnHandoverPending ? t('turnHandoverPending') : ''}
          visible={shouldShowTurnHandoverPending}
          variant="warning"
        />
        <ToastNotification
          message={shouldShowWaitTurn ? t('wait_your_turn') : ''}
          visible={shouldShowWaitTurn}
          variant="warning"
        />
        <ToastNotification message={rollToast.message} visible={rollToast.visible} variant={rollToast.variant} />
        <ToastNotification message={chatToast.message} visible={chatToast.visible} variant={chatToast.variant} />
        <div
          className={`game-layout${isPlayersLeftLayout ? ' players-left' : ''}`}
          data-layout={isPlayersLeftLayout ? 'chatLeft' : 'classic'}
          style={{ '--ui-scale': uiScale }}
        >
        {isPlayersLeftLayout && (
          <aside className="players-column">
            <div className="log-panel">{chatTabContent}</div>
          </aside>
        )}
        <div className="game-main">
          {playerTabsBar}
          <div className="board-wrapper notranslate" translate="no">
            <div className="board-row">
              <div className="board-scroll">
                <div
                  className={`board-area game-board notranslate${isPlayersLeftLayout ? ' game-board--players-left' : ''}`}
                  ref={boardAreaRef}
                  style={boardStyle}
                >
                  {canShowVisualHistory && (
                    <svg
                      className="visual-history-layer"
                      viewBox={`0 0 ${BOARD_COLS} ${BOARD_ROWS}`}
                      preserveAspectRatio="none"
                      aria-hidden="true"
                    >
                      {visualHistorySegments.map((segment) => {
                        const playerColor = getPlayerColor(segment.playerId);
                        if (segment.type === 'path') {
                          const points = segment.cells
                            .map((cell) => {
                              const coords = getCellCenter(cell, cellCenterByNumber);
                              return `${coords.x},${coords.y}`;
                            })
                            .join(' ');
                          return (
                            <polyline
                              key={`history-line-${segment.playerId}-${segment.index}`}
                              className="visual-history-line"
                              points={points}
                              style={{ stroke: playerColor }}
                              vectorEffect="non-scaling-stroke"
                              fill="none"
                            />
                          );
                        }

                        const start = getCellCenter(segment.fromCell, cellCenterByNumber);
                        const end = getCellCenter(segment.toCell, cellCenterByNumber);
                        return (
                          <line
                            key={`history-line-${segment.playerId}-${segment.index}`}
                            className="visual-history-line"
                            x1={start.x}
                            y1={start.y}
                            x2={end.x}
                            y2={end.y}
                            style={{ stroke: playerColor }}
                            vectorEffect="non-scaling-stroke"
                          />
                        );
                      })}
                      {visualHistoryStops.map((point) => {
                        const coords = getCellCenter(point.cell, cellCenterByNumber);
                        const playerColor = getPlayerColor(point.playerId);
                        return (
                          <circle
                            key={`history-point-${point.playerId}-${point.index}`}
                            className="visual-history-point"
                            cx={coords.x}
                            cy={coords.y}
                            r="0.14"
                            style={{ color: playerColor }}
                            vectorEffect="non-scaling-stroke"
                          />
                        );
                      })}
                    </svg>
                  )}
                  {introDropEntries.length > 0 && (
                    <div className="intro-drop-layer" aria-hidden="true">
                      {introDropEntries.map((entry) => {
                        const left = `${(entry.position.x / BOARD_COLS) * 100}%`;
                        const top = `${(entry.position.y / BOARD_ROWS) * 100}%`;
                        return (
                          <span
                            key={`intro-drop-${entry.playerId}`}
                            className={`intro-drop-token${entry.isCurrentPlayer ? ' current-player' : ''}`}
                            style={{ left, top }}
                            ref={(node) => {
                              if (node) {
                                introDropTokenRefByPlayerId.current.set(entry.playerId, node);
                              } else {
                                introDropTokenRefByPlayerId.current.delete(entry.playerId);
                              }
                            }}
                          >
                            <img className="intro-drop-token__image" src={entry.chipSrc} alt="" />
                          </span>
                        );
                      })}
                    </div>
                  )}
                  <BoardGrid
                    cellLayouts={cellLayouts}
                    cellTitles={cellTitles}
                    playersByCell={playersByCell}
                    onCellClick={handleCellClick}
                    highlightPlayerId={highlightPlayerId}
                    canShowVisitedCells={canShowVisitedCells}
                    stopCellsByCell={stopCellsByCell}
                    isCompactBoardLabels={isCompactBoardLabels}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
        <aside className="sidebar">
          <div className="sidebar-request">
            <div className="sidebar-request__header">
              <h3 className="sidebar-request__title">{t('room.request.title')}</h3>
              {isConductorView && (
                <button
                  type="button"
                  className="ghost-button sidebar-request__edit"
                  onClick={handleOpenRequestModal}
                  disabled={!activeActor}
                >
                  {t('room.request.edit')}
                </button>
              )}
            </div>
            {canEditRequest ? (
              <button type="button" className="sidebar-request__button" onClick={handleOpenRequestModal}>
                <span className="sidebar-request__text">{requestDisplayText}</span>
              </button>
            ) : (
              <>
                <p ref={requestTextRef} className="sidebar-request__text">
                  {requestDisplayText}
                </p>
                {showRequestMore && (
                  <button
                    type="button"
                    className="sidebar-request__more"
                    onClick={() => setRequestDetailsOpen(true)}
                  >
                    {t('room.request.more')}
                  </button>
                )}
              </>
            )}
          </div>
          <div className="control-tab">
            <div className="current-roll-card">
              <div className="current-roll-header">
                <div>
                  <div
                    className={`current-roll-value${showDiceTypeToggle ? ' current-roll-value--with-toggle' : ''}`}
                    style={{ borderColor: currentRollBorderColor }}
                  >
                    <div className="current-roll-value-body">
                      <p className="current-roll-label">{t('room.roll.title')}</p>
                      <div className="current-roll-number-row">
                        {showManualInput ? (
                          <div className="current-roll-input-wrapper">
                            <input
                              type="number"
                              min="1"
                              max="6"
                              step="1"
                              inputMode="numeric"
                              className="current-roll-input"
                              value={manualValue}
                              onChange={(event) => {
                                const { value } = event.target;
                                if (value === '') {
                                  setManualValue('');
                                  setRollError('');
                                  return;
                                }
                                const parsed = Number(value);
                                if (Number.isNaN(parsed)) {
                                  setManualValue('');
                                  return;
                                }
                                const clamped = Math.max(1, Math.min(6, Math.floor(parsed)));
                                setManualValue(String(clamped));
                                setRollError('');
                              }}
                              disabled={rollDisabledState}
                            />
                          </div>
                        ) : (
                          <span className="current-roll-number">{hasViewedRoll ? viewedRoll : '—'}</span>
                        )}
                        {showDiceTypeToggle && (
                          <button
                            type="button"
                            className="dice-type-toggle"
                            onClick={() => handleDiceTypeChange(activeActor, nextDiceType)}
                            disabled={isDiceTypeUpdating}
                            title={diceTypeTitle}
                            aria-label={diceTypeTitle}
                          >
                            {activeActor?.diceType === 'manual' ? (
                              <svg viewBox="0 0 24 24" aria-hidden="true">
                                <rect x="4" y="4" width="16" height="16" rx="3" />
                                <circle cx="8" cy="8" r="1.6" />
                                <circle cx="16" cy="16" r="1.6" />
                                <circle cx="8" cy="16" r="1.6" />
                                <circle cx="16" cy="8" r="1.6" />
                              </svg>
                            ) : (
                              <svg viewBox="0 0 24 24" aria-hidden="true">
                                <path d="M7 3 3 7v10l4 4h10l4-4V7l-4-4H7zm0 2h10l2 2H9L7 5zm-2 4h10v10H7l-2-2V9zm12 10V9l2-2v10l-2 2z" />
                              </svg>
                            )}
                          </button>
                        )}
                      </div>
                      {playerCanViewOwnRollState && (hasSeriesContext ? (
                        <div className="current-roll-series">
                          <div className="current-roll-series-label">{t('room.roll.seriesLabel')}</div>
                          <div className="current-roll-series-body">
                            <div className="current-roll-series-value-text">
                              {seriesText}
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="current-roll-series current-roll-series--placeholder" aria-hidden="true">
                          <div className="current-roll-series-label">{t('room.roll.seriesLabel')}</div>
                          <div className="current-roll-series-body">
                            <div className="current-roll-series-value-text">—</div>
                          </div>
                        </div>
                      ))}
                      {playerCanViewOwnRollState && viewedLatestMove && (
                        <span className="current-roll-path">
                          {viewedLatestMove.fromCell ?? '—'} → {viewedLatestMove.toCell ?? '—'}
                        </span>
                      )}

                    </div>
                  </div>
                </div>
                <div className="current-roll-actions">
                  <RollingDiceWidget
                    className="dice-widget-slot"
                    size={128}
                    disabled={rollDisabledState}
                    initialValue={hasViewedRoll ? viewedRoll : 1}
                    onRollRequest={handleDiceRoll}
                    onRollAnimationStart={handleRollAnimationStart}
                    onRollAnimationEnd={handleRollAnimationEnd}
                    onSettled={handleDiceSettled}
                    overlay={
                      hasConductorPrivileges && shouldShowContinue ? (
                        <button
                          type="button"
                          className="primary-button dice-overlay-button"
                          disabled={continueDisabled}
                          onClick={handleContinueMove}
                        >
                          {continuingMove ? t('room.roll.confirming') : t('room.roll.confirmMove')}
                        </button>
                      ) : null
                    }
                  />
                </div>
              </div>
              {activeActor && settings.onSixBehavior === 'storeAndWait' && activeActor.pendingSixSum > 0 && (
                <p className="pending-info">{t('room.roll.pending', { value: activeActor.pendingSixSum })}</p>
              )}
              {rollError && <p className="form-error">{rollError}</p>}
              {shouldShowWaitingState && <p className="muted current-roll-waiting">{t('room.turn.waitingOtherPlayer')}</p>}
              {rollDisabledReason && rollGuardReason !== 'seriesReady' && !isAwaitingPass && !shouldShowWaitingState && (
                <p className="muted">{rollDisabledReason}</p>
              )}
              {hasConductorPrivileges && shouldShowContinue && continueDisabledReason && !continueError && (
                <p className="muted">{continueDisabledReason}</p>
              )}
              {hasConductorPrivileges && shouldShowContinue && continueError && <p className="form-error">{continueError}</p>}
            </div>

            {canStartSession && (
              <div className="info-actions">
                <button
                  className={`primary-button${isWaitingPlayersToStartSession ? ' primary-button--waiting' : ''}`}
                  type="button"
                  onClick={handleStartSession}
                  disabled={startingSession || isWaitingPlayersToStartSession}
                >
                  {startSessionButtonLabel}
                </button>
                {startError && <p className="form-error">{startError}</p>}
              </div>
            )}

          </div>
          <div className="sidebar-actions notranslate" translate="no">
            {!personalGame && (
              <GameRecordingControls
                controller={recordingController}
                audioDiagnostic={recordingAudioDiagnostic}
                audioConnected={livekitStatus === 'connected'}
                onEnableAudio={handleEnableRecordingAudio}
                speakingIdentities={recordingActiveSpeakers}
                canControl={hasConductorPrivileges && !forcePlayerMode}
                gameType={game?.type}
                sessionStatus={sessionStatus}
                t={t}
              />
            )}
            {!personalGame && (
              <InfoTooltip content={livekitTooltipText}>
                <button
                  type="button"
                  className={`sidebar-action${livekitEnabled ? ' active' : ''}`}
                  onClick={handleLivekitButton}
                  disabled={livekitButtonDisabled}
                  aria-label={t('room.livekit.title')}
                >
                  <img src={videocallsIcon} alt="" aria-hidden="true" />
                </button>
              </InfoTooltip>
            )}
            {!isPlayersLeftLayout && (
              <InfoTooltip content={t('room.tooltips.chat')}>
                <button
                  type="button"
                  className={`sidebar-action${activeRightPanel === 'chat' ? ' active' : ''}`}
                  onClick={() => setActiveRightPanel('chat')}
                  aria-label={chatTabLabelClassic}
                >
                  <img src={chatIcon} alt="" aria-hidden="true" />
                  {chatUnreadCount > 0 && <span className="sidebar-action-badge">{chatUnreadCount}</span>}
                </button>
              </InfoTooltip>
            )}
            {canViewLog && (
              <InfoTooltip content={t('room.tooltips.history')}>
                <button
                  type="button"
                  className={`sidebar-action${activeRightPanel === 'log' ? ' active' : ''}`}
                  onClick={() => setActiveRightPanel('log')}
                  aria-label={t('room.tabs.log')}
                >
                  <img src={logIcon} alt="" aria-hidden="true" />
                </button>
              </InfoTooltip>
            )}
            {canShowNotes && (
              <InfoTooltip content={t('room.tooltips.notes')}>
                <button
                  type="button"
                  className={`sidebar-action${activeRightPanel === 'notes' ? ' active' : ''}`}
                  onClick={handleNotesOpen}
                  aria-label={t('room.notes.title')}
                >
                  <img src={notesIcon} alt="" aria-hidden="true" />
                </button>
              </InfoTooltip>
            )}
            {canOpenSettings && (
              <InfoTooltip content={t('room.tooltips.settings')}>
                <button
                  type="button"
                  className={`sidebar-action${settingsModalOpen ? ' active' : ''}`}
                  onClick={() => setSettingsModalOpen(true)}
                  aria-label={t('room.settings.title')}
                >
                  <img src={settingsIcon} alt="" aria-hidden="true" />
                </button>
              </InfoTooltip>
            )}
            {isSuperRole && (
              <InfoTooltip content={t('room.superScenario.popover')}>
                <button
                  type="button"
                  className={`sidebar-action sidebar-action--plus${superScenarioModalOpen ? ' active' : ''}`}
                  onClick={handleOpenSuperScenarioModal}
                  aria-label={t('room.superScenario.title')}
                >
                  <span className="sidebar-action__plus" aria-hidden="true">+</span>
                </button>
              </InfoTooltip>
            )}
          </div>
          <div className="sidebar-panel notranslate" translate="no">
            {activeRightPanel === 'notes' && canShowNotes && (
              <div className="sidebar-notes sidebar-panel__content">
                <span className="sidebar-notes__title">{t('room.notes.title')}</span>
                <textarea
                  className="sidebar-notes__textarea"
                  value={notesText}
                  onChange={handleNotesChange}
                  placeholder={t('room.notes.placeholder')}
                  rows={6}
                  maxLength={NOTES_MAX_LENGTH}
                />
              </div>
            )}
            {!isPlayersLeftLayout && activeRightPanel === 'chat' && (
              <div className="log-panel sidebar-panel__content">
                {chatTabContent}
              </div>
            )}
            {activeRightPanel === 'log' && canViewLog && (
              <div className="log-panel sidebar-panel__content">
                {movesTabContent}
              </div>
            )}
          </div>
        </aside>
      </div>
      {canShowSupportInGame && (
        <div className="floating-actions">
          <div className="floating-actions__buttons">
            <SupportRequestWidget
              token={token}
              fixed={false}
              buttonClassName="support-widget-button--compact support-widget-button--game-action floating-action-button"
            />
          </div>
        </div>
      )}
      {finishSessionConfirmOpen && canFinishSession && (
        <div className="modal-backdrop notranslate" translate="no" onClick={() => setFinishSessionConfirmOpen(false)}>
          <div className="modal finish-session-confirm-modal" onClick={(event) => event.stopPropagation()}>
            <p className="finish-session-confirm-modal__title">{t('room.session.finishConfirmTitle')}</p>
            <p className="finish-session-confirm-modal__description muted">{t('room.session.finishConfirmDescription')}</p>
            <div className="modal-actions">
              <button type="button" className="ghost-button" onClick={() => setFinishSessionConfirmOpen(false)}>
                {t('room.session.cancel')}
              </button>
              <button
                type="button"
                className="primary-button finish-session-confirm-modal__confirm-button"
                onClick={handleFinishSessionConfirm}
                disabled={finishingSession}
              >
                {t('room.session.confirmFinish')}
              </button>
            </div>
          </div>
        </div>
      )}
      {setupModalOpen && (
        <div className="modal-backdrop notranslate" translate="no">
          <div className="modal">
            <h2>{t('room.setup.title')}</h2>
            {game?.type !== 'personal' && (
              <label className="modal-field">
                <span>{t('room.setup.nameLabel')}</span>
                <input
                  value={setupName}
                  onChange={(event) => setSetupName(event.target.value)}
                  placeholder={t('room.setup.namePlaceholder')}
                />
              </label>
            )}
            <div className="token-section">
              <p className="modal-subtitle">{t('room.setup.chooseToken')}</p>
              <div className="chip-grid">
                {chipChoices.map((option) => (
                  <button
                    key={option.chipKey}
                    type="button"
                    className={`chip-option${setupChipKey === option.chipKey ? ' selected' : ''}${option.disabled ? ' disabled' : ''}`}
                    disabled={option.disabled}
                    onClick={(event) => {
                      event.preventDefault();
                      event.stopPropagation();
                      if (!option.disabled) {
                        setSetupChipKey(option.chipKey);
                      }
                    }}
                  >
                    <img className="chip-preview" src={option.src} alt="" />
                    {option.disabled && <span className="chip-hint">{t('room.setup.taken')}</span>}
                  </button>
                ))}
              </div>
              {game?.type === 'personal' && <p className="muted">{t('room.setup.personalHint')}</p>}
            </div>
            {setupError && <p className="form-error">{setupError}</p>}
            <div className="modal-actions">
              <button type="button" className="primary-button" onClick={handleSavePlayerSetup} disabled={setupSaving}>
                {setupSaving ? t('room.setup.saving') : t('room.setup.confirm')}
              </button>
            </div>
          </div>
        </div>
      )}
      <CardPreviewModal
        open={cardModal.open}
        card={cardModal.card}
        onClose={handleCloseCardModal}
        backgroundUrl={resolvedCardBackgroundUrl}
        textStyle={getDeckCardTextStyle(cardTextColor)}
        emptyMessage={cardModal.error}
        closeLabel={t('room.cardModal.close')}
      />

      {superScenarioModalOpen && isSuperRole && (
        <div className="modal-backdrop notranslate" translate="no" onClick={() => setSuperScenarioModalOpen(false)}>
          <div className="modal settings-modal super-scenario-modal" onClick={(event) => event.stopPropagation()}>
            <div className="super-scenario-modal__content">
              <h2 className="super-scenario-modal__title">{superScenarioModalMode === 'futureMoves' ? t('room.superScenario.enterFutureMoves') : superScenarioModalMode === 'targetCell' ? t('room.superScenario.specifyEndPoint') : t('room.superScenario.menuTitle')}</h2>
              {superScenarioModalMode === 'menu' ? (
                <div className="super-scenario-modal__menu">
                  <button type="button" className="secondary-button super-scenario-modal__menu-button" onClick={() => { setSuperScenarioModalMode('futureMoves'); setSuperScenarioError(''); }}>
                    {t('room.superScenario.enterFutureMoves')}
                  </button>
                  <button type="button" className="secondary-button super-scenario-modal__menu-button" onClick={() => { setSuperScenarioModalMode('targetCell'); setSuperScenarioError(''); }}>
                    {t('room.superScenario.specifyEndPoint')}
                  </button>
                  <button type="button" className="ghost-button super-scenario-modal__menu-button" onClick={() => setSuperScenarioModalOpen(false)}>
                    {t('room.superScenario.cancel')}
                  </button>
                </div>
              ) : (
                <>
                  <div className="super-scenario-modal__inputs">
                    {superScenarioModalMode === 'futureMoves' ? (
                      <div className="super-scenario-modal__input-item">
                        <label className="modal-field">
                          <span>{t('room.superScenario.enterFutureMoves')}</span>
                          <input
                            type="text"
                            className="current-roll-input super-scenario-modal__input"
                            value={superScenarioFutureMoves}
                            onChange={(event) => setSuperScenarioFutureMoves(event.target.value)}
                            placeholder={t('room.superScenario.futureMovesPlaceholder')}
                          />
                        </label>
                      </div>
                    ) : (
                      <div className="super-scenario-modal__input-item">
                        <label className="modal-field">
                          <span>{t('room.superScenario.targetCellLabel')}</span>
                          <input
                            type="number"
                            min="1"
                            max="72"
                            step="1"
                            className="current-roll-input super-scenario-modal__input"
                            value={superScenarioTargetCell}
                            onChange={(event) => setSuperScenarioTargetCell(event.target.value)}
                            placeholder={t('room.superScenario.targetCellPlaceholder')}
                          />
                        </label>
                      </div>
                    )}
                  </div>
                  <div className="modal-actions super-scenario-modal__actions">
                    <button type="button" className="primary-button" onClick={handleApplySuperScenario} disabled={superScenarioApplying}>
                      {t('room.superScenario.apply')}
                    </button>
                    <button type="button" className="secondary-button" onClick={() => { setSuperScenarioModalMode('menu'); setSuperScenarioError(''); }}>
                      {t('room.superScenario.back')}
                    </button>
                    <button type="button" className="ghost-button" onClick={() => setSuperScenarioModalOpen(false)}>
                      {t('room.superScenario.cancel')}
                    </button>
                  </div>
                </>
              )}
              {superScenarioError && <p className="form-error">{superScenarioError}</p>}
            </div>
          </div>
        </div>
      )}

      {settingsModalOpen && canOpenSettings && (
        <div className="modal-backdrop notranslate" translate="no" onClick={() => setSettingsModalOpen(false)}>
          <div className="modal settings-modal" onClick={(event) => event.stopPropagation()}>
            <div className="settings-modal__header">
              <h2>{t('room.settings.title')}</h2>
              <button className="ghost-button settings-modal__close" type="button" onClick={() => setSettingsModalOpen(false)}>
                {t('room.settings.close')}
                <span className="settings-modal__close-icon" aria-hidden="true">✕</span>
              </button>
            </div>
            <div className="settings-modal__body">
              {canConfigureVisualHistory && (
                <section className="settings-card">
                  <div className={`settings-card__header${showPlayerVisualHistoryOnly ? ' settings-card__header--with-toggle' : ''}`}>
                    <h3>{t('room.settings.sections.visualHistory')}</h3>
                    {showPlayerVisualHistoryOnly && (
                      <label className="toggle-switch">
                        <input
                          type="checkbox"
                          checked={visualHistoryEnabled}
                          onChange={(event) => handleVisualHistoryToggle(event.target.checked)}
                        />
                        <span className="toggle-slider" />
                      </label>
                    )}
                  </div>
                  {!showPlayerVisualHistoryOnly && (
                    <div className="settings-card__content">
                      <>
                        <div className="setting-row">
                          <div className="setting-row__text">
                            <p className="setting-row__title">{t('room.visualHistory.activityTitle')}</p>
                            <p className="setting-row__description muted">{t('room.visualHistory.activityDescription')}</p>
                          </div>
                          <div className="setting-row__control">
                            <label className="toggle-switch">
                              <input
                                type="checkbox"
                                checked={visualHistoryEnabled}
                                onChange={(event) => handleVisualHistoryToggle(event.target.checked)}
                              />
                              <span className="toggle-slider" />
                            </label>
                          </div>
                        </div>
                        <div className={`settings-card__subsection${visualHistoryEnabled ? '' : ' is-disabled'}`}>
                          <div className="setting-row setting-row--stacked">
                            <div className="setting-row__text">
                              <p className="setting-row__title">{t('room.visualHistory.playersTitle')}</p>
                              <p className="setting-row__description muted">{t('room.visualHistory.playersDescription')}</p>
                            </div>
                          </div>
                          <div className="players-scroll">
                            {playersList.map((player) => (
                              <label key={`visual-player-${player.id}`} className="player-checkbox">
                                <input
                                  type="checkbox"
                                  checked={visualHistoryPlayersSet.has(player.id)}
                                  onChange={() => handleVisualHistoryPlayerToggle(player.id)}
                                  disabled={!visualHistoryEnabled}
                                />
                                <span className="player-checkbox-label">
                                  <span className="player-dot" style={{ backgroundColor: getPlayerColor(player.id) }} />
                                  {getPlayerDisplayName(player)}
                                </span>
                              </label>
                            ))}
                            {playersList.length === 0 && <p className="muted">{t('room.visualHistory.noPlayers')}</p>}
                          </div>
                        </div>
                        <div className={`setting-row${visualHistoryEnabled ? '' : ' is-disabled'}`}>
                          <div className="setting-row__text">
                            <p className="setting-row__title">{t('room.visualHistory.visitedTitle')}</p>
                            <p className="setting-row__description muted">{t('room.visualHistory.visitedDescription')}</p>
                          </div>
                          <div className="setting-row__control">
                            <label className="toggle-switch">
                              <input
                                type="checkbox"
                                checked={settings.highlightVisitedCells}
                                onChange={(event) => handleHighlightVisitedChange(event.target.checked)}
                                disabled={!visualHistoryEnabled}
                              />
                              <span className="toggle-slider" />
                            </label>
                          </div>
                        </div>
                      </>
                    </div>
                  )}
                </section>
              )}
              {hasConductorPrivileges && (
                <section className="settings-card">
                  <div className="settings-card__header">
                    <h3>{t('room.settings.sections.animation')}</h3>
                  </div>
                  <div className="settings-card__content">
                    <div className="setting-row setting-row--stacked">
                      <div className="setting-row__text">
                        <p className="setting-row__title">{t('room.movementSpeed.title')}</p>
                        <p className="setting-row__description muted">{t('room.movementSpeed.description')}</p>
                      </div>
                      <div className="setting-row__control setting-row__control--full">
                        <div className="range-control range-control--compact">
                          <div className="range-control__meta">
                            <span className="range-control__edge">1</span>
                            <span className="range-control__edge range-control__edge--max">5</span>
                            <span className="range-control__badge">{settings.movementSpeed}</span>
                          </div>
                          <input
                            type="range"
                            min="1"
                            max="5"
                            step="1"
                            value={settings.movementSpeed}
                            onChange={handleMovementSpeedChange}
                          />
                        </div>
                      </div>
                    </div>
                    {speedSaving && <p className="muted">{t('room.movementSpeed.saving')}</p>}
                    {speedSaveError && <p className="form-error">{speedSaveError}</p>}
                  </div>
                </section>
              )}
              <section className="settings-card">
                <div className="settings-card__header">
                  <h3>{t('room.settings.sections.interface')}</h3>
                </div>
                <div className="settings-card__content">
                  {layoutSwitchControl}
                  <div className="setting-row">
                    <div className="setting-row__text">
                      <p className="setting-row__title">{t('room.alerts.showChatNotifications')}</p>
                    </div>
                    <div className="setting-row__control">
                      <label className="toggle-switch">
                        <input
                          type="checkbox"
                          checked={showChatAlerts}
                          onChange={(event) => handleChatAlertsToggle(event.target.checked)}
                        />
                        <span className="toggle-slider" />
                      </label>
                    </div>
                  </div>
                  <div className="setting-row">
                    <div className="setting-row__text">
                      <p className="setting-row__title">{t('room.settings.chatNotificationSound')}</p>
                    </div>
                    <div className="setting-row__control">
                      <label className="toggle-switch">
                        <input
                          type="checkbox"
                          checked={chatNotificationSoundEnabled}
                          onChange={(event) => handleChatNotificationSoundToggle(event.target.checked)}
                        />
                        <span className="toggle-slider" />
                      </label>
                    </div>
                  </div>
                  {hasConductorPrivileges && (
                    <div className="setting-row">
                      <div className="setting-row__text">
                        <p className="setting-row__title">{t('room.alerts.showNotifications')}</p>
                      </div>
                      <div className="setting-row__control">
                        <label className="toggle-switch">
                          <input
                            type="checkbox"
                            checked={showStopAlerts}
                            onChange={(event) => handleStopAlertsToggle(event.target.checked)}
                          />
                          <span className="toggle-slider" />
                        </label>
                      </div>
                    </div>
                  )}
                </div>
              </section>
            </div>
          </div>
        </div>
      )}
      {finishChoiceOpen && (
        <div className="modal-backdrop notranslate" translate="no" role="dialog" aria-modal="true">
          <div className="modal finish-choice-modal end-modal end-modal--conductor" lang={i18n.language === 'ua' ? 'uk' : i18n.language}>
            <div className="modal-header end-modal__header end-modal__header--center">
              <h2>{t('room.finish68.title')}</h2>
            </div>
            <div className="end-modal__body">
              <p className="modal-description end-modal__text">{t('room.finishDecision.conductorTextFirst')}</p>
              <p className="modal-description end-modal__text">{t('room.finishDecision.conductorTextSecond')}</p>
            </div>
            {finishChoiceError && <p className="form-error">{finishChoiceError}</p>}
            {hasConductorPrivileges && (
              <div className="modal-actions">
                <button
                  type="button"
                  className="primary-button end-modal__action-button"
                  onClick={() => handleFinishDecision('finish')}
                  disabled={finishChoiceLoading}
                >
                  {finishChoiceLoading ? t('room.finish68.finishing') : t('room.finish68.finishButton')}
                </button>
                <button
                  type="button"
                  className="ghost-button end-modal__action-button"
                  onClick={() => handleFinishDecision('continue')}
                  disabled={finishChoiceLoading}
                >
                  {finishChoiceLoading ? t('room.finish68.continuing') : t('room.finish68.continueButton')}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
      {conductorFinishedModalOpen && finishSecondTimeOpen && hasConductorPrivileges && (
        <div className="modal-backdrop notranslate" translate="no" role="dialog" aria-modal="true">
          <div className="modal finish-choice-modal end-modal end-modal--conductor" lang={i18n.language === 'ua' ? 'uk' : i18n.language}>
            <div className="end-modal__body">
              <p className="modal-description end-modal__text">
                {t('room.finishSecondTime.body', { playerName: finishDecisionPlayerName })}
              </p>
              {finishDecisionPlayerChipSrc && (
                <div style={{ display: 'flex', justifyContent: 'center', marginTop: '12px' }}>
                  <img src={finishDecisionPlayerChipSrc} alt="" style={{ width: '48px', height: '48px' }} />
                </div>
              )}
            </div>
            <div className="modal-actions">
              <button
                type="button"
                className="primary-button"
                onClick={() => {
                  setDismissedFinishSecondModalKey(finishSecondModalKey);
                  setConductorFinishedModalOpen(false);
                }}
              >
                {t('room.finishSecondTime.cta')}
              </button>
            </div>
          </div>
        </div>
      )}
      {tipsOfferOpen && tipPaymentState === 'idle' && (
        <div className="modal-backdrop notranslate" translate="no" role="dialog" aria-modal="true">
          <div className="modal tips-modal end-modal">
            <div className="modal-header end-modal__header end-modal__header--center">
              <h2>{t('room.finishDecision.playerCompletionTitle')}</h2>
            </div>
            <p className="modal-description end-modal__text" style={{ whiteSpace: 'pre-line' }}>{t('room.finishDecision.playerCompletionText')}</p>
            <div className="tips-modal__section end-modal__tips">
              <h3 className="tips-modal__title">{t('room.tips.title')}</h3>
              <p className="modal-description end-modal__text end-modal__text--small">{t('room.tips.description')}</p>
              <div className="tariff-card-list tips-modal__tariffs">
                {tipTariffs.map((tariff, index) => {
                  const tariffKey = tariff?.id ?? `tip-tariff-${tariff?.amountEur || index}`;
                  const displayAmount = Number.isFinite(tariff?.amountEur) ? tariff.amountEur : 0;
                  const isSelected = Number.isFinite(tipsSelectedAmount) && tipsSelectedAmount === displayAmount;
                  return (
                    <button
                      key={tariffKey}
                      type="button"
                      className={`tariff-card${isSelected ? ' tariff-card--active' : ''}`}
                      onClick={() => handleTipClick(displayAmount)}
                      aria-pressed={isSelected}
                    >
                      <div className="tariff-card__price">
                        {formatCurrency.format(displayAmount)}
                      </div>
                    </button>
                  );
                })}
              </div>
              {tipsPaymentError && <p className="form-error">{tipsPaymentError}</p>}
            </div>
            <div className="modal-actions">
              <button
                type="button"
                className="primary-button"
                onClick={handlePayTips}
                disabled={tipsPaymentLoading}
              >
                {tipsPaymentLoading ? t('room.tips.processing') : t('room.tips.payButton')}
              </button>
              <button type="button" className="ghost-button" onClick={handleDismissEndPopup} disabled={tipsPaymentLoading}>
                {t('room.tips.skipButton')}
              </button>
            </div>
          </div>
        </div>
      )}
      {globalErrorModalOpen && (
        <div className="modal-backdrop notranslate" translate="no" role="alertdialog" aria-modal="true">
          <div className="modal finish-choice-modal">
            <div className="modal-header">
              <h2>{t('error.genericTitle', { ns: 'common' })}</h2>
            </div>
            <p className="modal-description end-modal__text">{t('error.genericBody', { ns: 'common' })}</p>
            <p className="modal-description end-modal__text end-modal__text--small">{t('error.supportHint', { ns: 'common' })}</p>
            <div className="modal-actions">
              <button
                type="button"
                className="primary-button"
                onClick={() => setGlobalErrorModalOpen(false)}
              >
                {t('gotIt', { ns: 'common' })}
              </button>
            </div>
          </div>
        </div>
      )}
      {requestModalOpen && (
        <div className="modal-backdrop notranslate" translate="no">
          <div className="modal">
            <h2>{t('room.requestModal.title')}</h2>
            <textarea value={requestDraft} onChange={(event) => setRequestDraft(event.target.value)} rows={8} />
            {requestError && <p className="form-error">{requestError}</p>}
            <div className="modal-actions">
              <button type="button" className="ghost-button" onClick={handleCloseRequestModal}>
                {t('room.requestModal.cancel')}
              </button>
              <button type="button" className="primary-button" onClick={handleSaveRequest} disabled={savingRequest}>
                {savingRequest ? t('room.requestModal.saving') : t('room.requestModal.save')}
              </button>
            </div>
          </div>
        </div>
      )}
      {requestDetailsOpen && !forcePlayerMode && (
        <div className="modal-backdrop notranslate" translate="no" onClick={() => setRequestDetailsOpen(false)}>
          <div className="modal" onClick={(event) => event.stopPropagation()}>
            <h2>{t('room.request.detailsTitle', { name: requestPlayerName })}</h2>
            <p className="request-details-text">{viewedRequestText || t('room.gameLog.conductorFallback')}</p>
            <div className="modal-actions">
              <button type="button" className="primary-button" onClick={() => setRequestDetailsOpen(false)}>
                {t('room.request.close')}
              </button>
            </div>
          </div>
        </div>
      )}
      {!personalGame && livekitEnabled && (
        <LivekitErrorBoundary
          key={`livekit-${livekitBoundaryKey}`}
          fallback={livekitFallback}
          onError={(error) => console.error('[LiveKit] ui error', error)}
        >
          <Suspense fallback={null}>
            <VideoCallModal
              openRequestId={livekitOpenRequestId}
              audioOnlyRequestId={recordingAudioRequestId}
              getToken={fetchLivekitToken}
              kickParticipant={kickLivekitParticipant}
              gameId={game?.id || id}
              mode={mode}
              token={token}
              playerToken={resolvedPlayerToken}
              hasConductorPrivileges={hasConductorPrivileges}
              onStatusChange={handleLivekitStatusChange}
              onClose={handleLivekitClose}
              recordingStatus={recordingController.status}
              audioUpload={recordingAudioUpload}
              onRecordingSpeakers={handleRecordingSpeakers}
              onRecordingAudioState={setRecordingAudioDiagnostic}
            />
          </Suspense>
        </LivekitErrorBoundary>
      )}
      </>
    );
  };

  return (
    <div className={`game-page${isConductorView ? ' game-page--conductor' : ''}`}>
      <GamePageErrorBoundary
        onError={(error) => console.error('[GamePage] ui error', error)}
        onRetry={handleUiRetry}
        fallback={(_error, onRetry) => (
          <div className="page-loader">
            <p className="form-error">{t('errors.requestFailed')}</p>
            <button className="primary-button" type="button" onClick={onRetry}>
              {t('buttons.retry', { defaultValue: 'Повторить' })}
            </button>
          </div>
        )}
      >
        {renderPageContent()}
      </GamePageErrorBoundary>

    </div>
  );
};

export const ConductorGamePage = () => (
  <GamePage mode="conductor" />
);

export const PlayerGamePage = () => (
  <GamePage mode="player" />
);

export default GamePage;
